<template>
  <div class="cardapio-online-admin">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            Cardápio Digital
          </span>
          <span class="text-muted mt-2 font-weight-bold font-size-sm">
            Catálogo, canais, ofertas, entrega e experiência do cliente
          </span>
        </h3>
        <div class="card-toolbar">
          <button
            type="button"
            class="btn btn-light-dark font-weight-bolder font-size-sm mr-2"
            :disabled="!managementLoaded || !config.publicUrl"
            @click="copyPublicUrl"
          >
            Copiar link
          </button>
          <button
            type="button"
            class="btn btn-light-warning font-weight-bolder font-size-sm mr-2"
            :disabled="!managementLoaded || !config.publicUrl"
            @click="openPublicPreview"
          >
            Abrir cardápio
          </button>
        </div>
      </div>

      <div class="card-body pt-0">
        <div
          v-if="managementError"
          class="presentation-alert presentation-alert--danger mb-8"
          role="alert"
        >
          <div class="presentation-alert-icon">
            <i class="flaticon-warning-sign"></i>
          </div>
          <div>
            <strong>Não foi possível carregar o Cardápio Digital.</strong>
            <p>
              {{ managementError }} Nenhuma informação demonstrativa está sendo
              exibida como se fosse configuração real.
            </p>
            <button
              type="button"
              class="btn btn-sm btn-light-danger font-weight-bold"
              @click="loadManagement()"
            >
              Tentar novamente
            </button>
          </div>
        </div>

        <div
          v-else-if="managementLoading && !managementLoaded"
          class="presentation-alert presentation-alert--loading mb-8"
          aria-live="polite"
        >
          <div class="presentation-alert-icon">
            <i class="flaticon2-hourglass-1"></i>
          </div>
          <div>
            <strong>Carregando configurações reais...</strong>
            <p>Consultando canais, catálogo e regras de atendimento.</p>
          </div>
        </div>

        <div
          v-else-if="managementLoaded"
          class="presentation-alert mb-8"
          :class="hubStatus.alertClass"
          aria-live="polite"
        >
          <div class="presentation-alert-icon">
            <i :class="hubStatus.icon"></i>
          </div>
          <div>
            <strong>{{ hubStatus.title }}</strong>
            <p>{{ hubStatus.description }}</p>
          </div>
        </div>

        <div
          v-if="managementLoaded && activeWorkspace === 'overview'"
          class="overview-grid mb-8"
        >
          <div
            v-for="metric in metrics"
            :key="metric.label"
            class="overview-card"
          >
            <span>{{ metric.label }}</span>
            <strong>{{ metric.value }}</strong>
            <small>{{ metric.hint }}</small>
          </div>
        </div>

        <div v-if="managementLoaded" class="admin-layout">
          <aside class="admin-sidebar" aria-label="Áreas do Cardápio Digital">
            <button
              v-for="section in sections"
              :key="section.id"
              type="button"
              class="section-button"
              :class="{
                'section-button-active': activeWorkspace === section.id,
              }"
              :aria-current="activeWorkspace === section.id ? 'page' : null"
              @click="selectWorkspace(section.id)"
            >
              <strong>{{ section.label }}</strong>
              <span>{{ section.description }}</span>
            </button>
          </aside>

          <main class="admin-content">
            <div class="section-header">
              <div>
                <span class="text-muted font-weight-bold font-size-sm">
                  {{ currentSection.kicker }}
                </span>
                <h4>{{ currentSection.label }}</h4>
                <p>{{ currentSection.longDescription }}</p>
              </div>
            </div>

            <nav
              v-if="currentSection.tabs && currentSection.tabs.length"
              class="workspace-tabs"
              :aria-label="`Opções de ${currentSection.label}`"
            >
              <button
                v-for="tab in currentSection.tabs"
                :key="tab.id"
                type="button"
                class="workspace-tab"
                :class="{ 'workspace-tab--active': activeSection === tab.id }"
                :aria-current="activeSection === tab.id ? 'page' : null"
                @click="selectPanel(tab.id)"
              >
                {{ tab.label }}
              </button>
            </nav>

            <section v-if="activeSection === 'overview'" class="content-grid">
              <div class="control-card control-card-wide">
                <div class="brand-preview">
                  <img v-if="logoUrl" :src="logoUrl" alt="Logo" />
                  <span v-else class="brand-preview__fallback">
                    {{ initials(config.name) || "CD" }}
                  </span>
                  <div>
                    <h5>{{ config.name }}</h5>
                    <p>{{ config.description }}</p>
                    <strong>{{ config.publicUrl }}</strong>
                  </div>
                </div>
              </div>

              <div class="control-card control-card--status">
                <div class="control-card-top">
                  <h5>Cardápio Digital</h5>
                  <span
                    class="label label-inline"
                    :class="publicationStatusClass"
                  >
                    {{ publicationStatusLabel }}
                  </span>
                </div>
                <strong
                  :class="
                    channelForm.cardapioDigital ? 'text-success' : 'text-danger'
                  "
                >
                  {{
                    channelForm.cardapioDigital
                      ? "Canal ativo"
                      : "Canal inativo"
                  }}
                </strong>
                <p>{{ config.scheduleLabel }}</p>
                <small>{{ nextChangeLabel }}</small>
                <div class="publishing-actions mt-4">
                  <button
                    v-if="!config.habilitado"
                    type="button"
                    class="btn btn-sm btn-success font-weight-bold"
                    :disabled="isSaving('config') || !canPublishCardapio"
                    :title="publishBlockingIssues.join(' ')"
                    @click="publishCardapio"
                  >
                    {{ isSaving("config") ? "Salvando..." : "Publicar" }}
                  </button>
                  <button
                    v-else
                    type="button"
                    class="btn btn-sm btn-light-danger font-weight-bold"
                    :disabled="isSaving('config')"
                    @click="openCardapioPauseModal"
                  >
                    Pausar pedidos
                  </button>
                </div>
              </div>

              <div class="control-card control-card--status">
                <div class="control-card-top">
                  <h5>Self Pedido</h5>
                  <span
                    class="label label-inline"
                    :class="
                      !channelForm.selfPedido
                        ? 'label-light-dark'
                        : selfPedidoReadiness.pronto
                        ? 'label-light-success'
                        : 'label-light-warning'
                    "
                  >
                    {{
                      !channelForm.selfPedido
                        ? "Inativo"
                        : selfPedidoReadiness.pronto
                        ? "Pronto"
                        : "Requer atenção"
                    }}
                  </span>
                </div>
                <strong
                  :class="
                    channelForm.selfPedido ? 'text-success' : 'text-muted'
                  "
                >
                  {{ channelForm.selfPedido ? "Canal ativo" : "Canal inativo" }}
                </strong>
                <p>Usa o mesmo catálogo e a mesma identidade visual.</p>
                <button
                  type="button"
                  class="btn btn-sm btn-light-primary font-weight-bold mt-3"
                  @click="goToOperation('tables')"
                >
                  Ambientes, mesas e QR
                </button>
              </div>

              <div class="control-card">
                <h5>Pendências e avisos</h5>
                <ul>
                  <li v-for="alert in pendingAlerts" :key="alert">
                    {{ alert }}
                  </li>
                </ul>
              </div>

              <div class="control-card control-card-wide">
                <h5>Operação</h5>
                <p>
                  Configuração e operação têm papéis diferentes. Use estes
                  atalhos para acompanhar pedidos, entregas e mesas.
                </p>
                <div class="operation-shortcuts">
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold"
                    @click="goToOperation('orders')"
                  >
                    Pedidos Digitais
                  </button>
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold"
                    @click="goToOperation('deliveries')"
                  >
                    Entregas no Kanban
                  </button>
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold"
                    @click="goToOperation('tables')"
                  >
                    Ambientes e mesas
                  </button>
                </div>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'channels'"
              class="content-block"
            >
              <div class="channel-grid">
                <article class="channel-card">
                  <div class="channel-card__header">
                    <div>
                      <span>Venda online</span>
                      <h5>Cardápio Digital</h5>
                    </div>
                    <span
                      class="switch switch-outline switch-icon switch-success"
                    >
                      <label>
                        <input
                          v-model="channelForm.cardapioDigital"
                          type="checkbox"
                          aria-label="Ativar Cardápio Digital"
                        />
                        <span></span>
                      </label>
                    </span>
                  </div>
                  <p>
                    Permite ao cliente comprar pelo link público, usando entrega
                    ou retirada e as formas de pagamento configuradas.
                  </p>
                  <ul>
                    <li
                      v-for="item in cardapioReadiness.bloqueios"
                      :key="`cardapio-${item}`"
                    >
                      {{ item }}
                    </li>
                    <li v-if="!cardapioReadiness.bloqueios.length">
                      Requisitos essenciais atendidos.
                    </li>
                  </ul>
                </article>

                <article class="channel-card">
                  <div class="channel-card__header">
                    <div>
                      <span>Pedido na mesa</span>
                      <h5>Self Pedido</h5>
                    </div>
                    <span
                      class="switch switch-outline switch-icon switch-success"
                    >
                      <label>
                        <input
                          v-model="channelForm.selfPedido"
                          type="checkbox"
                          aria-label="Ativar Self Pedido"
                        />
                        <span></span>
                      </label>
                    </span>
                  </div>
                  <p>
                    Usa o catálogo e o tema do Cardápio Digital, mas recebe o
                    pedido pelo QR da mesa e não depende de entrega.
                  </p>
                  <ul>
                    <li
                      v-for="item in selfPedidoReadiness.bloqueios"
                      :key="`self-${item}`"
                    >
                      {{ item }}
                    </li>
                    <li v-if="!selfPedidoReadiness.bloqueios.length">
                      Requisitos essenciais atendidos.
                    </li>
                  </ul>
                </article>
              </div>

              <div class="section-save-bar">
                <span
                  v-if="channelsDirty"
                  class="text-warning font-weight-bold"
                >
                  Alterações não salvas
                </span>
                <span v-else class="text-muted">Configuração atualizada</span>
                <div>
                  <button
                    type="button"
                    class="btn btn-light font-weight-bold mr-2"
                    :disabled="!channelsDirty || isSaving('channels')"
                    @click="resetChannelDraft"
                  >
                    Descartar
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary font-weight-bold"
                    :disabled="!channelsDirty || isSaving('channels')"
                    @click="saveChannels"
                  >
                    {{ isSaving("channels") ? "Salvando..." : "Salvar canais" }}
                  </button>
                </div>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'publishing'"
              class="content-block"
            >
              <div class="publishing-summary">
                <div>
                  <span>Identidade pública</span>
                  <strong>{{ config.name }}</strong>
                  <small>{{ config.publicUrl }}</small>
                </div>
                <div>
                  <span>Horário atual</span>
                  <strong>{{
                    config.scheduleLabel || "Não configurado"
                  }}</strong>
                  <small>{{ config.closedMessage }}</small>
                </div>
                <div>
                  <span>Situação</span>
                  <strong>{{ publicationStatusLabel }}</strong>
                  <small>{{ publicationStatusDescription }}</small>
                </div>
              </div>
              <div class="shared-impact-note mt-5">
                <i class="flaticon2-information"></i>
                <span>
                  A publicação controla nome, endereço público, agenda semanal e
                  a única mensagem usada quando a loja estiver fechada.
                </span>
              </div>
              <div class="section-save-bar">
                <span class="text-muted"
                  >Edite todos estes dados em uma única tela.</span
                >
                <button
                  type="button"
                  class="btn btn-primary font-weight-bold"
                  @click="openPublishingModal"
                >
                  Editar publicação e horários
                </button>
              </div>
              <div class="control-card mt-5">
                <div class="control-card-top">
                  <h5>Checklist de publicação</h5>
                  <span
                    class="label label-inline"
                    :class="publicationStatusClass"
                  >
                    {{ publicationStatusLabel }}
                  </span>
                </div>
                <p>{{ publicationStatusDescription }}</p>
                <ul>
                  <li v-for="check in publicationChecks" :key="check">
                    {{ check }}
                  </li>
                </ul>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'catalog'"
              class="content-block"
            >
              <div class="toolbar-line">
                <div>
                  <h5>Produtos do Cardápio Digital</h5>
                  <p>
                    Publique produtos já cadastrados ou abra o cadastro oficial
                    para criar um item novo com dados fiscais e tributários.
                  </p>
                </div>
                <div class="toolbar-actions compact-toolbar-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-light-dark font-weight-bold"
                    @click="goToNewProduct"
                  >
                    Novo produto no cadastro
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-primary font-weight-bold"
                    @click="openProductPublisher"
                  >
                    Publicar produto existente
                  </button>
                </div>
              </div>

              <div class="shared-impact-note mb-5">
                <i class="flaticon2-information"></i>
                <span>
                  Este catálogo é compartilhado com salão e garçom. Ajustes de
                  preço, categoria ou disponibilidade podem afetar os dois usos.
                </span>
              </div>

              <div class="catalog-summary mb-5">
                <span>
                  <strong>{{ publishedProducts.length }}</strong> publicados
                </span>
                <span>
                  <strong>{{ candidateProducts.length }}</strong> disponíveis
                  para publicar
                </span>
                <span>
                  <strong>{{ unavailableProducts.length }}</strong>
                  indisponíveis
                </span>
              </div>

              <div class="table-responsive">
                <table class="table table-head-custom table-vertical-center">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Categoria</th>
                      <th>Preço no site</th>
                      <th>Preparo</th>
                      <th>Publicação</th>
                      <th>Status</th>
                      <th class="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="product in products"
                      :key="product.id || `source-${product.productId}`"
                    >
                      <td>
                        <div class="product-cell">
                          <img
                            v-if="product.imageUrl"
                            :src="product.imageUrl"
                            :alt="product.name"
                          />
                          <span v-else>{{ product.initials }}</span>
                          <div>
                            <strong>{{ product.name }}</strong>
                            <small>{{ product.description }}</small>
                          </div>
                        </div>
                      </td>
                      <td>{{ categoryName(product.categoryId) }}</td>
                      <td>{{ formatMoney(product.priceCents) }}</td>
                      <td>
                        {{ productPreparationLabel(product) }}
                      </td>
                      <td>
                        <span
                          class="label label-lg label-inline"
                          :class="
                            product.configured
                              ? 'label-light-primary'
                              : 'label-light-dark'
                          "
                        >
                          {{
                            product.published
                              ? "Publicado"
                              : product.configured
                              ? "Rascunho"
                              : "Candidato"
                          }}
                        </span>
                        <span
                          v-if="product.highlighted"
                          class="label label-lg label-inline label-light-warning ml-2"
                        >
                          Destaque
                        </span>
                      </td>
                      <td>
                        <span
                          class="label label-lg label-inline"
                          :class="
                            product.rawAvailable
                              ? 'label-light-success'
                              : 'label-light-danger'
                          "
                        >
                          {{ product.rawAvailable ? "Ativo" : "Pausado" }}
                        </span>
                        <small
                          v-if="
                            product.rawAvailable &&
                            !product.available &&
                            product.unavailabilityReasons.length
                          "
                          class="d-block text-warning mt-1"
                        >
                          {{ product.unavailabilityReasons.join(" ") }}
                        </small>
                      </td>
                      <td class="text-right">
                        <button
                          v-if="product.published"
                          type="button"
                          class="btn btn-sm btn-light font-weight-bold mr-2"
                          :disabled="isSaving('product-status') || !product.id"
                          @click="
                            !product.rawAvailable ||
                            isTemporarilyPaused(product)
                              ? activateProduct(product)
                              : openPauseModal('product', product)
                          "
                        >
                          {{
                            !product.id
                              ? "Publicar primeiro"
                              : isSaving("product-status")
                              ? "Salvando..."
                              : isTemporarilyPaused(product)
                              ? "Retomar agora"
                              : product.rawAvailable
                              ? "Pausar venda"
                              : "Ativar"
                          }}
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-light-primary font-weight-bold"
                          @click="openCatalogProduct(product)"
                        >
                          {{
                            product.published
                              ? "Ajustar no site"
                              : product.configured
                              ? "Revisar e publicar"
                              : "Configurar publicação"
                          }}
                        </button>
                      </td>
                    </tr>
                    <tr v-if="!products.length">
                      <td colspan="7" class="text-center text-muted py-8">
                        Nenhum produto foi encontrado. Use “Publicar produto
                        existente” para buscar no estoque.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'categories'"
              class="content-block"
            >
              <div class="toolbar-line">
                <div>
                  <h5>Categorias compartilhadas</h5>
                  <p>
                    A organização abaixo vem dos grupos do cadastro de produtos.
                  </p>
                </div>
                <button
                  type="button"
                  class="btn btn-primary font-weight-bold"
                  @click="openCategoryModal()"
                >
                  Nova categoria
                </button>
              </div>
              <div class="shared-impact-note mb-5">
                <i class="flaticon-warning-sign"></i>
                <span>
                  Nome e movimentação pertencem ao grupo compartilhado com salão
                  e garçom. Pausar aqui altera somente os canais escolhidos do
                  cardápio.
                </span>
              </div>
              <div v-if="!categories.length" class="loading-box">
                Nenhuma categoria cadastrada.
              </div>
              <div v-else class="category-row">
                <div
                  v-for="category in categories"
                  :key="category.id"
                  class="category-chip"
                  :class="{ 'category-chip--paused': !category.rawAvailable }"
                >
                  <strong>{{ category.name }}</strong>
                  <span>{{ category.count }} itens</span>
                  <small>{{
                    category.rawAvailable ? "Disponível" : "Pausada"
                  }}</small>
                  <div class="catalog-status-actions">
                    <button
                      type="button"
                      class="btn btn-xs btn-light-primary font-weight-bold"
                      @click="openCategoryModal(category)"
                    >
                      Editar
                    </button>
                    <button
                      v-if="category.rawAvailable"
                      type="button"
                      class="btn btn-xs btn-light-danger font-weight-bold"
                      :disabled="isSaving('category-status') || !category.id"
                      @click="confirmCategoryImpact(category, false)"
                    >
                      Pausar venda
                    </button>
                    <button
                      v-else
                      type="button"
                      class="btn btn-xs btn-light-success font-weight-bold"
                      :disabled="isSaving('category-status') || !category.id"
                      @click="confirmCategoryImpact(category, true)"
                    >
                      Ativar
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'options'"
              class="content-block"
            >
              <div class="toolbar-line">
                <div>
                  <h5>Grupos globais</h5>
                  <p>
                    Use quando a mesma escolha deve aparecer em todo o cardápio
                    ou em categorias inteiras. Para borda, adicionais e
                    acompanhamentos de um item específico, abra o produto no
                    catálogo.
                  </p>
                </div>
                <div class="toolbar-actions compact-toolbar-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-primary font-weight-bold"
                    @click="openOptionGroupModal()"
                  >
                    Novo complemento global
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-light-primary font-weight-bold"
                    @click="openOptionProductPicker"
                  >
                    Adicionar a um produto
                  </button>
                </div>
              </div>

              <div class="option-scope-guide">
                <article>
                  <span>Global</span>
                  <strong>Vale para vários produtos</strong>
                  <p>
                    Bom para molhos, talheres, sacola, observações ou escolhas
                    que toda categoria compartilha.
                  </p>
                </article>
                <article>
                  <span>Produto</span>
                  <strong>Fica dentro de um item</strong>
                  <p>
                    Bom para borda de uma pizza, ponto da carne, remoção de
                    ingrediente ou adicional daquele produto.
                  </p>
                </article>
              </div>

              <div v-if="!globalOptionGroups.length" class="loading-box">
                Nenhum grupo de opções cadastrado.
              </div>

              <div v-else class="option-admin-list">
                <article
                  v-for="group in globalOptionGroups"
                  :key="group.id"
                  class="option-admin-card"
                >
                  <div>
                    <strong>{{ group.nome }}</strong>
                    <span>{{ optionGroupScopeLabel(group) }}</span>
                    <small>
                      {{ optionTypeLabel(group.tipo) }} · mínimo
                      {{ group.minimo }} · máximo {{ group.maximo || "livre" }}
                    </small>
                  </div>
                  <div class="option-admin-items">
                    <div
                      v-for="option in group.opcoes"
                      :key="option.id"
                      class="option-admin-item"
                      :class="{
                        'option-admin-item--paused': !option.available,
                      }"
                    >
                      <span>
                        {{ option.nome }}
                        <template v-if="option.preco_adicional_centavos">
                          · {{ formatMoney(option.preco_adicional_centavos) }}
                        </template>
                      </span>
                      <button
                        v-if="option.available"
                        type="button"
                        class="btn btn-xs btn-clean text-danger p-0"
                        :aria-label="`Pausar ${option.nome}`"
                        :disabled="isSaving('option-item-status')"
                        @click="openPauseModal('option', option)"
                      >
                        Pausar
                      </button>
                      <button
                        v-if="!option.available"
                        type="button"
                        class="btn btn-xs btn-clean text-success p-0"
                        :aria-label="`Ativar ${option.nome}`"
                        :disabled="isSaving('option-item-status')"
                        @click="activateOptionItem(option)"
                      >
                        Ativar
                      </button>
                    </div>
                  </div>
                  <div class="option-admin-actions">
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openOptionGroupModal(group)"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-dark font-weight-bold"
                      @click="duplicateOptionGroup(group)"
                    >
                      Duplicar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm font-weight-bold"
                      :class="
                        group.ativo ? 'btn-light-danger' : 'btn-light-success'
                      "
                      @click="toggleOptionGroup(group)"
                    >
                      {{ group.ativo ? "Pausar" : "Ativar" }}
                    </button>
                  </div>
                </article>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'pizzas'"
              class="content-block"
            >
              <div class="toolbar-line">
                <div>
                  <h5>Variantes de venda</h5>
                  <p>
                    Configure pizzas ou escolhas reutilizáveis, como tamanho e
                    ponto. Variantes comerciais não criam combinações de
                    estoque.
                  </p>
                </div>
                <button
                  type="button"
                  class="btn btn-primary font-weight-bold"
                  @click="openVariantCreator"
                >
                  Nova variante
                </button>
              </div>

              <div
                v-if="!pizzas.length && !globalVariantGroups.length"
                class="loading-box"
              >
                Nenhuma variante cadastrada.
              </div>

              <div v-else class="option-admin-list">
                <article
                  v-for="group in globalVariantGroups"
                  :key="`variante-global-${group.id}`"
                  class="option-admin-card"
                >
                  <div>
                    <strong>{{ group.nome }}</strong>
                    <span
                      >Variação comercial ·
                      {{ optionGroupScopeLabel(group) }}</span
                    >
                    <small
                      >{{ optionTypeLabel(group.tipo) }} ·
                      {{ group.opcoes.length }} escolhas</small
                    >
                  </div>
                  <div class="option-admin-items">
                    <span
                      v-for="option in group.opcoes"
                      :key="`variante-opcao-${option.id}`"
                      class="label label-inline label-light-primary"
                    >
                      {{ option.nome }}
                    </span>
                  </div>
                  <div class="option-admin-actions">
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openOptionGroupModal(group)"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm font-weight-bold"
                      :class="
                        group.ativo ? 'btn-light-danger' : 'btn-light-success'
                      "
                      @click="toggleOptionGroup(group)"
                    >
                      {{ group.ativo ? "Pausar" : "Ativar" }}
                    </button>
                  </div>
                </article>
                <article
                  v-for="pizza in pizzas"
                  :key="pizza.id"
                  class="option-admin-card"
                >
                  <div>
                    <strong>{{ pizza.nome_template }}</strong>
                    <span>{{ productName(pizza.cardapio_produto_id) }}</span>
                    <small>
                      {{ pizza.tamanhos.length }} tamanhos ·
                      {{ pizza.sabores.length }} sabores · adicional por sabor
                    </small>
                  </div>
                  <div class="option-admin-items">
                    <span
                      v-for="size in pizza.tamanhos"
                      :key="size.id || size.localKey"
                      class="label label-inline label-light-primary"
                    >
                      {{ size.nome }} · até {{ size.max_sabores }} sabores
                    </span>
                  </div>
                  <div class="option-admin-actions">
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openPizzaModal(pizza)"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-dark font-weight-bold"
                      @click="duplicatePizza(pizza)"
                    >
                      Duplicar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm font-weight-bold"
                      :class="
                        pizza.ativo ? 'btn-light-danger' : 'btn-light-success'
                      "
                      @click="togglePizza(pizza)"
                    >
                      {{ pizza.ativo ? "Pausar" : "Ativar" }}
                    </button>
                  </div>
                </article>
              </div>
            </section>

            <section
              v-else-if="
                ['promotions', 'coupons', 'combos'].includes(activeSection)
              "
              class="content-block commercial-section"
            >
              <div class="toolbar-line">
                <div>
                  <h5>{{ currentOffer.title }}</h5>
                  <p>{{ currentOffer.description }}</p>
                </div>
                <button
                  type="button"
                  class="btn btn-primary font-weight-bold"
                  @click="openCurrentOfferModal"
                >
                  {{ currentOffer.actionLabel }}
                </button>
              </div>

              <div class="commercial-list-grid">
                <div
                  v-if="activeSection === 'promotions'"
                  class="commercial-list commercial-list-wide"
                >
                  <h5>Campanhas</h5>
                  <div v-if="!promocoes.length" class="loading-box">
                    Nenhuma promoção cadastrada.
                  </div>
                  <article
                    v-for="promotion in promocoes"
                    :key="promotion.id"
                    class="commercial-row"
                    :class="{
                      'commercial-row-with-image':
                        promotion.imagem_url || promotion.regras?.imagem_url,
                    }"
                  >
                    <img
                      v-if="
                        promotion.imagem_url || promotion.regras?.imagem_url
                      "
                      class="commercial-row-image"
                      :src="promotion.imagem_url || promotion.regras.imagem_url"
                      alt=""
                    />
                    <div>
                      <strong>{{ promotion.nome }}</strong>
                      <small>
                        {{ promotionTargetLabel(promotion) }} ·
                        {{ promotionPeriodLabel(promotion) }}
                      </small>
                    </div>
                    <span
                      class="label label-inline"
                      :class="
                        promotion.ativo
                          ? 'label-light-success'
                          : 'label-light-danger'
                      "
                    >
                      {{ promotion.ativo ? "Ativa" : "Pausada" }}
                    </span>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openPromotionModal(promotion)"
                    >
                      Editar
                    </button>
                  </article>
                </div>

                <div
                  v-if="activeSection === 'coupons'"
                  class="commercial-list commercial-list-wide"
                >
                  <h5>Cupons</h5>
                  <div v-if="!cupons.length" class="loading-box">
                    Nenhum cupom cadastrado.
                  </div>
                  <article
                    v-for="coupon in cupons"
                    :key="coupon.id"
                    class="commercial-row"
                  >
                    <div>
                      <strong>{{ coupon.codigo }}</strong>
                      <small>
                        {{ coupon.nome || "Cupom de desconto" }} · mínimo
                        {{ formatMoney(coupon.pedido_minimo_centavos) }}
                      </small>
                    </div>
                    <span
                      class="label label-inline"
                      :class="
                        coupon.ativo
                          ? 'label-light-success'
                          : 'label-light-danger'
                      "
                    >
                      {{ coupon.ativo ? "Ativo" : "Pausado" }}
                    </span>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openCouponModal(coupon)"
                    >
                      Editar
                    </button>
                  </article>
                </div>

                <div
                  v-if="activeSection === 'combos'"
                  class="commercial-list commercial-list-wide"
                >
                  <h5>Combos</h5>
                  <div v-if="!combos.length" class="loading-box">
                    Nenhum combo cadastrado.
                  </div>
                  <article
                    v-for="combo in combos"
                    :key="combo.id"
                    class="commercial-row"
                  >
                    <div>
                      <strong>{{ combo.nome }}</strong>
                      <small>
                        {{ productName(combo.cardapio_produto_id) }} ·
                        {{ formatMoney(combo.preco_combo_centavos) }}
                      </small>
                    </div>
                    <span
                      class="label label-inline"
                      :class="
                        combo.ativo
                          ? 'label-light-success'
                          : 'label-light-danger'
                      "
                    >
                      {{ combo.ativo ? "Ativo" : "Pausado" }}
                    </span>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openComboModal(combo)"
                    >
                      Editar
                    </button>
                  </article>
                </div>
              </div>
            </section>

            <section
              v-else-if="
                ['delivery', 'pickup', 'checkout'].includes(activeSection)
              "
              class="content-block"
            >
              <div class="service-summary-card">
                <div>
                  <span>{{ currentServiceSummary.eyebrow }}</span>
                  <h5>{{ currentServiceSummary.title }}</h5>
                  <p>{{ currentServiceSummary.description }}</p>
                </div>
                <span
                  class="label label-lg label-inline"
                  :class="
                    currentServiceSummary.enabled
                      ? 'label-light-success'
                      : 'label-light-danger'
                  "
                >
                  {{ currentServiceSummary.enabled ? "Ativo" : "Inativo" }}
                </span>
              </div>
              <div class="service-detail-grid">
                <div
                  v-for="detail in currentServiceSummary.details"
                  :key="detail.label"
                >
                  <span>{{ detail.label }}</span>
                  <strong>{{ detail.value }}</strong>
                </div>
              </div>
              <div class="section-save-bar">
                <span class="text-muted">
                  {{ currentServiceSummary.help }}
                </span>
                <button
                  type="button"
                  class="btn btn-primary font-weight-bold"
                  @click="openServiceEditor(activeSection)"
                >
                  {{ currentServiceSummary.actionLabel }}
                </button>
              </div>

              <div
                v-if="activeSection === 'delivery'"
                class="delivery-team-card"
              >
                <div class="delivery-team-card__header">
                  <div>
                    <span class="delivery-team-card__eyebrow"
                      >Equipe de entrega</span
                    >
                    <h5>Entregadores</h5>
                    <p>
                      Primeiro habilite a função no cadastro do usuário. Depois,
                      vincule-o aqui para receber atribuições de entrega. Pausar
                      remove o entregador das novas atribuições sem apagar o
                      histórico.
                    </p>
                  </div>
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold"
                    :disabled="couriersLoading"
                    @click="openCourierModal"
                  >
                    Definir entregador
                  </button>
                </div>

                <div
                  v-if="couriersLoading"
                  class="delivery-team-card__empty"
                  aria-live="polite"
                >
                  Carregando equipe de entrega...
                </div>
                <div
                  v-else-if="!deliveryCouriers.length"
                  class="delivery-team-card__empty"
                >
                  Nenhum entregador foi vinculado. No cadastro do usuário,
                  marque “Pode atuar como entregador” e volte aqui para
                  ativá-lo.
                </div>
                <div v-else class="delivery-team-list">
                  <div
                    v-for="courier in deliveryCouriers"
                    :key="courier.id"
                    class="delivery-team-row"
                  >
                    <div>
                      <strong>{{ courierDisplayName(courier) }}</strong>
                      <small>{{ courierSecondaryLabel(courier) }}</small>
                    </div>
                    <div class="delivery-team-row__actions">
                      <span
                        class="label label-inline"
                        :class="
                          courier.ativo
                            ? 'label-light-success'
                            : 'label-light-danger'
                        "
                      >
                        {{ courier.ativo ? "Ativo" : "Pausado" }}
                      </span>
                      <button
                        type="button"
                        class="btn btn-sm font-weight-bold"
                        :class="
                          courier.ativo
                            ? 'btn-light-danger'
                            : 'btn-light-success'
                        "
                        :disabled="isSaving(`courier-${courier.id}`)"
                        @click="toggleCourier(courier)"
                      >
                        {{ courier.ativo ? "Pausar" : "Ativar" }}
                      </button>
                    </div>
                  </div>
                </div>

                <small class="delivery-team-card__permission-note">
                  Para operar a própria entrega, o usuário também precisa da
                  permissão de entrega do sistema.
                </small>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'visual'"
              class="content-block"
            >
              <div class="appearance-summary">
                <div class="appearance-summary__brand">
                  <img v-if="logoUrl" :src="logoUrl" alt="" />
                  <span v-else>{{ initials(config.name) || "CD" }}</span>
                  <div>
                    <strong>{{ config.name }}</strong>
                    <small>{{ selectedThemeLabel }}</small>
                  </div>
                </div>
                <div class="appearance-summary__colors">
                  <span :style="{ background: visualForm.primaryColor }"></span>
                  <span :style="{ background: visualForm.accentColor }"></span>
                  <span
                    :style="{ background: visualForm.backgroundColor }"
                  ></span>
                </div>
              </div>
              <div class="service-detail-grid mt-5">
                <div>
                  <span>Cards</span>
                  <strong>{{
                    visualForm.cardStyle === "flat" ? "Limpos" : "Suaves"
                  }}</strong>
                </div>
                <div>
                  <span>Botões</span>
                  <strong>{{
                    visualForm.buttonStyle === "pill"
                      ? "Cápsula"
                      : "Arredondados"
                  }}</strong>
                </div>
                <div>
                  <span>Mensagens próprias</span>
                  <strong>Vazio, erro e acompanhamento</strong>
                </div>
              </div>
              <div class="section-save-bar">
                <span class="text-muted">
                  A mensagem de loja fechada fica em Publicação; a de checkout,
                  em Pagamentos. Assim cada texto tem uma única fonte.
                </span>
                <button
                  type="button"
                  class="btn btn-primary font-weight-bold"
                  @click="openVisualModal"
                >
                  Editar aparência e mensagens
                </button>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>

    <b-modal
      id="modalCardapioPublishing"
      title="Publicação do cardápio"
      size="xl"
      hide-footer
      @hidden="cancelPublishing(false)"
    >
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Nome exibido no site</label>
          <input
            v-model="publishingForm.name"
            class="form-control"
            maxlength="120"
          />
          <small>Nome que aparece no topo do cardápio.</small>
          <small v-if="fieldError('nome_publico')" class="text-danger">
            {{ fieldError("nome_publico") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Slug do link</label>
          <input
            v-model="publishingForm.slug"
            class="form-control"
            :class="{
              'is-valid': slugAvailability.status === 'available',
              'is-invalid': slugAvailability.status === 'unavailable',
            }"
            maxlength="80"
            autocomplete="off"
            aria-describedby="publishing-slug-help publishing-slug-status"
          />
          <small id="publishing-slug-help">Ex.: restaurante-demo.</small>
          <small
            id="publishing-slug-status"
            class="slug-availability"
            :class="`slug-availability--${slugAvailability.status}`"
            aria-live="polite"
          >
            {{ slugAvailabilityMessage }}
          </small>
          <small v-if="publishingPublicUrlPreview" class="slug-public-url">
            {{ publishingPublicUrlPreview }}
          </small>
          <small v-if="fieldError('slug')" class="text-danger">
            {{ fieldError("slug") }}
          </small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Descrição curta</label>
          <textarea
            v-model="publishingForm.description"
            class="form-control"
            rows="3"
            maxlength="255"
          ></textarea>
          <small>Texto curto para orientar o cliente.</small>
          <small v-if="fieldError('descricao')" class="text-danger">
            {{ fieldError("descricao") }}
          </small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Mensagem de loja fechada</label>
          <textarea
            v-model="publishingForm.closedMessage"
            class="form-control"
            rows="2"
            maxlength="255"
          ></textarea>
          <small
            >Esta é a única mensagem usada pelo site quando a loja estiver
            fechada.</small
          >
          <small v-if="fieldError('mensagem_fechado')" class="text-danger">
            {{ fieldError("mensagem_fechado") }}
          </small>
        </div>
      </div>
      <div class="weekly-schedule mt-6">
        <div class="weekly-schedule__head">
          <div>
            <h5>Agenda semanal</h5>
            <p>
              Adicione mais de um período no mesmo dia. Quando o fim for menor
              ou igual ao início, o horário termina no dia seguinte.
            </p>
            <p>
              Publicado mantém o cardápio visível; novos pedidos são liberados
              somente dentro destes horários.
            </p>
          </div>
          <small>Máximo de 6 períodos por dia</small>
        </div>
        <div class="weekly-schedule__presets" aria-label="Atalhos de horário">
          <div>
            <strong>Repetir o primeiro horário ativo em:</strong>
            <small>Use 24 horas quando não quiser fechamento por agenda.</small>
          </div>
          <div>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold"
              @click="applySchedulePreset('all')"
            >
              Todos os dias
            </button>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold"
              @click="applySchedulePreset('weekdays')"
            >
              Seg a sex
            </button>
            <button
              type="button"
              class="btn btn-sm btn-light-success font-weight-bold"
              @click="applySchedulePreset('always')"
            >
              24 horas
            </button>
          </div>
        </div>
        <div class="weekly-schedule__summary" aria-live="polite">
          <span>Resumo exibido ao cliente</span>
          <strong>{{
            publishingScheduleSummary || "Nenhum dia aberto"
          }}</strong>
        </div>
        <article
          v-for="day in publishingForm.weeklyHours"
          :key="day.dia_semana"
          class="weekly-day"
          :class="{ 'weekly-day--closed': !day.ativo }"
        >
          <div class="weekly-day__identity">
            <span class="switch switch-sm switch-icon switch-success">
              <label>
                <input
                  v-model="day.ativo"
                  type="checkbox"
                  :aria-label="`Abrir ${weekdayLongLabel(day.dia_semana)}`"
                />
                <span></span>
              </label>
            </span>
            <strong>{{ weekdayLongLabel(day.dia_semana) }}</strong>
          </div>
          <div v-if="day.ativo" class="weekly-day__intervals">
            <div
              v-for="(interval, intervalIndex) in day.intervalos"
              :key="`${day.dia_semana}-${intervalIndex}`"
              class="weekly-interval"
            >
              <label>
                <span>Abre</span>
                <input
                  v-model="interval.inicio"
                  class="form-control"
                  type="time"
                  :aria-label="`Abertura de ${weekdayLongLabel(
                    day.dia_semana
                  )}, período ${intervalIndex + 1}`"
                />
              </label>
              <label>
                <span>Fecha</span>
                <input
                  v-model="interval.fim"
                  class="form-control"
                  type="time"
                  :aria-label="`Fechamento de ${weekdayLongLabel(
                    day.dia_semana
                  )}, período ${intervalIndex + 1}`"
                />
              </label>
              <button
                type="button"
                class="btn btn-sm btn-light-danger"
                :disabled="day.intervalos.length === 1"
                :aria-label="`Remover horário de ${weekdayLongLabel(
                  day.dia_semana
                )}`"
                @click="removeScheduleInterval(day, intervalIndex)"
              >
                Remover
              </button>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold"
              :disabled="day.intervalos.length >= 6"
              @click="addScheduleInterval(day)"
            >
              Adicionar período
            </button>
          </div>
          <span v-else class="text-muted">Fechado</span>
        </article>
        <small v-if="fieldError('horarios_semanais')" class="text-danger">
          {{ fieldError("horarios_semanais") }}
        </small>
      </div>
      <div class="modal-footer px-0 pb-0">
        <span
          v-if="publishingDirty"
          class="text-warning font-weight-bold mr-auto"
        >
          Alterações não salvas
        </span>
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="cancelPublishing"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="
            isSaving('config') ||
            !publishingDirty ||
            slugAvailability.status === 'checking' ||
            slugAvailability.status === 'unavailable' ||
            slugAvailability.status === 'error'
          "
          @click="savePublishing"
        >
          {{ isSaving("config") ? "Salvando..." : "Salvar publicação" }}
        </button>
      </div>
    </b-modal>

    <b-modal id="modalCardapioPause" title="Pausar venda" size="md" hide-footer>
      <div class="pause-modal-body">
        <div class="pause-target-box">
          <span>{{ pauseForm.typeLabel }}</span>
          <strong>{{ pauseForm.name }}</strong>
          <small>
            Ao pausar, o cliente deixa de comprar este item até a venda ser
            reativada{{
              pauseForm.type === "cardapio"
                ? "."
                : " ou até o horário escolhido."
            }}
          </small>
        </div>

        <label
          v-for="option in visiblePauseOptions"
          :key="option.value"
          class="pause-choice"
          :class="{ 'pause-choice-active': pauseForm.mode === option.value }"
        >
          <input v-model="pauseForm.mode" type="radio" :value="option.value" />
          <span>
            <strong>{{ option.label }}</strong>
            <small>{{ option.description }}</small>
          </span>
        </label>

        <div v-if="pauseForm.mode === 'custom'" class="modal-control-field">
          <label>Data e hora para reativar</label>
          <input
            v-model="pauseForm.customUntil"
            class="form-control"
            type="datetime-local"
          />
          <small>Depois desse horário, a venda volta automaticamente.</small>
        </div>

        <div v-if="pauseForm.type === 'cardapio'" class="modal-control-field">
          <label>Motivo interno</label>
          <input
            v-model="pauseForm.reason"
            class="form-control"
            maxlength="120"
            placeholder="Ex.: loja fechada, manutenção, falta de equipe"
          />
          <small
            >Ajuda a equipe a entender por que o cardápio foi pausado.</small
          >
        </div>

        <div v-if="pauseForm.type === 'cardapio'" class="modal-control-field">
          <label>Mensagem para o cliente</label>
          <textarea
            v-model="pauseForm.message"
            class="form-control"
            rows="3"
            maxlength="255"
          ></textarea>
          <small
            >Texto exibido no site enquanto o cardápio estiver pausado.</small
          >
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioPause')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-danger font-weight-bold"
          :disabled="isSaving(pauseForm.savingKey)"
          @click="confirmPauseTarget"
        >
          {{ isSaving(pauseForm.savingKey) ? "Salvando..." : "Pausar venda" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioDelivery"
      title="Entrega"
      size="lg"
      hide-footer
      @hidden="resetDeliveryDraft"
    >
      <div class="modal-control-grid">
        <div class="modal-control-field modal-control-field-wide">
          <div class="advanced-section-note">
            <strong>Taxa e regiões de entrega</strong>
            <span>
              Configure a taxa geral e, quando necessário, valores específicos
              por bairro ou faixa de CEP.
            </span>
          </div>
        </div>
        <div class="modal-control-field">
          <label>Taxa de entrega</label>
          <money
            v-model="deliveryForm.feeText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>Ex.: R$ 7,00.</small>
          <small
            v-if="fieldError('taxa_entrega_padrao_centavos')"
            class="text-danger"
          >
            {{ fieldError("taxa_entrega_padrao_centavos") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Pedido mínimo para entrega</label>
          <money
            v-model="deliveryForm.minimumText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>Ex.: R$ 25,00.</small>
          <small
            v-if="fieldError('pedido_minimo_centavos')"
            class="text-danger"
          >
            {{ fieldError("pedido_minimo_centavos") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Tempo mínimo</label>
          <input
            v-model.number="deliveryForm.minMinutes"
            class="form-control"
            type="number"
            min="0"
            max="1440"
          />
          <small>Tempo mínimo em minutos.</small>
        </div>
        <div class="modal-control-field">
          <label>Tempo máximo</label>
          <input
            v-model.number="deliveryForm.maxMinutes"
            class="form-control"
            type="number"
            min="0"
            max="1440"
          />
          <small>Tempo máximo em minutos.</small>
        </div>
        <div class="modal-control-field">
          <label>Entrega ativa</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="deliveryForm.deliveryEnabled" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Mostra entrega como opção no checkout.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Endereço do restaurante</label>
          <input v-model="deliveryForm.baseAddress" class="form-control" />
          <small>
            Informe rua, número, bairro, cidade e UF. A entrega é cotada pelo
            endereço, bairro ou CEP informado pelo cliente.
          </small>
        </div>
        <div class="modal-control-field">
          <label>Resumo</label>
          <input class="form-control" :value="deliveryPreviewLabel" readonly />
          <small>Conferência dos valores exibidos ao cliente.</small>
        </div>
      </div>

      <div class="delivery-zones">
        <div class="delivery-zones__head">
          <div>
            <strong>Zonas por bairro/CEP (opcional)</strong>
            <small>
              Defina taxa, pedido mínimo e tempo por bairro ou faixa de CEP.
              Endereços fora das zonas usam a taxa única acima.
            </small>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-light font-weight-bold"
            @click="newZone"
          >
            Adicionar zona
          </button>
        </div>

        <p v-if="!deliveryZones.length" class="delivery-zones__empty">
          Nenhuma zona cadastrada. A taxa única vale para toda a área atendida.
        </p>

        <ul v-else class="delivery-zones__list">
          <li
            v-for="zone in deliveryZones"
            :key="zone.id"
            class="delivery-zones__item"
          >
            <div class="delivery-zones__info">
              <strong>{{ zone.nome }}</strong>
              <small>{{ zoneScopeLabel(zone) }}</small>
              <small>
                Taxa {{ moneyTextFromCents(zone.taxa_entrega_centavos) }} · mín.
                {{ moneyTextFromCents(zone.pedido_minimo_centavos) }}
                <template v-if="zone.bloqueada"> · bloqueada</template>
                <template v-else-if="!zone.ativo"> · inativa</template>
              </small>
            </div>
            <div class="delivery-zones__actions">
              <button
                type="button"
                class="btn btn-sm btn-light"
                @click="editZone(zone)"
              >
                Editar
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light"
                @click="toggleZone(zone)"
              >
                {{ zone.ativo ? "Desativar" : "Ativar" }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light text-danger"
                @click="deleteZone(zone)"
              >
                Excluir
              </button>
            </div>
          </li>
        </ul>

        <div
          v-if="zoneForm.open"
          class="delivery-zones__form modal-control-grid"
        >
          <div class="modal-control-field modal-control-field-wide">
            <strong>{{ zoneForm.id ? "Editar zona" : "Nova zona" }}</strong>
          </div>
          <div class="modal-control-field">
            <label>Nome da zona</label>
            <input v-model="zoneForm.nome" class="form-control" />
          </div>
          <div class="modal-control-field">
            <label>Bairro</label>
            <input v-model="zoneForm.bairro" class="form-control" />
            <small>Casa pelo nome do bairro informado pelo cliente.</small>
          </div>
          <div class="modal-control-field">
            <label>CEP inicial</label>
            <input v-model="zoneForm.cepInicio" class="form-control" />
          </div>
          <div class="modal-control-field">
            <label>CEP final</label>
            <input v-model="zoneForm.cepFim" class="form-control" />
            <small>Preencha bairro OU faixa de CEP.</small>
          </div>
          <div class="modal-control-field">
            <label>Taxa de entrega</label>
            <money
              v-model="zoneForm.feeText"
              class="form-control"
              v-bind="moneyOptions"
            ></money>
          </div>
          <div class="modal-control-field">
            <label>Pedido mínimo</label>
            <money
              v-model="zoneForm.minimumText"
              class="form-control"
              v-bind="moneyOptions"
            ></money>
          </div>
          <div class="modal-control-field">
            <label>Frete grátis acima de</label>
            <money
              v-model="zoneForm.freeAboveText"
              class="form-control"
              v-bind="moneyOptions"
            ></money>
            <small>Deixe R$ 0,00 quando não houver.</small>
          </div>
          <div class="modal-control-field">
            <label>Tempo mínimo</label>
            <input
              v-model.number="zoneForm.minMinutes"
              class="form-control"
              type="number"
            />
          </div>
          <div class="modal-control-field">
            <label>Tempo máximo</label>
            <input
              v-model.number="zoneForm.maxMinutes"
              class="form-control"
              type="number"
            />
          </div>
          <div class="modal-control-field">
            <label>Bloquear esta zona</label>
            <span class="switch switch-outline switch-icon switch-danger">
              <label>
                <input v-model="zoneForm.bloqueada" type="checkbox" />
                <span></span>
              </label>
            </span>
            <small>Recusa entregas para o bairro/CEP.</small>
          </div>
          <div
            class="modal-control-field modal-control-field-wide delivery-zones__form-actions"
          >
            <button
              type="button"
              class="btn btn-light font-weight-bold"
              @click="cancelZone"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary font-weight-bold"
              :disabled="isSaving('delivery-zone')"
              @click="saveZone"
            >
              {{ isSaving("delivery-zone") ? "Salvando..." : "Salvar zona" }}
            </button>
          </div>
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <span
          v-if="deliveryDirty"
          class="text-warning font-weight-bold mr-auto"
        >
          Alterações não salvas
        </span>
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="cancelDelivery"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('delivery')"
          @click="saveDelivery"
        >
          {{ isSaving("delivery") ? "Salvando..." : "Salvar entrega" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioCourier"
      title="Definir entregador"
      size="lg"
      hide-footer
      @hidden="resetCourierForm"
    >
      <div class="modal-control-grid">
        <div class="modal-control-field modal-control-field-wide">
          <div class="advanced-section-note">
            <strong>Vincule um usuário existente</strong>
            <span>
              Somente usuários ativos com “Pode atuar como entregador” aparecem
              nesta lista. Ao vincular, o usuário poderá receber atribuições nos
              Pedidos Digitais e acessar a aba Entregas no aplicativo.
            </span>
          </div>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label for="cardapio-courier-user">Usuário</label>
          <select
            id="cardapio-courier-user"
            v-model="courierForm.usuarioId"
            class="form-control"
            :disabled="courierUsersLoading"
          >
            <option value="">
              {{
                courierUsersLoading
                  ? "Carregando usuários..."
                  : courierUserOptions.length
                  ? "Selecione um usuário habilitado"
                  : "Nenhum usuário habilitado disponível"
              }}
            </option>
            <option
              v-for="option in courierUserOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
          <small v-if="fieldError('usuario_id')" class="text-danger">
            {{ fieldError("usuario_id") }}
          </small>
          <small v-else-if="!courierUsersLoading && !courierUserOptions.length">
            Habilite a função no cadastro do usuário para que ele apareça aqui.
          </small>
        </div>
        <div class="modal-control-field">
          <label for="cardapio-courier-name">Nome exibido</label>
          <input
            id="cardapio-courier-name"
            v-model="courierForm.displayName"
            class="form-control"
            maxlength="120"
            placeholder="Opcional"
          />
          <small>Se vazio, será usado o nome do usuário.</small>
        </div>
        <div class="modal-control-field">
          <label for="cardapio-courier-phone">Telefone operacional</label>
          <input
            id="cardapio-courier-phone"
            v-model="courierForm.phone"
            class="form-control"
            maxlength="30"
            inputmode="tel"
            placeholder="Opcional"
          />
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light-primary font-weight-bold mr-auto"
          :disabled="isSaving('courier')"
          @click="openCourierUsers"
        >
          Abrir cadastro de usuários
        </button>
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          :disabled="isSaving('courier')"
          @click="$bvModal.hide('modalCardapioCourier')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="
            isSaving('courier') || courierUsersLoading || !courierForm.usuarioId
          "
          @click="saveCourier"
        >
          {{ isSaving("courier") ? "Vinculando..." : "Vincular usuário" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioPickup"
      title="Retirada"
      size="lg"
      hide-footer
      @hidden="resetDeliveryDraft"
    >
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Permitir retirada no balcão</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="deliveryForm.pickupEnabled" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Retirada não pede endereço do cliente.</small>
        </div>
        <div class="modal-control-field">
          <label>Tempo de preparo</label>
          <input
            v-model.number="deliveryForm.pickupMinutes"
            class="form-control"
            type="number"
          />
          <small>Estimativa mostrada para retirada.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Instrução para o cliente</label>
          <textarea
            v-model="deliveryForm.pickupMessage"
            class="form-control"
            rows="3"
            maxlength="500"
          ></textarea>
          <small>Ex.: retire no balcão informando o código do pedido.</small>
          <small v-if="fieldError('mensagem')" class="text-danger">
            {{ fieldError("mensagem") }}
          </small>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <span
          v-if="deliveryDirty"
          class="text-warning font-weight-bold mr-auto"
        >
          Alterações não salvas
        </span>
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="cancelPickup"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('pickup')"
          @click="savePickup"
        >
          {{ isSaving("pickup") ? "Salvando..." : "Salvar retirada" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioProduct"
      title="Produto no site"
      size="xl"
      hide-footer
      @hidden="closeProductDraft"
    >
      <div v-if="productForm">
        <div class="product-modal-header">
          <div class="product-photo-preview product-photo-preview-sm">
            <img
              v-if="productForm.imagePreviewUrl || productForm.imageUrl"
              :src="productForm.imagePreviewUrl || productForm.imageUrl"
              :alt="productForm.name"
            />
            <span v-else>{{ productForm.initials }}</span>
          </div>
          <div>
            <strong>{{ productForm.name }}</strong>
            <small>{{ categoryName(productForm.categoryId) }}</small>
          </div>
        </div>

        <div class="product-tabs">
          <button
            v-for="tab in productTabs"
            :key="tab.value"
            type="button"
            :class="{
              'product-tab-active': productForm.activeTab === tab.value,
            }"
            @click="productForm.activeTab = tab.value"
          >
            {{ tab.label }}
          </button>
        </div>

        <div v-if="productForm.activeTab === 'data'" class="product-modal-grid">
          <div class="modal-control-field">
            <label>Nome no site</label>
            <input v-model="productForm.name" class="form-control" />
            <small>Nome curto e claro para o cliente.</small>
            <small v-if="fieldError('nome_publico')" class="text-danger">
              {{ fieldError("nome_publico") }}
            </small>
          </div>
          <div class="modal-control-field">
            <label>Categoria</label>
            <select v-model="productForm.categoryId" class="form-control">
              <option
                v-for="category in categories"
                :key="category.id"
                :value="category.id"
              >
                {{ category.name }}
              </option>
            </select>
            <small>Define onde aparece no cardápio.</small>
            <button
              type="button"
              class="btn btn-link btn-sm pl-0"
              @click="openCategoryModal(null, false, true)"
            >
              + Nova categoria
            </button>
          </div>
          <div class="modal-control-field">
            <label>Preço exibido</label>
            <money
              v-model="productForm.priceText"
              class="form-control"
              v-bind="moneyOptions"
            ></money>
            <small>Ex.: R$ 29,90.</small>
            <small v-if="fieldError('preco_centavos')" class="text-danger">
              {{ fieldError("preco_centavos") }}
            </small>
          </div>
          <div class="modal-control-field modal-control-field-wide">
            <label>Descrição</label>
            <textarea
              v-model="productForm.description"
              class="form-control"
              rows="3"
            ></textarea>
            <small>Texto que aparece no card e no detalhe.</small>
          </div>
        </div>

        <div
          v-if="productForm.activeTab === 'photo'"
          class="product-media-panel"
        >
          <div class="product-photo-preview product-photo-preview-lg">
            <img
              v-if="productForm.imagePreviewUrl || productForm.imageUrl"
              :src="productForm.imagePreviewUrl || productForm.imageUrl"
              :alt="productForm.name"
            />
            <span v-else>{{ productForm.initials }}</span>
          </div>
          <div class="modal-control-field">
            <label>Foto do produto</label>
            <input
              class="form-control"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              @change="onProductImageSelected"
            />
            <small>
              JPG, PNG ou WebP, até 5 MB. A foto aparece no cardápio.
            </small>
            <small v-if="fieldError('imagem')" class="text-danger">
              {{ fieldError("imagem") }}
            </small>
          </div>
        </div>

        <div
          v-if="productForm.activeTab === 'options'"
          class="product-config-panel"
        >
          <div class="guided-option-panel">
            <div>
              <strong>Adicionar escolhas deste produto</strong>
              <p>
                Escolha o tipo de personalização e o formulário já vem com
                exemplos prontos para editar.
              </p>
            </div>
            <div class="guided-option-grid">
              <button
                v-for="preset in optionGroupPresets"
                :key="`product-${preset.key}`"
                type="button"
                @click="openProductOptionGroupModal(productForm, preset.key)"
              >
                <strong>{{ preset.label }}</strong>
                <span>{{ preset.short }}</span>
              </button>
            </div>
          </div>

          <div class="toolbar-line">
            <div>
              <h5>Opções deste produto</h5>
              <p>
                Cadastre aqui borda, adicionais, remoções e acompanhamentos que
                pertencem somente a este produto.
              </p>
            </div>
            <button
              type="button"
              class="btn btn-light-primary font-weight-bold"
              @click="openProductOptionGroupModal(productForm)"
            >
              Novo grupo
            </button>
          </div>
          <div
            v-if="!productOptionGroups(productForm).length"
            class="loading-box"
          >
            Nenhum grupo exclusivo deste produto.
          </div>
          <div v-else class="product-linked-list">
            <article
              v-for="group in productOptionGroups(productForm)"
              :key="group.id"
            >
              <div>
                <strong>{{ group.nome }}</strong>
                <small>
                  {{ optionTypeLabel(group.tipo) }} ·
                  {{ group.opcoes.length }} opções
                </small>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-light-primary font-weight-bold"
                @click="openOptionGroupModal(group)"
              >
                Editar
              </button>
            </article>
          </div>
        </div>

        <div
          v-if="productForm.activeTab === 'pizza'"
          class="product-config-panel"
        >
          <div class="toolbar-line">
            <div>
              <h5>Variante</h5>
              <p>
                Use quando este produto precisar de tamanho e sabores antes de
                ir para o carrinho.
              </p>
            </div>
            <button
              type="button"
              class="btn btn-light-primary font-weight-bold"
              @click="openProductPizzaModal(productForm)"
            >
              {{
                productPizzaTemplate(productForm)
                  ? "Editar variante"
                  : "Ativar variante"
              }}
            </button>
          </div>
          <div
            v-if="productPizzaTemplate(productForm)"
            class="product-linked-list"
          >
            <article>
              <div>
                <strong>
                  {{ productPizzaTemplate(productForm).nome_template }}
                </strong>
                <small>
                  {{ productPizzaTemplate(productForm).tamanhos.length }}
                  tamanhos ·
                  {{ productPizzaTemplate(productForm).sabores.length }}
                  sabores
                </small>
              </div>
              <span
                class="label label-inline"
                :class="
                  productPizzaTemplate(productForm).ativo
                    ? 'label-light-success'
                    : 'label-light-danger'
                "
              >
                {{
                  productPizzaTemplate(productForm).ativo ? "Ativa" : "Pausada"
                }}
              </span>
            </article>
          </div>
          <div v-else class="loading-box">
            Este produto ainda usa o fluxo comum de compra.
          </div>
        </div>

        <div
          v-if="productForm.activeTab === 'promotion'"
          class="product-config-panel"
        >
          <div class="toolbar-line">
            <div>
              <h5>Promoção</h5>
              <p>
                Marque o produto como destaque agora. A vitrine comercial
                completa fica na seção Promoções.
              </p>
            </div>
            <button
              type="button"
              class="btn btn-light-primary font-weight-bold"
              @click="openProductPromotionModal(productForm)"
            >
              Abrir promoções
            </button>
          </div>
          <label class="highlight-toggle-card">
            <input v-model="productForm.highlighted" type="checkbox" />
            <span>
              <strong>Destacar no cardápio</strong>
              <small>Aparece em destaques e sugestões do site.</small>
            </span>
          </label>
        </div>

        <div
          v-if="productForm.activeTab === 'availability'"
          class="product-config-panel"
        >
          <div class="modal-control-grid">
            <div class="modal-control-field">
              <label>Disponível no site</label>
              <span class="switch switch-outline switch-icon switch-success">
                <label>
                  <input v-model="productForm.available" type="checkbox" />
                  <span></span>
                </label>
              </span>
              <small>
                Use para pausar temporariamente sem apagar o produto.
              </small>
              <small
                v-if="
                  productForm.rawAvailable &&
                  !productForm.effectiveAvailable &&
                  productForm.unavailabilityReasons.length
                "
                class="d-block mt-2 text-warning"
              >
                {{ productForm.unavailabilityReasons.join(" ") }}
              </small>
            </div>
            <div class="modal-control-field">
              <label>Preparo</label>
              <input
                class="form-control"
                :value="productPreparationLabel(productForm)"
                disabled
              />
              <small>O setor vem do cadastro operacional do produto.</small>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioProduct')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-light-dark font-weight-bold"
          @click="goToProducts"
        >
          Abrir cadastro oficial
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('product')"
          @click="saveProduct"
        >
          {{ isSaving("product") ? "Salvando..." : "Salvar produto" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioProductPublisher"
      title="Publicar produto do estoque"
      size="xl"
      hide-footer
      @hidden="resetProductPublisher"
    >
      <div v-if="!publicationForm">
        <div class="stock-search-toolbar">
          <div class="modal-control-field stock-search-toolbar__query">
            <label for="cardapio-stock-search">Buscar no estoque</label>
            <input
              id="cardapio-stock-search"
              v-model.trim="stockSearch.q"
              class="form-control"
              placeholder="Nome, código ou código de barras"
              @keyup.enter="loadStockProducts(1)"
            />
          </div>
          <div class="modal-control-field">
            <label>Categoria</label>
            <select v-model="stockSearch.grupo_id" class="form-control">
              <option value="">Todas</option>
              <option
                v-for="category in categories"
                :key="`estoque-grupo-${category.id}`"
                :value="category.grupoId"
              >
                {{ category.name }}
              </option>
            </select>
          </div>
          <div class="modal-control-field">
            <label>Uso no restaurante</label>
            <select v-model="stockSearch.restaurante" class="form-control">
              <option value="">Todos</option>
              <option value="1">Já habilitados</option>
              <option value="0">Ainda não habilitados</option>
            </select>
          </div>
          <div class="modal-control-field">
            <label>Status</label>
            <select v-model="stockSearch.ativo" class="form-control">
              <option value="">Ativos e inativos</option>
              <option value="1">Ativos</option>
              <option value="0">Inativos</option>
            </select>
          </div>
          <button
            type="button"
            class="btn btn-primary font-weight-bold stock-search-toolbar__button"
            :disabled="stockLoading"
            @click="loadStockProducts(1)"
          >
            {{ stockLoading ? "Buscando..." : "Buscar" }}
          </button>
        </div>

        <div v-if="stockLoading" class="loading-box mt-5">
          Buscando produtos do estoque...
        </div>
        <div
          v-else-if="stockError"
          class="presentation-alert presentation-alert--danger mt-5"
          role="alert"
        >
          <div>
            <strong>Falha ao consultar o estoque.</strong>
            <p>{{ stockError }}</p>
            <button
              type="button"
              class="btn btn-sm btn-light-danger font-weight-bold mt-2"
              @click="loadStockProducts(1)"
            >
              Tentar novamente
            </button>
          </div>
        </div>
        <div v-else-if="!stockProducts.length" class="loading-box mt-5">
          Nenhum produto encontrado com estes filtros.
        </div>
        <div v-else class="stock-product-list mt-5">
          <button
            v-for="product in stockProducts"
            :key="`estoque-${product.productId}`"
            type="button"
            class="stock-product-card"
            @click="selectStockProduct(product)"
          >
            <img v-if="product.imageUrl" :src="product.imageUrl" alt="" />
            <span v-else class="stock-product-card__initials">
              {{ product.initials }}
            </span>
            <span class="stock-product-card__body">
              <strong>{{ product.name }}</strong>
              <small>
                {{ product.code || "Sem código" }} ·
                {{ product.groupName || "Sem categoria" }}
              </small>
              <small>
                {{ formatMoney(product.priceCents) }} ·
                {{
                  product.restaurante
                    ? "Restaurante ativo"
                    : "Habilitar restaurante"
                }}
              </small>
              <small
                v-if="
                  product.quantity !== null && Number(product.quantity) <= 0
                "
                class="text-warning"
              >
                Estoque zerado — pode configurar, mas confira antes de vender.
              </small>
            </span>
            <span
              class="label label-inline"
              :class="
                product.active ? 'label-light-success' : 'label-light-warning'
              "
            >
              {{ product.active ? "Ativo" : "Somente rascunho" }}
            </span>
          </button>
        </div>

        <div v-if="stockMeta.lastPage > 1" class="stock-pagination mt-5">
          <button
            type="button"
            class="btn btn-light font-weight-bold"
            :disabled="stockMeta.page <= 1 || stockLoading"
            @click="loadStockProducts(stockMeta.page - 1)"
          >
            Anterior
          </button>
          <span>Página {{ stockMeta.page }} de {{ stockMeta.lastPage }}</span>
          <button
            type="button"
            class="btn btn-light font-weight-bold"
            :disabled="stockMeta.page >= stockMeta.lastPage || stockLoading"
            @click="loadStockProducts(stockMeta.page + 1)"
          >
            Próxima
          </button>
        </div>
      </div>

      <div v-else>
        <button
          type="button"
          class="btn btn-link pl-0 mb-4"
          @click="publicationForm = null"
        >
          ← Voltar para a busca
        </button>
        <div class="publisher-product-summary">
          <span>{{ publicationForm.initials }}</span>
          <div>
            <strong>{{ publicationForm.name }}</strong>
            <small>
              {{ publicationForm.code || "Sem código" }} ·
              {{ formatMoney(publicationForm.priceCents) }}
            </small>
          </div>
        </div>

        <div class="modal-control-grid mt-6">
          <div class="modal-control-field modal-control-field-wide">
            <label>Preparação operacional</label>
            <label class="checkbox-line mb-3">
              <input
                v-model="publicationForm.noPreparation"
                type="checkbox"
                @change="onPublicationPreparationChange"
              />
              <span>Sem preparo</span>
            </label>
            <div
              class="destination-picker"
              :class="{ disabled: publicationForm.noPreparation }"
            >
              <label class="checkbox-line">
                <input
                  v-model="publicationForm.destinations"
                  type="checkbox"
                  value="cozinha"
                  :disabled="publicationForm.noPreparation"
                />
                <span>Cozinha</span>
              </label>
              <label class="checkbox-line">
                <input
                  v-model="publicationForm.destinations"
                  type="checkbox"
                  value="bar"
                  :disabled="publicationForm.noPreparation"
                />
                <span>Bar</span>
              </label>
            </div>
            <small
              >Bar e Cozinha podem ser combinados. Sem preparo é
              exclusivo.</small
            >
          </div>
          <div class="modal-control-field">
            <label>Categoria</label>
            <select v-model="publicationForm.categoryId" class="form-control">
              <option :value="null">Selecione</option>
              <option
                v-for="category in categories"
                :key="`publicar-categoria-${category.id}`"
                :value="category.id"
              >
                {{ category.name }}
              </option>
            </select>
            <button
              type="button"
              class="btn btn-link btn-sm pl-0"
              @click="openCategoryModal(null, true)"
            >
              + Nova categoria
            </button>
          </div>
          <div class="modal-control-field">
            <label>Canais</label>
            <label
              v-for="channel in publicationChannelOptions"
              :key="channel.value"
              class="checkbox-line mb-2"
            >
              <input
                v-model="publicationForm.channels"
                type="checkbox"
                :value="channel.value"
              />
              <span>{{ channel.label }}</span>
            </label>
          </div>
          <div class="modal-control-field modal-control-field-wide">
            <label class="checkbox-line">
              <input
                v-model="publicationForm.published"
                type="checkbox"
                :disabled="!publicationForm.active"
              />
              <span>Publicar agora nos canais selecionados</span>
            </label>
            <small v-if="!publicationForm.active" class="text-warning">
              Produto inativo será salvo como rascunho e precisará ser ativado
              no cadastro antes da publicação.
            </small>
          </div>
        </div>

        <div class="modal-footer px-0 pb-0">
          <button
            type="button"
            class="btn btn-light font-weight-bold"
            @click="$bvModal.hide('modalCardapioProductPublisher')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary font-weight-bold"
            :disabled="isSaving('product-publication')"
            @click="publishStockProduct"
          >
            {{
              isSaving("product-publication")
                ? "Salvando..."
                : publicationForm.active && publicationForm.published
                ? "Salvar e publicar"
                : "Salvar como rascunho"
            }}
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioCategory"
      :title="
        categoryForm && categoryForm.id ? 'Editar categoria' : 'Nova categoria'
      "
      size="lg"
      hide-footer
      @hidden="closeEntityDraft('category')"
    >
      <div v-if="categoryForm" class="modal-control-grid">
        <div class="modal-control-field modal-control-field-wide">
          <label>Nome do grupo e da categoria</label>
          <input
            v-model.trim="categoryForm.name"
            class="form-control"
            maxlength="120"
          />
          <small>O nome também será usado no cadastro, salão e garçom.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Descrição pública</label>
          <textarea
            v-model="categoryForm.description"
            class="form-control"
            rows="3"
            maxlength="1000"
          ></textarea>
        </div>
        <div class="modal-control-field">
          <label>Ordem</label>
          <input
            v-model.number="categoryForm.order"
            type="number"
            min="0"
            class="form-control"
          />
        </div>
        <div class="modal-control-field">
          <label>Categoria ativa</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="categoryForm.visible" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Exibir nos canais</label>
          <label
            v-for="channel in publicationChannelOptions"
            :key="`category-${channel.value}`"
            class="checkbox-line mb-2"
          >
            <input
              v-model="categoryForm.channels"
              type="checkbox"
              :value="channel.value"
            />
            <span>{{ channel.label }}</span>
          </label>
          <small>
            Retirar um canal pausa somente a exposição da categoria nele; o
            grupo e seus produtos continuam disponíveis nos demais fluxos.
          </small>
        </div>
      </div>
      <div class="shared-impact-note mt-5">
        <i class="flaticon-warning-sign"></i>
        <span
          >Salvar o nome altera o grupo compartilhado. A visibilidade continua
          específica do cardápio.</span
        >
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioCategory')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('category')"
          @click="saveCategory"
        >
          {{ isSaving("category") ? "Salvando..." : "Salvar categoria" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioVariantCreator"
      title="Nova variante"
      size="lg"
      hide-footer
    >
      <div class="variant-type-grid">
        <button type="button" @click="selectVariantType('pizza')">
          <strong>Pizza</strong>
          <span>Tamanhos, sabores, regra do sabor mais caro e bordas.</span>
        </button>
        <button type="button" @click="selectVariantType('commercial')">
          <strong>Variação comercial reutilizável</strong>
          <span
            >Tamanho, ponto ou outra escolha aplicada a produtos e
            categorias.</span
          >
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioPromotions"
      title="Promoção comercial"
      size="xl"
      hide-footer
      @hidden="closePromotionDraft"
    >
      <div class="promotion-preset-grid mb-6">
        <button
          v-for="preset in promotionPresetOptions"
          :key="preset.value"
          type="button"
          :class="{
            'promotion-preset-active': promotionForm.preset === preset.value,
          }"
          @click="applyPromotionPreset(preset.value)"
        >
          <strong>{{ preset.label }}</strong>
          <span>{{ preset.description }}</span>
        </button>
      </div>

      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Título da promoção</label>
          <input v-model="promotionForm.name" class="form-control" />
          <small>Ex.: Terça da pizza, Combo do almoço, Semana do burger.</small>
          <small v-if="fieldError('nome')" class="text-danger">
            {{ fieldError("nome") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Subtítulo</label>
          <input v-model="promotionForm.subtitle" class="form-control" />
          <small>Texto curto que aparece abaixo do título da campanha.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Descrição comercial</label>
          <textarea
            v-model="promotionForm.description"
            class="form-control"
            rows="2"
          ></textarea>
        </div>
        <div class="modal-control-field">
          <label>Onde aparece</label>
          <select
            v-model="promotionForm.targetType"
            class="form-control"
            @change="onPromotionTargetTypeChange"
          >
            <option value="produto">Produto específico</option>
            <option value="categoria">Categoria inteira</option>
            <option value="cardapio">Todo o cardápio</option>
          </select>
        </div>
        <div
          v-if="promotionForm.targetType === 'produto'"
          class="modal-control-field"
        >
          <label>Produto principal</label>
          <select
            v-model="promotionForm.productId"
            class="form-control"
            @change="syncPromotionPreviousPriceFromProduct"
          >
            <option
              v-for="product in publishedProducts"
              :key="product.id"
              :value="product.id"
            >
              {{ product.name }}
            </option>
          </select>
          <small>Item principal da campanha.</small>
        </div>
        <div
          v-if="
            promotionForm.targetType === 'produto' &&
            promotionVariantOptions.length
          "
          class="modal-control-field"
        >
          <label>Tamanho ou variante</label>
          <select
            v-model="promotionForm.variantKey"
            class="form-control"
            @change="syncPromotionPreviousPriceFromProduct"
          >
            <option value="">Selecione a variante</option>
            <option
              v-for="variant in promotionVariantOptions"
              :key="variant.key"
              :value="variant.key"
            >
              {{ variant.label }} · {{ formatMoney(variant.priceCents) }}
            </option>
          </select>
          <small>
            A promoção será aplicada somente ao tamanho ou variante escolhido.
          </small>
        </div>
        <div
          v-if="promotionForm.targetType === 'categoria'"
          class="modal-control-field"
        >
          <label>Categoria</label>
          <select v-model="promotionForm.categoryId" class="form-control">
            <option
              v-for="category in categories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </option>
          </select>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Imagem da vitrine</label>
          <div class="promotion-image-row">
            <div class="promotion-image-preview">
              <img
                v-if="promotionImagePreview"
                :src="promotionImagePreview"
                alt="Prévia da promoção"
              />
              <span v-else>{{ promotionForm.name || "Promoção" }}</span>
            </div>
            <div>
              <input
                class="form-control"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                @change="onPromotionImageSelected"
              />
              <small>
                JPG, PNG ou WebP, até 5 MB. Use uma imagem horizontal para a
                vitrine de campanhas.
              </small>
            </div>
          </div>
        </div>
        <div class="modal-control-field">
          <label>Tipo de desconto</label>
          <select v-model="promotionForm.discountType" class="form-control">
            <option value="valor">Valor fixo</option>
            <option value="percentual">Percentual</option>
          </select>
        </div>
        <div class="modal-control-field">
          <label>
            {{
              promotionForm.discountType === "percentual"
                ? "Percentual de desconto"
                : "Valor de desconto"
            }}
          </label>
          <input
            v-if="promotionForm.discountType === 'percentual'"
            v-model.number="promotionForm.discountPercent"
            class="form-control"
            min="0"
            max="100"
            type="number"
          />
          <money
            v-else
            v-model="promotionForm.discountText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>O backend recalcula o preço final no checkout.</small>
        </div>
        <div class="modal-control-field">
          <label>Preço anterior exibido</label>
          <money
            v-model="promotionForm.previousPriceText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>Preenchido com o preço atual do produto selecionado.</small>
        </div>
        <div class="modal-control-field">
          <label>Preço promocional exibido</label>
          <money
            v-model="promotionForm.promotionalPriceText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>
            Quando informado para um produto, calculamos o desconto a partir do
            preço atual.
          </small>
        </div>
        <div class="modal-control-field">
          <label>Início</label>
          <input
            v-model="promotionForm.start"
            class="form-control"
            type="date"
          />
        </div>
        <div class="modal-control-field">
          <label>Fim</label>
          <input v-model="promotionForm.end" class="form-control" type="date" />
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Chamada curta do banner</label>
          <input v-model="promotionForm.bannerText" class="form-control" />
          <small>Chamada curta para aparecer na vitrine do cardápio.</small>
        </div>
        <div
          v-if="promotionForm.preset === 'compre_ganhe'"
          class="modal-control-field modal-control-field-wide"
        >
          <label>O que o cliente ganha</label>
          <input v-model="promotionForm.giftText" class="form-control" />
          <small>Ex.: Ganhe uma bebida, sobremesa ou borda grátis.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Dias da semana</label>
          <div class="weekday-picker">
            <label v-for="day in weekdays" :key="day.value">
              <input
                v-model="promotionForm.weekdays"
                type="checkbox"
                :value="day.value"
              />
              <span>{{ day.label }}</span>
            </label>
          </div>
          <small>Sem seleção significa disponível em todos os dias.</small>
        </div>
        <div class="modal-control-field">
          <label>Promoção ativa</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="promotionForm.enabled" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioPromotions')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('promotion')"
          @click="savePromotion"
        >
          {{ isSaving("promotion") ? "Salvando..." : "Salvar promoção" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioCoupon"
      title="Cupom"
      size="lg"
      hide-footer
      @hidden="closeEntityDraft('coupon')"
    >
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Código</label>
          <input v-model="couponForm.code" class="form-control" />
          <small>Ex.: PIZZA10, RETIRA5, CLIENTE20.</small>
        </div>
        <div class="modal-control-field">
          <label>Nome interno</label>
          <input v-model="couponForm.name" class="form-control" />
        </div>
        <div class="modal-control-field">
          <label>Tipo de desconto</label>
          <select v-model="couponForm.discountType" class="form-control">
            <option value="valor">Valor fixo</option>
            <option value="percentual">Percentual</option>
          </select>
        </div>
        <div class="modal-control-field">
          <label>Desconto</label>
          <input
            v-if="couponForm.discountType === 'percentual'"
            v-model.number="couponForm.discountPercent"
            class="form-control"
            min="0"
            max="100"
            type="number"
          />
          <money
            v-else
            v-model="couponForm.discountText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
        </div>
        <div class="modal-control-field">
          <label>Pedido mínimo</label>
          <money
            v-model="couponForm.minimumText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
        </div>
        <div class="modal-control-field">
          <label>Limite total de uso</label>
          <input
            v-model.number="couponForm.totalLimit"
            class="form-control"
            min="1"
            type="number"
          />
        </div>
        <div class="modal-control-field">
          <label>Início</label>
          <input v-model="couponForm.start" class="form-control" type="date" />
        </div>
        <div class="modal-control-field">
          <label>Fim</label>
          <input v-model="couponForm.end" class="form-control" type="date" />
        </div>
        <div class="modal-control-field">
          <label>Cupom ativo</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="couponForm.enabled" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioCoupon')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('coupon')"
          @click="saveCoupon"
        >
          {{ isSaving("coupon") ? "Salvando..." : "Salvar cupom" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioCombo"
      title="Combo"
      size="lg"
      hide-footer
      @hidden="closeEntityDraft('combo')"
    >
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Produto publicado</label>
          <select v-model="comboForm.productId" class="form-control">
            <option
              v-for="product in publishedProducts"
              :key="product.id"
              :value="product.id"
            >
              {{ product.name }}
            </option>
          </select>
          <small>Este produto passa a representar o combo no cardápio.</small>
        </div>
        <div class="modal-control-field">
          <label>Nome do combo</label>
          <input v-model="comboForm.name" class="form-control" />
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Descrição</label>
          <textarea
            v-model="comboForm.description"
            class="form-control"
            rows="3"
          ></textarea>
        </div>
        <div class="modal-control-field">
          <label>Preço do combo</label>
          <money
            v-model="comboForm.priceText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
        </div>
        <div class="modal-control-field">
          <label>Combo ativo</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="comboForm.enabled" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Composição</label>
          <textarea
            v-model="comboForm.itemsText"
            class="form-control"
            rows="3"
          ></textarea>
          <small>Informe um item por linha. Ex.: Burger, batata, bebida.</small>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioCombo')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('combo')"
          @click="saveCombo"
        >
          {{ isSaving("combo") ? "Salvando..." : "Salvar combo" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioCheckout"
      title="Pagamento presencial"
      size="lg"
      hide-footer
      @hidden="resetCheckoutDraft"
    >
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Dinheiro presencial</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="checkoutForm.cash" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Permite informar troco para entrega ou retirada.</small>
        </div>
        <div class="modal-control-field">
          <label>Cartão/Pix no balcão</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="checkoutForm.card" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>
            Na retirada, o cliente verá “Cartão/Pix no balcão”. Na entrega, esta
            opção representa somente cartão na maquininha.
          </small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Mensagem ao cliente</label>
          <textarea
            v-model="checkoutForm.message"
            class="form-control"
            rows="3"
            maxlength="500"
          ></textarea>
          <small>Texto exibido antes de finalizar.</small>
          <small v-if="fieldError('mensagem')" class="text-danger">
            {{ fieldError("mensagem") }}
          </small>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <span
          v-if="checkoutDirty"
          class="text-warning font-weight-bold mr-auto"
        >
          Alterações não salvas
        </span>
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="cancelCheckout"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('checkout')"
          @click="saveCheckout"
        >
          {{
            isSaving("checkout") ? "Salvando..." : "Salvar pagamento presencial"
          }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioVisual"
      title="Aparência e mensagens"
      size="xl"
      dialog-class="cardapio-visual-dialog"
      content-class="cardapio-visual-content"
      body-class="cardapio-visual-body"
      hide-footer
      @shown="initVisualPreview"
      @hidden="cancelVisual(false)"
    >
      <div class="visual-editor">
        <div>
          <b-tabs pills content-class="pt-5">
            <b-tab title="Marca" active>
              <div class="visual-upload-card">
                <label>Logo do cardápio</label>
                <div class="visual-logo-preview">
                  <img
                    v-if="visualLogoPreview"
                    :src="visualLogoPreview"
                    alt="Prévia da logo"
                  />
                  <span v-else>{{ config.name ? config.name[0] : "C" }}</span>
                </div>
                <input
                  class="form-control"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  @change="onVisualImageSelected($event, 'logo')"
                />
                <small>
                  JPG, PNG ou WebP, até 5 MB. Use uma imagem quadrada para topo,
                  busca e checkout.
                </small>
                <small v-if="fieldError('logo')" class="text-danger">
                  {{ fieldError("logo") }}
                </small>
                <button
                  v-if="visualLogoPreview"
                  type="button"
                  class="btn btn-sm btn-light-danger font-weight-bold mt-3"
                  @click="removeVisualImage('logo')"
                >
                  Remover logo
                </button>
              </div>
            </b-tab>

            <b-tab title="Imagens">
              <div class="visual-upload-card">
                <label>Capa do site</label>
                <div class="visual-cover-preview">
                  <img
                    v-if="visualCoverPreview"
                    :src="visualCoverPreview"
                    alt="Prévia da capa"
                  />
                  <div v-else>
                    <strong>{{ config.name || "Cardápio Online" }}</strong>
                    <span>Imagem principal exibida no topo do site.</span>
                  </div>
                </div>
                <input
                  class="form-control"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  @change="onVisualImageSelected($event, 'capa')"
                />
                <small>
                  JPG, PNG ou WebP, até 5 MB. Prefira foto horizontal de
                  ambiente, produto ou fachada.
                </small>
                <small v-if="fieldError('capa')" class="text-danger">
                  {{ fieldError("capa") }}
                </small>
                <button
                  v-if="visualCoverPreview"
                  type="button"
                  class="btn btn-sm btn-light-danger font-weight-bold mt-3"
                  @click="removeVisualImage('capa')"
                >
                  Remover capa
                </button>
              </div>
            </b-tab>

            <b-tab title="Tema">
              <p class="visual-choice-label">Temas prontos</p>
              <div class="theme-preset-grid">
                <button
                  v-for="preset in themePresets"
                  :key="preset.id"
                  type="button"
                  class="theme-preset"
                  :class="{
                    'theme-preset--active': selectedTheme === preset.id,
                  }"
                  @click="applyThemePreset(preset)"
                >
                  <span class="theme-preset__colors">
                    <i :style="{ background: preset.primary }"></i>
                    <i :style="{ background: preset.accent }"></i>
                    <i :style="{ background: preset.background }"></i>
                  </span>
                  <strong>{{ preset.label }}</strong>
                  <small>{{ preset.description }}</small>
                </button>
              </div>
              <div class="advanced-theme-toggle mt-5">
                <button
                  type="button"
                  class="btn btn-sm btn-light-primary font-weight-bold"
                  @click="advancedTheme = !advancedTheme"
                >
                  {{
                    advancedTheme
                      ? "Ocultar modo avançado"
                      : "Personalizar cores"
                  }}
                </button>
              </div>
              <div class="modal-control-grid">
                <div
                  v-if="advancedTheme"
                  class="modal-control-field color-control"
                >
                  <label>Cor principal</label>
                  <div>
                    <input v-model="visualForm.primaryColor" type="color" />
                    <input
                      v-model.trim="visualForm.primaryColor"
                      class="form-control"
                      maxlength="7"
                      :class="{
                        'is-invalid': !isValidHexColor(visualForm.primaryColor),
                      }"
                    />
                  </div>
                  <small>Botões principais, abas ativas e destaques.</small>
                </div>
                <div
                  v-if="advancedTheme"
                  class="modal-control-field color-control"
                >
                  <label>Cor de apoio</label>
                  <div>
                    <input v-model="visualForm.accentColor" type="color" />
                    <input
                      v-model.trim="visualForm.accentColor"
                      class="form-control"
                      maxlength="7"
                      :class="{
                        'is-invalid': !isValidHexColor(visualForm.accentColor),
                      }"
                    />
                  </div>
                  <small>Banners, detalhes suaves e áreas promocionais.</small>
                </div>
                <div
                  v-if="advancedTheme"
                  class="modal-control-field color-control"
                >
                  <label>Cor de fundo</label>
                  <div>
                    <input v-model="visualForm.backgroundColor" type="color" />
                    <input
                      v-model.trim="visualForm.backgroundColor"
                      class="form-control"
                      maxlength="7"
                      :class="{
                        'is-invalid': !isValidHexColor(
                          visualForm.backgroundColor
                        ),
                      }"
                    />
                  </div>
                  <small>Fundo principal do cardápio público.</small>
                </div>
              </div>
              <div v-if="visualContrastWarning" class="visual-warning">
                {{ visualContrastWarning }}
              </div>
              <p class="visual-choice-label mt-6">Estilo dos cards</p>
              <div class="visual-choice-grid">
                <button
                  v-for="style in cardStyleOptions"
                  :key="style.value"
                  type="button"
                  class="visual-choice"
                  :class="{
                    'visual-choice--active':
                      visualForm.cardStyle === style.value,
                  }"
                  @click="visualForm.cardStyle = style.value"
                >
                  <strong>{{ style.label }}</strong>
                  <span>{{ style.description }}</span>
                </button>
              </div>

              <p class="visual-choice-label mt-4">Formato dos botões</p>
              <div class="visual-choice-grid">
                <button
                  v-for="style in buttonStyleOptions"
                  :key="style.value"
                  type="button"
                  class="visual-choice"
                  :class="{
                    'visual-choice--active':
                      visualForm.buttonStyle === style.value,
                  }"
                  @click="visualForm.buttonStyle = style.value"
                >
                  <strong>{{ style.label }}</strong>
                  <span>{{ style.description }}</span>
                </button>
              </div>
            </b-tab>

            <b-tab title="Mensagens">
              <div class="modal-control-grid">
                <div class="modal-control-field">
                  <label>Sem produtos</label>
                  <input
                    v-model="visualForm.emptyText"
                    class="form-control"
                    maxlength="500"
                  />
                  <small>Aparece quando não houver itens disponíveis.</small>
                  <small v-if="fieldError('empty_text')" class="text-danger">
                    {{ fieldError("empty_text") }}
                  </small>
                </div>
                <div class="modal-control-field">
                  <label>Erro ao carregar</label>
                  <input
                    v-model="visualForm.errorText"
                    class="form-control"
                    maxlength="500"
                  />
                  <small>Aparece se o cardápio não abrir.</small>
                  <small v-if="fieldError('error_text')" class="text-danger">
                    {{ fieldError("error_text") }}
                  </small>
                </div>
                <div class="modal-control-field">
                  <label>Acompanhamento</label>
                  <input
                    v-model="visualForm.trackingText"
                    class="form-control"
                    maxlength="500"
                  />
                  <small>Texto de apoio na tela de status do pedido.</small>
                  <small v-if="fieldError('tracking_text')" class="text-danger">
                    {{ fieldError("tracking_text") }}
                  </small>
                </div>
              </div>
              <div class="shared-impact-note mt-4">
                <i class="flaticon2-information"></i>
                <span>
                  Loja fechada é editada em Publicação e horários. A mensagem do
                  checkout é editada em Pagamentos.
                </span>
              </div>
            </b-tab>

            <b-tab title="Pré-visualização">
              <div class="modal-control-field mb-4">
                <label>Cenário da prévia</label>
                <select v-model="previewScenario" class="form-control">
                  <option
                    v-for="scenario in previewScenarios"
                    :key="scenario.value"
                    :value="scenario.value"
                  >
                    {{ scenario.label }}
                  </option>
                </select>
              </div>
              <div class="visual-preview-note">
                A prévia ao lado é o próprio cardápio público, atualizado em
                tempo real com cores, estilos, imagens e mensagens enquanto você
                edita.
              </div>
            </b-tab>
          </b-tabs>
        </div>

        <aside class="visual-live-preview-panel">
          <header class="visual-live-preview-panel__header">
            <div>
              <strong>Prévia mobile</strong>
              <span>Visualização real em 390 × 844 px</span>
            </div>
            <span class="visual-live-preview-panel__badge">Ao vivo</span>
          </header>
          <div class="visual-live-preview">
            <iframe
              v-if="visualPreviewSrc"
              ref="visualPreviewFrame"
              :src="visualPreviewSrc"
              class="visual-live-preview__frame"
              title="Prévia mobile do cardápio"
              loading="lazy"
              @load="pushVisualPreviewTheme"
            ></iframe>
            <div v-else class="visual-live-preview__empty">
              <strong>Prévia indisponível</strong>
              <span>
                Publique o cardápio (aba Publicação) para ver a prévia em tempo
                real aqui.
              </span>
            </div>
          </div>
        </aside>
      </div>
      <div class="modal-footer px-0 pb-0">
        <span v-if="visualDirty" class="text-warning font-weight-bold mr-auto">
          Alterações não salvas
        </span>
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="cancelVisual"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('visual') || !visualDirty || !visualColorsValid"
          @click="saveVisual"
        >
          {{ isSaving("visual") ? "Salvando..." : "Salvar visual" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioOptionProductPicker"
      title="Criar opção em produto"
      size="lg"
      hide-footer
    >
      <div class="advanced-section-note">
        <strong>Escolha o produto</strong>
        <span>
          Use este caminho para borda, adicionais, molhos, acompanhamentos ou
          remoções que só fazem sentido em um item específico.
        </span>
      </div>

      <div class="option-product-picker">
        <button
          v-for="product in configuredProducts"
          :key="product.id"
          type="button"
          @click="selectProductForOptionGroup(product)"
        >
          <img v-if="product.imageUrl" :src="product.imageUrl" alt="" />
          <span v-else>{{ product.initials }}</span>
          <strong>{{ product.name }}</strong>
          <small>{{ categoryName(product.categoryId) }}</small>
        </button>
      </div>

      <div v-if="!configuredProducts.length" class="loading-box">
        Configure um produto para o cardápio ou salão antes de criar opções.
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioOptionGroup"
      :title="optionGroupModalTitle"
      size="xl"
      hide-footer
      @hidden="closeEntityDraft('optionGroup')"
    >
      <div class="advanced-section-note">
        <strong>{{ optionGroupModalSubtitle }}</strong>
        <span>
          {{ optionGroupModalDescription }}
        </span>
      </div>

      <div v-if="optionGroupForm" class="option-preset-panel">
        <button
          v-for="preset in optionGroupPresets"
          :key="preset.key"
          type="button"
          :class="{
            'option-preset-active': optionGroupPresetMatches(preset),
          }"
          @click="applyOptionGroupPreset(preset.key)"
        >
          <strong>{{ preset.label }}</strong>
          <span>{{ preset.description }}</span>
        </button>
      </div>

      <div v-if="optionGroupForm" class="modal-control-grid">
        <div
          v-if="optionGroupForm.mode === 'produto'"
          class="modal-control-field"
        >
          <label>Produto</label>
          <input
            :value="productName(optionGroupForm.cardapio_produto_id)"
            class="form-control"
            disabled
          />
          <small>
            Este grupo aparece somente neste produto. Para outro item, abra o
            produto correspondente no catálogo.
          </small>
        </div>
        <div v-else class="modal-control-field">
          <label>Alcance global</label>
          <select v-model="optionGroupForm.globalScope" class="form-control">
            <option value="todos">Todo o cardápio</option>
            <option value="categorias">Categorias selecionadas</option>
            <option value="produtos">Produtos selecionados</option>
          </select>
          <small>Grupo global não pertence a um produto específico.</small>
        </div>
        <div
          v-if="
            optionGroupForm.mode === 'global' &&
            optionGroupForm.globalScope === 'produtos'
          "
          class="modal-control-field modal-control-field-wide"
        >
          <label>Produtos que recebem este grupo</label>
          <div class="option-category-picker">
            <label
              v-for="product in configuredProducts"
              :key="`opcao-produto-${product.id}`"
              class="checkbox-line"
            >
              <input
                v-model="optionGroupForm.produto_ids"
                type="checkbox"
                :value="product.id"
              />
              <span>{{ product.name }}</span>
            </label>
          </div>
          <small>
            O grupo será reutilizado somente nos produtos marcados, sem criar
            novas combinações de estoque.
          </small>
        </div>
        <div
          v-if="
            optionGroupForm.mode === 'global' &&
            optionGroupForm.globalScope === 'categorias'
          "
          class="modal-control-field modal-control-field-wide"
        >
          <label>Categorias que recebem este grupo</label>
          <div class="option-category-picker">
            <label
              v-for="category in categories"
              :key="category.id"
              class="checkbox-line"
            >
              <input
                v-model="optionGroupForm.categoria_ids"
                type="checkbox"
                :value="category.id"
              />
              <span>{{ category.name }}</span>
            </label>
          </div>
          <small>
            Deixe como todo o cardápio quando a escolha deve aparecer em todos
            os produtos.
          </small>
        </div>
        <div class="modal-control-field">
          <label>Função do grupo</label>
          <select v-model="optionGroupForm.papel" class="form-control">
            <option value="complemento">Complemento ou adicional</option>
            <option value="variante">Variação comercial</option>
          </select>
          <small>Variação comercial não cria combinação de estoque.</small>
        </div>
        <div class="modal-control-field">
          <label>Nome do grupo</label>
          <input v-model="optionGroupForm.nome" class="form-control" />
          <small v-if="fieldError('nome')" class="text-danger">
            {{ fieldError("nome") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Tipo</label>
          <select v-model="optionGroupForm.tipo" class="form-control">
            <option
              v-for="type in optionGroupTypes"
              :key="type.value"
              :value="type.value"
            >
              {{ type.label }}
            </option>
          </select>
          <small>{{ optionGroupTypeHelp }}</small>
        </div>
        <div class="modal-control-field">
          <label>Mínimo</label>
          <input
            v-model.number="optionGroupForm.minimo"
            type="number"
            min="0"
            class="form-control"
          />
        </div>
        <div class="modal-control-field">
          <label>Máximo</label>
          <input
            v-model.number="optionGroupForm.maximo"
            type="number"
            min="1"
            class="form-control"
          />
        </div>
        <div class="modal-control-field">
          <label>Obrigatório</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="optionGroupForm.obrigatorio" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
        <div class="modal-control-field">
          <label>Múltipla quantidade</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input
                v-model="optionGroupForm.permite_quantidade"
                type="checkbox"
              />
              <span></span>
            </label>
          </span>
        </div>
        <div class="modal-control-field">
          <label>Ativo</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="optionGroupForm.ativo" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Canais que usam este grupo</label>
          <div class="destination-picker">
            <label
              v-for="channel in publicationChannelOptions"
              :key="`opcao-canal-${channel.value}`"
              class="checkbox-line"
            >
              <input
                v-model="optionGroupForm.canais"
                type="checkbox"
                :value="channel.value"
              />
              <span>{{ channel.label }}</span>
            </label>
          </div>
        </div>
      </div>

      <div v-if="optionGroupForm" class="option-editor mt-6">
        <div class="toolbar-line">
          <div>
            <h5>Itens do grupo</h5>
            <p>
              {{ optionGroupItemHint }}
            </p>
          </div>
          <div class="toolbar-actions">
            <button
              type="button"
              class="btn btn-light-primary font-weight-bold"
              @click="addOptionItem"
            >
              Adicionar opção manual
            </button>
          </div>
        </div>

        <div class="bar-product-picker mb-5">
          <div>
            <strong>Produtos do Bar e da Cozinha</strong>
            <small>
              O preço acompanha o cadastro e será congelado no pedido. O item
              seguirá ao setor configurado no produto como item real, não como
              observação.
            </small>
          </div>
          <select
            v-model="operationalProductDestination"
            class="form-control"
            :disabled="operationalProductsLoading"
            @change="loadOperationalProducts"
          >
            <option value="">Bar e Cozinha</option>
            <option value="cozinha">Somente Cozinha</option>
            <option value="bar">Somente Bar</option>
          </select>
          <div class="input-group">
            <input
              v-model.trim="operationalProductSearch"
              class="form-control"
              placeholder="Buscar produto operacional"
              :disabled="operationalProductsLoading"
              @keyup.enter="loadOperationalProducts"
            />
            <div class="input-group-append">
              <button
                type="button"
                class="btn btn-light-dark"
                :disabled="operationalProductsLoading"
                @click="loadOperationalProducts"
              >
                {{ operationalProductsLoading ? "Buscando..." : "Buscar" }}
              </button>
            </div>
          </div>
          <p
            v-if="operationalProductsError"
            class="text-danger mb-0"
            role="alert"
          >
            {{ operationalProductsError }}
          </p>
          <select
            v-model="operationalProductSelection"
            class="form-control"
            :disabled="
              operationalProductsLoading || !operationalProducts.length
            "
          >
            <option :value="null">Selecione um produto</option>
            <option
              v-for="product in operationalProducts"
              :key="`operational-${product.productId}`"
              :value="product.productId"
            >
              {{ product.name }} · {{ operationalDestinationLabel(product) }} ·
              {{ formatMoney(product.priceCents) }}
            </option>
          </select>
          <button
            type="button"
            class="btn btn-light-dark font-weight-bold"
            :disabled="
              operationalProductsLoading || !operationalProductSelection
            "
            @click="addOperationalProductOption"
          >
            Adicionar produto vinculado
          </button>
        </div>

        <div
          v-for="(option, index) in optionGroupForm.opcoes"
          :key="option.localKey"
          class="option-editor-row"
        >
          <input
            v-model="option.nome"
            class="form-control"
            placeholder="Nome da opção"
          />
          <input
            v-model="option.descricao"
            class="form-control"
            placeholder="Descrição curta"
          />
          <money
            v-model="option.precoText"
            class="form-control"
            v-bind="moneyOptions"
            :disabled="
              option.efeito_operacional === 'produto_adicional' ||
              option.efeito_operacional === 'observacao' ||
              optionGroupForm.tipo === 'remocao_sem_preco'
            "
          ></money>
          <details class="option-advanced-link">
            <summary>Comportamento operacional</summary>
            <select
              v-model="option.efeito_operacional"
              class="form-control mb-2"
              @change="onOptionEffectChange(option)"
            >
              <option value="modificador">Modificador do item</option>
              <option value="produto_adicional">Produto adicional real</option>
              <option value="observacao">Somente observação</option>
            </select>
            <select
              v-model="option.produto_id"
              class="form-control"
              :disabled="option.efeito_operacional !== 'produto_adicional'"
              @change="onOptionLinkedProductChange(option)"
            >
              <option :value="null">Sem vínculo interno</option>
              <option
                v-for="product in linkableProducts"
                :key="`interno-${product.productId}`"
                :value="product.productId"
              >
                {{ product.name }}
              </option>
            </select>
          </details>
          <label class="checkbox-line">
            <input v-model="option.disponivel" type="checkbox" />
            Disponível
          </label>
          <button
            type="button"
            class="btn btn-sm btn-light-danger font-weight-bold"
            @click="removeOptionItem(index)"
          >
            Remover
          </button>
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioOptionGroup')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('option-group')"
          @click="saveOptionGroup"
        >
          {{ isSaving("option-group") ? "Salvando..." : "Salvar grupo" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioPizza"
      title="Variante"
      size="xl"
      hide-footer
      @hidden="closeEntityDraft('pizza')"
    >
      <div v-if="pizzaForm" class="modal-control-grid">
        <div class="modal-control-field">
          <label>Produto publicado</label>
          <select v-model="pizzaForm.cardapio_produto_id" class="form-control">
            <option
              v-for="product in configuredProducts"
              :key="product.id"
              :value="product.id"
            >
              {{ product.name }}
            </option>
          </select>
          <small>Este produto passa a pedir tamanho e sabores no site.</small>
        </div>
        <div class="modal-control-field">
          <label>Nome da variante</label>
          <input v-model="pizzaForm.nome_template" class="form-control" />
          <small v-if="fieldError('nome_template')" class="text-danger">
            {{ fieldError("nome_template") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Máximo padrão de sabores</label>
          <input
            v-model.number="pizzaForm.max_sabores_padrao"
            class="form-control"
            min="1"
            type="number"
          />
        </div>
        <div class="modal-control-field">
          <label>Template ativo</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="pizzaForm.ativo" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
      </div>

      <div v-if="pizzaForm" class="option-editor mt-6">
        <div class="toolbar-line">
          <div>
            <h5>Tamanhos</h5>
            <p>
              Defina preço base e limite de sabores. O valor final usa somente o
              adicional do sabor de maior valor.
            </p>
          </div>
          <button
            type="button"
            class="btn btn-light-primary font-weight-bold"
            @click="addPizzaSize"
          >
            Adicionar tamanho
          </button>
        </div>

        <div
          v-for="(size, index) in pizzaForm.tamanhos"
          :key="size.localKey"
          class="option-editor-row"
        >
          <input v-model="size.nome" class="form-control" placeholder="Nome" />
          <money
            v-model="size.precoText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <input
            v-model.number="size.max_sabores"
            class="form-control"
            min="1"
            type="number"
          />
          <input
            v-model.number="size.fatias"
            class="form-control"
            min="1"
            type="number"
            placeholder="Fatias"
          />
          <label class="checkbox-line">
            <input v-model="size.ativo" type="checkbox" />
            Ativo
          </label>
          <button
            type="button"
            class="btn btn-sm btn-light-danger font-weight-bold"
            @click="removePizzaSize(index)"
          >
            Remover
          </button>
        </div>
      </div>

      <div v-if="pizzaForm" class="option-editor mt-6">
        <div class="toolbar-line">
          <div>
            <h5>Sabores</h5>
            <p>
              Informe nome e preço de referência. Restrinja por tamanho somente
              quando o sabor não serve para todos.
            </p>
          </div>
          <button
            type="button"
            class="btn btn-light-primary font-weight-bold"
            @click="addPizzaFlavor"
          >
            Adicionar sabor
          </button>
        </div>

        <div
          v-for="(flavor, index) in pizzaForm.sabores"
          :key="flavor.localKey"
          class="option-editor-row option-editor-row-wide"
        >
          <input
            v-model="flavor.nome"
            class="form-control"
            placeholder="Nome"
          />
          <input
            v-model="flavor.descricao"
            class="form-control"
            placeholder="Descrição"
          />
          <money
            v-model="flavor.precoText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <select v-model="flavor.tamanhos" class="form-control" multiple>
            <option
              v-for="size in pizzaForm.tamanhos"
              :key="size.localKey"
              :value="size.slug"
            >
              {{ size.nome || size.slug }}
            </option>
          </select>
          <label class="checkbox-line">
            <input v-model="flavor.disponivel" type="checkbox" />
            Disponível
          </label>
          <button
            type="button"
            class="btn btn-sm btn-light-danger font-weight-bold"
            @click="removePizzaFlavor(index)"
          >
            Remover
          </button>
        </div>
      </div>

      <div v-if="pizzaForm" class="option-editor mt-6">
        <div class="toolbar-line">
          <div>
            <h5>Bordas</h5>
            <p>Cadastre bordas próprias da pizza e os tamanhos compatíveis.</p>
          </div>
          <button
            type="button"
            class="btn btn-light-primary font-weight-bold"
            @click="addPizzaBorder"
          >
            Adicionar borda
          </button>
        </div>

        <div
          v-for="(border, index) in pizzaForm.bordas"
          :key="border.localKey"
          class="option-editor-row option-editor-row-wide"
        >
          <input
            v-model="border.nome"
            class="form-control"
            placeholder="Nome"
          />
          <input
            v-model="border.descricao"
            class="form-control"
            placeholder="Descrição"
          />
          <money
            v-model="border.precoText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <select v-model="border.tamanhos" class="form-control" multiple>
            <option
              v-for="size in pizzaForm.tamanhos"
              :key="`borda-${size.localKey}`"
              :value="size.slug"
            >
              {{ size.nome || size.slug }}
            </option>
          </select>
          <label class="checkbox-line">
            <input v-model="border.disponivel" type="checkbox" />
            Disponível
          </label>
          <button
            type="button"
            class="btn btn-sm btn-light-danger font-weight-bold"
            @click="removePizzaBorder(index)"
          >
            Remover
          </button>
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioPizza')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('pizza')"
          @click="savePizza"
        >
          {{ isSaving("pizza") ? "Salvando..." : "Salvar variante" }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Money } from "v-money";
import {
  CARDAPIO_PREVIEW_SCENARIOS,
  CARDAPIO_THEME_PRESETS,
  CARDAPIO_WORKSPACES,
  applyWeeklyHoursPreset,
  colorContrast as calculateColorContrast,
  defaultWeeklyHours as createDefaultWeeklyHours,
  isValidHexColor as validateHexColor,
  mergeProductSources,
  normalizeBoolean,
  normalizeHexColor,
  normalizeReadiness as normalizeReadinessValue,
  normalizeWeeklyHours as normalizeWeeklyHoursValue,
  parseBRLMoneyToCents,
  sameIdentifier,
  serializeVisualDraft as serializeVisualDraftValue,
  summarizeWeeklyHours,
  validateWeeklyHours as validateWeeklyHoursValue,
} from "./hubConfig";

export default {
  name: "app-restaurante-cardapio-online",
  components: {
    Money,
  },
  data() {
    return {
      activeSection: "overview",
      managementLoading: false,
      managementLoaded: false,
      managementError: "",
      managementRequestSequence: 0,
      saving: {},
      fieldErrors: {},
      entityDraftBaselines: {},
      moneyOptions: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        suffix: "",
        precision: 2,
        masked: true,
      },
      productForm: null,
      publicationForm: null,
      categoryForm: null,
      categoryCreatedForPublisher: false,
      categoryCreatedForProduct: false,
      stockLoading: false,
      stockError: "",
      stockRequestSequence: 0,
      stockSearch: {
        q: "",
        grupo_id: "",
        restaurante: "",
        ativo: "",
      },
      stockProducts: [],
      stockMeta: {
        page: 1,
        lastPage: 1,
        total: 0,
      },
      operationalProducts: [],
      operationalProductsLoading: false,
      operationalProductsError: "",
      operationalProductsRequestSequence: 0,
      operationalProductSearch: "",
      operationalProductSelection: null,
      operationalProductDestination: "",
      optionGroupForm: null,
      pizzaForm: null,
      publicationChannelOptions: [
        { value: "cardapio_digital", label: "Cardápio Digital" },
        { value: "self_pedido", label: "Self Pedido" },
        { value: "salao_garcom", label: "Salão e garçom" },
      ],
      productTabs: [
        { value: "data", label: "Dados do site" },
        { value: "photo", label: "Foto" },
        { value: "options", label: "Opções" },
        { value: "pizza", label: "Variante" },
        { value: "promotion", label: "Promoção" },
        { value: "availability", label: "Disponibilidade" },
      ],
      weekdays: [
        { value: 0, label: "Dom" },
        { value: 1, label: "Seg" },
        { value: 2, label: "Ter" },
        { value: 3, label: "Qua" },
        { value: 4, label: "Qui" },
        { value: 5, label: "Sex" },
        { value: 6, label: "Sáb" },
      ],
      weekdayLongLabels: [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado",
      ],
      pauseOptions: [
        {
          value: "manual",
          label: "Até ativar novamente",
          description: "Mantém indisponível até alguém reativar.",
        },
        {
          value: "hour",
          label: "Por 1 hora",
          description: "Bom para pausa rápida de preparo ou estoque.",
        },
        {
          value: "today",
          label: "Até o fim do dia",
          description: "Volta automaticamente amanhã.",
        },
        {
          value: "custom",
          label: "Escolher data e hora",
          description: "Use quando já souber quando a venda retorna.",
        },
      ],
      pauseForm: {
        type: null,
        target: null,
        typeLabel: "",
        name: "",
        mode: "manual",
        customUntil: "",
        reason: "",
        message: "",
        savingKey: "pause",
      },
      optionGroupTypes: [
        { value: "escolha_unica", label: "Escolha única" },
        { value: "multipla_escolha", label: "Múltipla escolha" },
        { value: "adicional_pago", label: "Adicional pago" },
        { value: "remocao_sem_preco", label: "Remoção sem preço" },
        { value: "observacao_textual", label: "Observação textual" },
        {
          value: "acompanhamento_obrigatorio",
          label: "Acompanhamento obrigatório",
        },
      ],
      optionGroupPresets: [
        {
          key: "borda",
          label: "Borda",
          short: "Sem borda, recheada",
          description: "Para pizza, lanche ou prato com opção de borda.",
          name: "Borda",
          type: "escolha_unica",
          min: 0,
          max: 1,
          required: false,
          quantity: false,
          items: [
            {
              name: "Sem borda",
              description: "Produto sem borda adicional",
              price: 0,
            },
            {
              name: "Borda recheada",
              description: "Borda com recheio",
              price: 800,
            },
          ],
        },
        {
          key: "massa",
          label: "Massa",
          short: "Tradicional, fina",
          description: "Para escolher uma base ou preparo principal.",
          name: "Massa",
          type: "escolha_unica",
          min: 1,
          max: 1,
          required: true,
          quantity: false,
          items: [
            {
              name: "Massa tradicional",
              description: "Padrão da casa",
              price: 0,
            },
            {
              name: "Massa fina",
              description: "Mais leve e crocante",
              price: 0,
            },
          ],
        },
        {
          key: "adicional",
          label: "Adicional",
          short: "Bacon, cheddar",
          description: "Para itens pagos que o cliente pode incluir.",
          name: "Adicionais",
          type: "adicional_pago",
          min: 0,
          max: null,
          required: false,
          quantity: true,
          items: [
            { name: "Cheddar", description: "Porção extra", price: 500 },
            { name: "Bacon", description: "Porção extra", price: 700 },
          ],
        },
        {
          key: "acompanhamento",
          label: "Acompanhamento",
          short: "Batata, salada",
          description: "Para escolher complemento do item.",
          name: "Acompanhamento",
          type: "multipla_escolha",
          min: 0,
          max: 2,
          required: false,
          quantity: false,
          items: [
            { name: "Batata frita", description: "Porção pequena", price: 900 },
            {
              name: "Salada simples",
              description: "Acompanha o pedido",
              price: 0,
            },
          ],
        },
        {
          key: "molho",
          label: "Molho",
          short: "Barbecue, maionese",
          description: "Para molhos extras ou escolhas de molho.",
          name: "Molhos",
          type: "multipla_escolha",
          min: 0,
          max: 3,
          required: false,
          quantity: true,
          items: [
            {
              name: "Molho barbecue",
              description: "Sachê ou pote",
              price: 250,
            },
            {
              name: "Maionese da casa",
              description: "Pote pequeno",
              price: 300,
            },
          ],
        },
        {
          key: "remocao",
          label: "Remover ingrediente",
          short: "Sem cebola, sem molho",
          description: "Para o cliente retirar algo sem alterar preço.",
          name: "Remover ingredientes",
          type: "remocao_sem_preco",
          min: 0,
          max: null,
          required: false,
          quantity: false,
          items: [
            { name: "Sem cebola", description: "Retirar cebola", price: 0 },
            { name: "Sem molho", description: "Retirar molho", price: 0 },
          ],
        },
        {
          key: "observacao",
          label: "Observação",
          short: "Campo de texto",
          description: "Para o cliente escrever um pedido específico.",
          name: "Observação do item",
          type: "observacao_textual",
          min: 0,
          max: 1,
          required: false,
          quantity: false,
          items: [],
        },
      ],
      config: {
        slug: "",
        publicUrl: "",
        name: "",
        description: "",
        habilitado: false,
        openNow: true,
        deliveryEnabled: false,
        pickupEnabled: false,
        scheduleLabel: "",
        weeklyHours: [],
        closedMessage: "",
        minimumOrderCents: 0,
        deliveryFeeCents: 0,
        minMinutes: 0,
        maxMinutes: 0,
        baseAddress: "",
        pickupMinutes: 0,
        pickupMessage: "",
      },
      publishingForm: {},
      persistedPublishing: {},
      slugAvailability: {
        status: "idle",
        checkedSlug: "",
        message: "",
      },
      slugAvailabilityTimer: null,
      slugAvailabilityRequest: 0,
      channelForm: {
        cardapioDigital: false,
        selfPedido: false,
      },
      persistedChannels: {
        cardapioDigital: false,
        selfPedido: false,
      },
      readiness: {
        cardapio_digital: { pronto: false, bloqueios: [], avisos: [] },
        self_pedido: { pronto: false, bloqueios: [], avisos: [] },
      },
      deliveryForm: {
        feeText: "R$ 0,00",
        minimumText: "R$ 0,00",
        minMinutes: 0,
        maxMinutes: 0,
        deliveryEnabled: false,
        baseAddress: "",
        pickupEnabled: false,
        pickupMinutes: 0,
        pickupMessage: "",
      },
      persistedDeliveryForm: null,
      deliveryCouriers: [],
      courierUsers: [],
      couriersLoading: false,
      courierUsersLoading: false,
      courierForm: {
        usuarioId: "",
        displayName: "",
        phone: "",
      },
      zoneForm: {
        open: false,
        id: null,
        nome: "",
        bairro: "",
        cepInicio: "",
        cepFim: "",
        feeText: "R$ 0,00",
        minimumText: "R$ 0,00",
        freeAboveText: "R$ 0,00",
        minMinutes: null,
        maxMinutes: null,
        bloqueada: false,
      },
      checkoutForm: {
        cash: false,
        card: false,
        message: "",
      },
      persistedCheckout: {
        cash: false,
        card: false,
        message: "",
      },
      promotionForm: {
        id: null,
        preset: "banner",
        name: "Promoção do cardápio",
        subtitle: "",
        description: "",
        targetType: "produto",
        productId: null,
        variantKey: "",
        categoryId: null,
        discountType: "valor",
        discountText: "R$ 0,00",
        discountPercent: 0,
        previousPriceText: "R$ 0,00",
        promotionalPriceText: "R$ 0,00",
        bannerText: "",
        giftText: "",
        imageUrl: "",
        imageFile: null,
        imagePreviewUrl: null,
        start: "",
        end: "",
        weekdays: [],
        enabled: true,
      },
      promotionPresetOptions: [
        {
          value: "banner",
          label: "Banner promocional",
          description: "Campanha visual sem desconto obrigatório.",
        },
        {
          value: "preco_especial",
          label: "Preço especial",
          description: "Produto com preço anterior e preço promocional.",
        },
        {
          value: "compre_ganhe",
          label: "Compre e ganhe",
          description: "Brinde ou benefício comunicado na vitrine.",
        },
        {
          value: "dia_semana",
          label: "Dia da semana",
          description: "Promoção válida somente nos dias marcados.",
        },
      ],
      couponForm: {
        id: null,
        code: "",
        name: "",
        discountType: "valor",
        discountText: "R$ 0,00",
        discountPercent: 0,
        minimumText: "R$ 0,00",
        totalLimit: null,
        start: "",
        end: "",
        enabled: true,
      },
      comboForm: {
        id: null,
        productId: null,
        name: "",
        description: "",
        priceText: "R$ 0,00",
        itemsText: "",
        enabled: true,
      },
      visualForm: {
        logoUrl: null,
        capaUrl: "",
        logoFile: null,
        capaFile: null,
        logoPreviewUrl: null,
        capaPreviewUrl: null,
        removeLogo: false,
        removeCapa: false,
        primaryColor: "#ee7361",
        accentColor: "#ffd993",
        backgroundColor: "#fff8f6",
        cardStyle: "soft",
        buttonStyle: "rounded",
        emptyText: "",
        errorText: "",
        trackingText: "",
      },
      persistedVisual: null,
      advancedTheme: false,
      previewScenario: "home",
      themePresets: CARDAPIO_THEME_PRESETS,
      previewScenarios: CARDAPIO_PREVIEW_SCENARIOS,
      visualPreviewSrc: "",
      visualRevision: 0,
      visualPreviewTimer: null,
      cardStyleOptions: [
        {
          value: "soft",
          label: "Cards suaves",
          description: "Bordas leves e sombra discreta.",
        },
        {
          value: "flat",
          label: "Cards limpos",
          description: "Menos sombra e mais foco no conteúdo.",
        },
      ],
      buttonStyleOptions: [
        {
          value: "rounded",
          label: "Botões arredondados",
          description: "Visual acolhedor e comercial.",
        },
        {
          value: "pill",
          label: "Botões cápsula",
          description: "Mais destaque em ações rápidas.",
        },
      ],
      sections: CARDAPIO_WORKSPACES,
      categories: [],
      products: [],
      optionGroups: [],
      pizzas: [],
      areasEntrega: [],
      promocoes: [],
      cupons: [],
      combos: [],
      textosPublicos: [],
    };
  },
  computed: {
    activeWorkspace() {
      const workspace = this.sections.find(
        (section) =>
          section.defaultPanel === this.activeSection ||
          (section.tabs || []).some((tab) => tab.id === this.activeSection)
      );

      return workspace ? workspace.id : "overview";
    },
    currentSection() {
      return (
        this.sections.find((section) => section.id === this.activeWorkspace) ||
        this.sections[0]
      );
    },
    logoUrl() {
      return this.visualForm.logoUrl || null;
    },
    visualLogoPreview() {
      return this.visualForm.logoPreviewUrl || this.visualForm.logoUrl;
    },
    visualCoverPreview() {
      return this.visualForm.capaPreviewUrl || this.visualForm.capaUrl;
    },
    visualPreviewStyle() {
      return {
        "--preview-primary": this.normalizedHexColor(
          this.visualForm.primaryColor,
          "#ee7361"
        ),
        "--preview-accent": this.normalizedHexColor(
          this.visualForm.accentColor,
          "#ffd993"
        ),
        "--preview-background": this.normalizedHexColor(
          this.visualForm.backgroundColor,
          "#fff8f6"
        ),
        "--preview-card-radius":
          this.visualForm.cardStyle === "flat" ? "6px" : "12px",
        "--preview-button-radius":
          this.visualForm.buttonStyle === "pill" ? "999px" : "10px",
      };
    },
    visualCoverPreviewStyle() {
      if (this.visualCoverPreview) {
        return {
          backgroundImage: `linear-gradient(90deg, rgba(255,255,255,.92), rgba(255,255,255,.72)), url(${this.visualCoverPreview})`,
        };
      }

      return {};
    },
    visualContrastWarning() {
      const bestContrast = Math.max(
        this.colorContrast(this.visualForm.primaryColor, "#ffffff"),
        this.colorContrast(this.visualForm.primaryColor, "#000000")
      );

      if (bestContrast > 0 && bestContrast < 4.5) {
        return "A cor principal não alcança contraste AA nem com texto claro nem escuro. Escolha outro tom.";
      }

      return "";
    },
    promotionImagePreview() {
      return this.promotionForm.imagePreviewUrl || this.promotionForm.imageUrl;
    },
    promotionVariantOptions() {
      return this.promotionVariantOptionsFor(this.promotionForm.productId);
    },
    selectedPromotionVariant() {
      return (
        this.promotionVariantOptions.find(
          (variant) => variant.key === this.promotionForm.variantKey
        ) || null
      );
    },
    availableProducts() {
      return this.publishedProducts.filter((product) => product.available)
        .length;
    },
    publishedProducts() {
      return this.products.filter((product) => product.published && product.id);
    },
    candidateProducts() {
      return this.products.filter((product) => !product.configured);
    },
    configuredProducts() {
      return this.products.filter(
        (product) => product.configured && product.id
      );
    },
    linkableProducts() {
      const byId = new Map();

      [...this.products, ...this.operationalProducts].forEach((product) => {
        const productId = Number(product.productId || product.sourceId || 0);
        if (productId > 0) byId.set(productId, product);
      });

      return Array.from(byId.values()).sort((left, right) =>
        String(left.name || "").localeCompare(String(right.name || ""), "pt-BR")
      );
    },
    unavailableProducts() {
      return this.publishedProducts.filter((product) => !product.available);
    },
    cardapioReadiness() {
      return this.normalizeReadiness(this.readiness.cardapio_digital);
    },
    selfPedidoReadiness() {
      return this.normalizeReadiness(this.readiness.self_pedido);
    },
    hubStatus() {
      if (!this.channelForm.cardapioDigital && !this.channelForm.selfPedido) {
        return {
          title: "Canais configuráveis, mas ainda inativos.",
          description:
            "Ative Cardápio Digital ou Self Pedido em Atendimento quando estiver pronto.",
          alertClass: "presentation-alert--neutral",
          icon: "flaticon2-information",
        };
      }

      const blockers = [
        ...(this.channelForm.cardapioDigital
          ? this.cardapioReadiness.bloqueios
          : []),
        ...(this.channelForm.selfPedido
          ? this.selfPedidoReadiness.bloqueios
          : []),
      ];

      if (blockers.length) {
        return {
          title: "Existem pendências antes da operação.",
          description: `${blockers.length} item(ns) precisam de atenção. Consulte a Visão geral e o Atendimento.`,
          alertClass: "presentation-alert--warning",
          icon: "flaticon-warning-sign",
        };
      }

      return {
        title: "Configuração pronta para a operação.",
        description:
          "Os canais ativos atendem aos requisitos essenciais. Avisos não bloqueiam a publicação.",
        alertClass: "presentation-alert--success",
        icon: "flaticon2-check-mark",
      };
    },
    metrics() {
      return [
        {
          label: "Produtos no site",
          value: this.publishedProducts.length,
          hint: `${this.availableProducts} disponíveis para o cliente`,
        },
        {
          label: "Categorias",
          value: this.categories.length,
          hint: "Organização do cardápio",
        },
        {
          label: "Pedido mínimo",
          value: this.formatMoney(this.config.minimumOrderCents),
          hint: "Valor conferido ao finalizar",
        },
        {
          label: "Entrega",
          value: this.config.deliveryEnabled
            ? `${this.config.minMinutes} a ${this.config.maxMinutes} min`
            : "Inativa",
          hint: this.config.deliveryEnabled
            ? `${this.formatMoney(this.config.deliveryFeeCents)} de taxa`
            : "Ative em Atendimento",
        },
      ];
    },
    pendingAlerts() {
      const alerts = [];

      if (this.channelForm.cardapioDigital) {
        alerts.push(
          ...this.cardapioReadiness.bloqueios,
          ...this.cardapioReadiness.avisos
        );
      } else {
        alerts.push("O canal Cardápio Digital está inativo.");
        alerts.push(...this.publishBlockingIssues);
      }

      if (this.channelForm.selfPedido) {
        alerts.push(
          ...this.selfPedidoReadiness.bloqueios,
          ...this.selfPedidoReadiness.avisos
        );
      } else {
        alerts.push("O canal Self Pedido está inativo.");
      }

      if (this.publishedProducts.some((product) => !product.imageUrl)) {
        alerts.push("Alguns produtos ainda estao sem foto.");
      }

      if (!alerts.length) {
        alerts.push("Nenhuma pendência encontrada.");
      }

      return [...new Set(alerts)];
    },
    hasFulfillmentMode() {
      return Boolean(this.config.deliveryEnabled || this.config.pickupEnabled);
    },
    publishBlockingIssues() {
      const normalize = (value) =>
        String(value || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
      const isCircularBlocker = (value) => {
        const text = normalize(value);

        return (
          text.includes("publique o cardapio") ||
          text.includes("cardapio nao publicado") ||
          text.includes("cardapio nao esta publicado") ||
          text.includes("ative o canal") ||
          (text.includes("canal") && text.includes("inativo"))
        );
      };
      const blockers = this.cardapioReadiness.bloqueios.filter(
        (item) => !isCircularBlocker(item)
      );
      const hasBlockerContaining = (...terms) =>
        blockers.some((item) => {
          const text = normalize(item);
          return terms.every((term) => text.includes(term));
        });

      if (!this.publishedProducts.length && !hasBlockerContaining("produto")) {
        blockers.push("Publique pelo menos um produto no catálogo.");
      } else if (!this.availableProducts && !hasBlockerContaining("produto")) {
        blockers.push("Deixe pelo menos um produto publicado disponível.");
      }

      if (
        !this.hasFulfillmentMode &&
        !hasBlockerContaining("entrega", "retirada")
      ) {
        blockers.push("Ative entrega ou retirada.");
      }

      return [...new Set(blockers)];
    },
    canPublishCardapio() {
      return this.publishBlockingIssues.length === 0;
    },
    publicationStatusLabel() {
      if (!this.config.habilitado) {
        return "Pausado";
      }

      if (!this.publishedProducts.length) {
        return "Sem produtos";
      }

      if (!this.availableProducts) {
        return "Sem itens disponíveis";
      }

      if (!this.hasFulfillmentMode) {
        return "Sem entrega ou retirada";
      }

      return "Publicado";
    },
    publicationStatusClass() {
      if (!this.config.habilitado) {
        return "label-light-danger";
      }

      if (!this.canPublishCardapio) {
        return "label-light-warning";
      }

      return "label-light-success";
    },
    publicationStatusDescription() {
      if (!this.config.habilitado) {
        return this.config.closedMessage || "O cliente vê o cardápio pausado.";
      }

      if (!this.publishedProducts.length) {
        return "Cadastre ou publique produtos antes de abrir o cardápio para pedidos.";
      }

      if (!this.availableProducts) {
        return "Ative pelo menos um produto para o cliente conseguir comprar.";
      }

      if (!this.hasFulfillmentMode) {
        return "Ative entrega ou retirada para liberar o checkout.";
      }

      if (!this.config.openNow) {
        return `Publicado para consulta. Os pedidos estão fechados agora pelo horário configurado: ${
          this.config.scheduleLabel || "horário não informado"
        }.`;
      }

      return "O cardápio está publicado e pronto para receber pedidos.";
    },
    publicationChecks() {
      return [
        this.publishedProducts.length
          ? `${this.publishedProducts.length} produtos publicados`
          : "Nenhum produto publicado",
        this.availableProducts
          ? `${this.availableProducts} produtos disponíveis`
          : "Nenhum produto disponível",
        this.hasFulfillmentMode
          ? "Entrega ou retirada ativa"
          : "Entrega e retirada desativadas",
        this.config.scheduleLabel || "Horário não informado",
      ];
    },
    visiblePauseOptions() {
      if (this.pauseForm.type === "cardapio") {
        return this.pauseOptions.filter((option) => option.value === "manual");
      }

      return this.pauseOptions;
    },
    globalOptionGroups() {
      return this.optionGroups.filter(
        (group) => !group.cardapio_produto_id && group.papel !== "variante"
      );
    },
    globalVariantGroups() {
      return this.optionGroups.filter(
        (group) => !group.cardapio_produto_id && group.papel === "variante"
      );
    },
    optionGroupModalTitle() {
      if (this.optionGroupForm?.papel === "variante") {
        return this.optionGroupForm.mode === "produto"
          ? "Variação deste produto"
          : "Nova variante reutilizável";
      }
      if (this.optionGroupForm?.mode === "produto") {
        return "Opções deste produto";
      }

      return "Grupo global do cardápio";
    },
    optionGroupModalSubtitle() {
      if (this.optionGroupForm?.papel === "variante") {
        return "Configuração comercial reutilizável";
      }
      if (this.optionGroupForm?.mode === "produto") {
        return "Grupo exclusivo do produto";
      }

      return "Grupo global";
    },
    optionGroupModalDescription() {
      if (this.optionGroupForm?.mode === "produto") {
        return "Use para escolhas que pertencem somente a este item, como borda, ponto, molho ou remoção de ingrediente.";
      }

      return "Use quando a mesma escolha deve aparecer em todo o cardápio ou em categorias inteiras, como talheres, embalagem, molhos gerais ou observações.";
    },
    optionGroupTypeHelp() {
      if (!this.optionGroupForm) {
        return "";
      }

      const helps = {
        escolha_unica: "Cliente escolhe uma opção, como massa ou borda.",
        multipla_escolha: "Cliente pode marcar mais de uma opção.",
        adicional_pago: "Itens pagos somam ao valor final.",
        remocao_sem_preco: "Opções para retirar ingredientes sem cobrar.",
        observacao_textual: "Mostra um campo de texto para instruções do item.",
        acompanhamento_obrigatorio:
          "Cliente precisa escolher acompanhamento antes de comprar.",
      };

      return helps[this.optionGroupForm.tipo] || "";
    },
    optionGroupItemHint() {
      if (!this.optionGroupForm) {
        return "Cadastre as opções que o cliente poderá escolher.";
      }

      if (this.optionGroupForm.tipo === "observacao_textual") {
        return "Este tipo mostra um campo de texto no produto. Itens abaixo são opcionais.";
      }

      return "Cadastre as opções que o cliente poderá escolher no produto.";
    },
    nextChangeLabel() {
      if (!this.channelForm.cardapioDigital) {
        return "O canal está inativo; a configuração permanece salva para quando ele for ativado.";
      }

      if (!this.config.habilitado) {
        return this.config.closedMessage;
      }

      return this.config.openNow
        ? "Aceitando pedidos agora; fechamento conforme o horário informado."
        : "Publicado para consulta; pedidos abrem conforme o horário informado.";
    },
    deliveryPreviewLabel() {
      const feeCents = this.parseMoneyToCents(this.deliveryForm.feeText);
      const minimumCents = this.parseMoneyToCents(
        this.deliveryForm.minimumText
      );

      return `Taxa: ${this.formatMoney(
        feeCents
      )} | Pedido mínimo: ${this.formatMoney(minimumCents)}`;
    },
    deliveryZones() {
      return (this.areasEntrega || []).filter(
        (area) => area.raio_km_max === null || area.raio_km_max === undefined
      );
    },
    currentOffer() {
      const offers = {
        promotions: {
          title: "Campanhas do cardápio",
          description:
            "Banners, preços especiais e comunicações com período definido.",
          actionLabel: "Nova campanha",
        },
        coupons: {
          title: "Cupons de desconto",
          description:
            "Códigos validados no checkout, com validade e pedido mínimo.",
          actionLabel: "Novo cupom",
        },
        combos: {
          title: "Combos",
          description:
            "Composições de produtos com preço fechado, sem criar uma campanha genérica.",
          actionLabel: "Novo combo",
        },
      };

      return offers[this.activeSection] || offers.promotions;
    },
    currentServiceSummary() {
      const activePayments = [
        this.checkoutForm.cash && "Dinheiro",
        this.checkoutForm.card && "Cartão",
      ].filter(Boolean);
      const summaries = {
        delivery: {
          eyebrow: "Forma de atendimento",
          title: "Entrega",
          description: "Taxa geral com zonas opcionais por bairro ou CEP.",
          enabled: this.config.deliveryEnabled,
          actionLabel: "Configurar entrega",
          help: "Taxa, pedido mínimo, prazo e regiões ficam no mesmo editor.",
          details: [
            {
              label: "Taxa padrão",
              value: this.formatMoney(this.config.deliveryFeeCents),
            },
            {
              label: "Pedido mínimo",
              value: this.formatMoney(this.config.minimumOrderCents),
            },
            {
              label: "Prazo",
              value: `${this.config.minMinutes || 0} a ${
                this.config.maxMinutes || 0
              } min`,
            },
            { label: "Zonas", value: String(this.deliveryZones.length) },
          ],
        },
        pickup: {
          eyebrow: "Forma de atendimento",
          title: "Retirada",
          description:
            "Pedido sem endereço, retirado no balcão do restaurante.",
          enabled: this.config.pickupEnabled,
          actionLabel: "Configurar retirada",
          help: "Defina o prazo e a orientação mostrada ao cliente.",
          details: [
            {
              label: "Tempo de preparo",
              value: `${this.deliveryForm.pickupMinutes || 0} min`,
            },
            {
              label: "Endereço",
              value: this.config.baseAddress || "Usa o cadastro do restaurante",
            },
          ],
        },
        checkout: {
          eyebrow: "Fechamento do pedido",
          title: "Pagamentos presenciais",
          description: "Formas recebidas no balcão ou pelo entregador.",
          enabled: activePayments.length > 0,
          actionLabel: "Configurar pagamentos",
          help: "A mensagem de checkout tem uma única fonte nesta seção.",
          details: [
            {
              label: "Formas ativas",
              value: activePayments.join(", ") || "Nenhuma",
            },
            {
              label: "Mensagem",
              value: this.checkoutForm.message || "Não informada",
            },
          ],
        },
      };

      return summaries[this.activeSection] || summaries.delivery;
    },
    courierUserOptions() {
      const linkedUserIds = new Set(
        this.deliveryCouriers.map((courier) =>
          String(courier.usuario_id || courier.usuario?.id_usuario || "")
        )
      );

      return this.courierUsers
        .filter((user) => {
          const userId = user.usuario_id || user.id_usuario || user.id;
          return userId && !linkedUserIds.has(String(userId));
        })
        .map((user) => ({
          value: user.usuario_id || user.id_usuario || user.id,
          text:
            user.nome ||
            user.name ||
            user.usuario ||
            user.email ||
            `Usuário ${user.usuario_id || user.id_usuario || user.id}`,
        }));
    },
    selectedTheme() {
      const normalize = (value) => String(value || "").toUpperCase();
      const preset = this.themePresets.find(
        (item) =>
          normalize(item.primary) === normalize(this.visualForm.primaryColor) &&
          normalize(item.accent) === normalize(this.visualForm.accentColor) &&
          normalize(item.background) ===
            normalize(this.visualForm.backgroundColor)
      );

      return preset ? preset.id : "custom";
    },
    selectedThemeLabel() {
      const preset = this.themePresets.find(
        (item) => item.id === this.selectedTheme
      );

      return preset ? preset.label : "Tema personalizado";
    },
    visualColorsValid() {
      return [
        this.visualForm.primaryColor,
        this.visualForm.accentColor,
        this.visualForm.backgroundColor,
      ].every(this.isValidHexColor);
    },
    visualDirty() {
      if (!this.persistedVisual) return false;
      return (
        JSON.stringify(this.serializeVisualDraft(this.visualForm)) !==
        JSON.stringify(this.persistedVisual)
      );
    },
    publishingDirty() {
      return (
        JSON.stringify(this.serializePublishingDraft(this.publishingForm)) !==
        JSON.stringify(this.persistedPublishing)
      );
    },
    publishingScheduleSummary() {
      return summarizeWeeklyHours(this.publishingForm.weeklyHours || []);
    },
    slugAvailabilityMessage() {
      const messages = {
        idle: "Digite o endereço que deseja usar.",
        checking: "Verificando disponibilidade...",
        available: "Endereço disponível.",
        unavailable:
          this.slugAvailability.message ||
          "Este endereço já foi utilizado. Escolha outro.",
        error:
          this.slugAvailability.message ||
          "Não foi possível verificar agora. Tente novamente.",
      };

      return messages[this.slugAvailability.status] || messages.idle;
    },
    publishingPublicUrlPreview() {
      const slug = this.slugify(this.publishingForm.slug);
      if (!slug) return "";
      if (slug === this.config.slug && this.config.publicUrl) {
        return this.config.publicUrl;
      }

      return `Novo endereço: /${slug} (confirmado ao salvar)`;
    },
    channelsDirty() {
      return (
        JSON.stringify(this.channelForm) !==
        JSON.stringify(this.persistedChannels)
      );
    },
    deliveryDirty() {
      return Boolean(
        this.persistedDeliveryForm &&
          JSON.stringify(this.deliveryForm) !==
            JSON.stringify(this.persistedDeliveryForm)
      );
    },
    checkoutDirty() {
      return (
        JSON.stringify(this.checkoutForm) !==
        JSON.stringify(this.persistedCheckout)
      );
    },
    entityDraftDirty() {
      return Object.keys(this.entityDraftBaselines).some(
        (type) =>
          this.draftSignature(this.entityDraftValue(type)) !==
          this.entityDraftBaselines[type]
      );
    },
    hasUnsavedChanges() {
      return (
        this.visualDirty ||
        this.publishingDirty ||
        this.channelsDirty ||
        this.deliveryDirty ||
        this.checkoutDirty ||
        this.entityDraftDirty
      );
    },
  },
  watch: {
    "publishingForm.slug": function (value) {
      this.scheduleSlugAvailability(value);
    },
    visualForm: {
      deep: true,
      handler() {
        this.scheduleVisualPreviewPush();
      },
    },
    previewScenario() {
      this.scheduleVisualPreviewPush();
    },
    "$route.query": {
      deep: true,
      handler() {
        this.restoreNavigationFromQuery();
      },
    },
  },
  async mounted() {
    this.restoreNavigationFromQuery();
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    await this.loadManagement();
    await this.loadDeliveryCouriers(true);
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedChanges) {
      next();
      return;
    }

    this.$bvModal
      .msgBoxConfirm(
        "Existem alterações não salvas. Deseja sair e descartá-las?",
        {
          title: "Descartar alterações?",
          okTitle: "Sair sem salvar",
          cancelTitle: "Continuar editando",
          okVariant: "danger",
          centered: true,
        }
      )
      .then((confirmed) => next(Boolean(confirmed)))
      .catch(() => next(false));
  },
  beforeRouteUpdate(to, from, next) {
    if (!this.hasUnsavedChanges) {
      next();
      return;
    }
    this.$bvModal
      .msgBoxConfirm(
        "Existem alterações não salvas. Deseja trocar de seção e descartá-las?",
        {
          title: "Descartar alterações?",
          okTitle: "Descartar",
          cancelTitle: "Continuar editando",
          okVariant: "danger",
          centered: true,
        }
      )
      .then((confirmed) => {
        if (confirmed) this.discardAllDrafts();
        next(Boolean(confirmed));
      })
      .catch(() => next(false));
  },
  beforeDestroy() {
    if (this.slugAvailabilityTimer) {
      clearTimeout(this.slugAvailabilityTimer);
    }
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    if (this.visualPreviewTimer) {
      clearTimeout(this.visualPreviewTimer);
    }

    // Revoga object URLs pendentes para não vazar memória.
    [
      this.productForm && this.productForm.imagePreviewUrl,
      this.visualForm && this.visualForm.logoPreviewUrl,
      this.visualForm && this.visualForm.capaPreviewUrl,
      this.promotionForm && this.promotionForm.imagePreviewUrl,
    ].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });
  },
  methods: {
    normalizeReadiness(value) {
      return normalizeReadinessValue(value);
    },
    defaultWeeklyHours() {
      return createDefaultWeeklyHours();
    },
    normalizeWeeklyHours(value) {
      return normalizeWeeklyHoursValue(value);
    },
    applySchedulePreset(preset) {
      this.publishingForm.weeklyHours = applyWeeklyHoursPreset(
        this.publishingForm.weeklyHours || [],
        preset
      );
      this.$delete(this.fieldErrors, "horarios_semanais");
    },
    weekdayLongLabel(day) {
      return this.weekdayLongLabels[Number(day)] || "Dia";
    },
    addScheduleInterval(day) {
      if (!day || day.intervalos.length >= 6) return;
      const last = day.intervalos[day.intervalos.length - 1];
      day.intervalos.push({
        inicio: last && last.fim ? last.fim : "18:00",
        fim: "23:00",
      });
    },
    removeScheduleInterval(day, index) {
      if (!day || day.intervalos.length <= 1) return;
      day.intervalos.splice(index, 1);
    },
    clonePlain(value) {
      return JSON.parse(JSON.stringify(value || {}));
    },
    entityDraftValue(type) {
      const drafts = {
        product: this.productForm,
        publisher: this.publicationForm,
        category: this.categoryForm,
        optionGroup: this.optionGroupForm,
        pizza: this.pizzaForm,
        promotion: this.promotionForm,
        coupon: this.couponForm,
        combo: this.comboForm,
        zone: this.zoneForm?.open ? this.zoneForm : null,
      };
      return drafts[type] ?? null;
    },
    draftSignature(value) {
      return JSON.stringify(value ?? null, (key, item) => {
        if (key === "imagePreviewUrl" || key === "localKey") return undefined;
        if (typeof File !== "undefined" && item instanceof File) {
          return {
            name: item.name,
            size: item.size,
            type: item.type,
            lastModified: item.lastModified,
          };
        }
        return item;
      });
    },
    captureEntityDraft(type) {
      this.$set(
        this.entityDraftBaselines,
        type,
        this.draftSignature(this.entityDraftValue(type))
      );
    },
    closeEntityDraft(type) {
      this.$delete(this.entityDraftBaselines, type);
    },
    closeProductDraft() {
      if (this.productForm?.imagePreviewUrl) {
        URL.revokeObjectURL(this.productForm.imagePreviewUrl);
      }
      this.productForm = null;
      this.closeEntityDraft("product");
    },
    closePromotionDraft() {
      if (this.promotionForm?.imagePreviewUrl) {
        URL.revokeObjectURL(this.promotionForm.imagePreviewUrl);
      }
      this.closeEntityDraft("promotion");
    },
    serializePublishingDraft(form) {
      const value = form || {};
      return {
        name: value.name || "",
        slug: value.slug || "",
        description: value.description || "",
        closedMessage: value.closedMessage || "",
        weeklyHours: this.normalizeWeeklyHours(value.weeklyHours || []),
      };
    },
    serializeVisualDraft(form) {
      return serializeVisualDraftValue(form);
    },
    handleBeforeUnload(event) {
      if (!this.hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    },
    restoreNavigationFromQuery() {
      const workspaceId = this.$route.query.secao;
      const panelId = this.$route.query.aba;
      const workspace = this.sections.find((item) => item.id === workspaceId);
      const validPanel =
        workspace && (workspace.tabs || []).some((tab) => tab.id === panelId);

      if (validPanel) {
        this.activeSection = panelId;
      } else if (workspace) {
        this.activeSection = workspace.defaultPanel;
      }
    },
    syncNavigationQuery() {
      const query = Object.assign({}, this.$route.query, {
        secao: this.activeWorkspace,
      });
      if (this.currentSection.tabs && this.currentSection.tabs.length) {
        query.aba = this.activeSection;
      } else {
        delete query.aba;
      }

      if (
        query.secao === this.$route.query.secao &&
        query.aba === this.$route.query.aba
      ) {
        return;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    async loadManagement(silent = false) {
      const requestSequence = ++this.managementRequestSequence;
      if (!silent) {
        this.managementLoading = true;
      }
      this.managementError = "";

      try {
        const { data } = await this.$http.get(
          "/restaurante/cardapio-online/gestao"
        );
        if (requestSequence !== this.managementRequestSequence) {
          return false;
        }
        this.applyManagementData(this.unwrapResponse(data) || {});
        this.managementLoaded = true;
        return true;
      } catch (error) {
        if (requestSequence !== this.managementRequestSequence) {
          return false;
        }
        if (silent) {
          this.$toast.warning(
            "A alteração foi enviada, mas não foi possível confirmar o estado persistido. Recarregue a página."
          );
        } else {
          this.managementError =
            "Verifique sua conexão e sua permissão para gerenciar o restaurante.";
        }
        return false;
      } finally {
        if (requestSequence === this.managementRequestSequence) {
          this.managementLoading = false;
        }
      }
    },
    applyManagementData(data) {
      const config = data.configuracao || {};
      const pickup = config.retirada || data.retirada || {};
      const has = (field) =>
        Object.prototype.hasOwnProperty.call(config, field);

      this.config = {
        slug: has("slug") ? config.slug || "" : this.config.slug,
        publicUrl: has("public_url")
          ? config.public_url || ""
          : this.config.publicUrl,
        name: has("nome_publico")
          ? config.nome_publico || ""
          : this.config.name,
        description: has("descricao")
          ? config.descricao || ""
          : this.config.description,
        habilitado: normalizeBoolean(config.habilitado),
        deliveryEnabled: normalizeBoolean(config.aceita_entrega),
        pickupEnabled: normalizeBoolean(config.aceita_retirada),
        scheduleLabel: has("horario_resumo")
          ? config.horario_resumo || ""
          : this.config.scheduleLabel,
        openNow: has("aberto_agora")
          ? normalizeBoolean(config.aberto_agora)
          : this.config.openNow,
        weeklyHours: this.normalizeWeeklyHours(
          config.horarios_semanais || config.horarios || this.config.weeklyHours
        ),
        closedMessage: has("mensagem_fechado")
          ? config.mensagem_fechado || ""
          : this.config.closedMessage,
        minimumOrderCents: has("pedido_minimo_centavos")
          ? Number(config.pedido_minimo_centavos || 0)
          : this.config.minimumOrderCents,
        deliveryFeeCents: has("taxa_entrega_padrao_centavos")
          ? Number(config.taxa_entrega_padrao_centavos || 0)
          : this.config.deliveryFeeCents,
        minMinutes: has("tempo_entrega_minutos_min")
          ? Number(config.tempo_entrega_minutos_min || 0)
          : this.config.minMinutes,
        maxMinutes: has("tempo_entrega_minutos_max")
          ? Number(config.tempo_entrega_minutos_max || 0)
          : this.config.maxMinutes,
        baseAddress: has("endereco_base")
          ? config.endereco_base || ""
          : this.config.baseAddress,
        pickupMinutes: Number(pickup.tempo_preparo_minutos || 0),
        pickupMessage: pickup.mensagem || "",
      };

      this.categories = (data.categorias || []).map(this.normalizeCategory);
      const rawProducts = mergeProductSources(
        data.produtos_candidatos,
        data.produtos,
        data.produtos_publicados
      );
      this.products = rawProducts.map(this.normalizeProduct);
      this.optionGroups = (data.opcoes || []).map(this.normalizeOptionGroup);
      this.pizzas = (data.pizzas || []).map(this.normalizePizza);
      this.areasEntrega = (data.areas_entrega || []).map((area) => ({
        ...area,
        ativo: normalizeBoolean(area.ativo, true),
        bloqueada: normalizeBoolean(area.bloqueada),
      }));
      this.promocoes = (data.promocoes || []).map((promotion) => ({
        ...promotion,
        ativo: normalizeBoolean(promotion.ativo, true),
      }));
      this.cupons = (data.cupons || []).map((coupon) => ({
        ...coupon,
        ativo: normalizeBoolean(coupon.ativo, true),
      }));
      this.combos = (data.combos || []).map((combo) => ({
        ...combo,
        ativo: normalizeBoolean(combo.ativo, true),
      }));
      this.textosPublicos = data.textos || [];
      const canais = data.canais || {};
      this.channelForm = {
        cardapioDigital: normalizeBoolean(canais.cardapio_digital_ativo),
        selfPedido: normalizeBoolean(canais.self_pedido_ativo),
      };
      this.persistedChannels = Object.assign({}, this.channelForm);
      this.readiness = Object.assign(
        {
          cardapio_digital: { pronto: false, bloqueios: [], avisos: [] },
          self_pedido: { pronto: false, bloqueios: [], avisos: [] },
        },
        data.readiness || {}
      );
      this.applyOperationData(data.operacao || {});
      this.applyVisualData(
        Object.assign(
          {},
          {
            logo_url: Object.prototype.hasOwnProperty.call(config, "logo_url")
              ? config.logo_url
              : undefined,
            capa_url: Object.prototype.hasOwnProperty.call(config, "capa_url")
              ? config.capa_url
              : undefined,
          },
          data.visual || {}
        ),
        data.textos || []
      );
      this.syncFormsFromConfig();
      this.persistedVisual = this.serializeVisualDraft(this.visualForm);

      if (!this.promotionForm.productId && this.publishedProducts.length) {
        this.promotionForm.productId = this.publishedProducts[0].id;
      }

      if (!this.comboForm.productId && this.publishedProducts.length) {
        this.comboForm.productId = this.publishedProducts[0].id;
      }
    },
    unwrapResponse(response) {
      if (
        response &&
        response.success === true &&
        response.data !== undefined
      ) {
        return response.data;
      }

      return response;
    },
    isSaving(key) {
      return Boolean(this.saving[key]);
    },
    setSaving(key, value) {
      this.$set(this.saving, key, value);
    },
    clearFieldErrors() {
      this.fieldErrors = {};
    },
    setFieldErrors(errors) {
      this.fieldErrors = Object.keys(errors || {}).reduce((carry, field) => {
        const value = errors[field];
        carry[field] = Array.isArray(value) ? value[0] : value;
        return carry;
      }, {});
    },
    fieldError(field) {
      return this.fieldErrors[field] || "";
    },
    showApiError(error, fallbackMessage) {
      const data = error.response?.data || {};
      const errors = data.errors || {};
      const firstField = Object.keys(errors)[0];
      const firstError = firstField ? errors[firstField] : null;
      const firstMessage = Array.isArray(firstError)
        ? firstError[0]
        : firstError;

      this.setFieldErrors(errors);
      this.$toast.error(data.message || firstMessage || fallbackMessage);
    },
    normalizeCategory(category) {
      const rawAvailable =
        normalizeBoolean(category.ativo, true) &&
        normalizeBoolean(category.visivel, true);
      const available =
        category.disponivel_efetivo !== undefined
          ? normalizeBoolean(category.disponivel_efetivo)
          : rawAvailable;

      return {
        id: category.id || category.codigo,
        slug: category.codigo || category.slug,
        name: category.nome,
        grupoId: category.grupo_id || null,
        count: category.quantidade_produtos || 0,
        description: category.descricao || "",
        order: Number(category.ordem || 0),
        visible: normalizeBoolean(category.visivel, true),
        channels: Array.isArray(category.canais)
          ? category.canais
          : this.publicationChannelOptions.map((channel) => channel.value),
        available,
        rawAvailable,
        pausedUntil: category.pausado_ate || null,
      };
    },
    applyOperationData(operacao) {
      const formasPagamento = operacao.formas_pagamento || {};
      const mensagens = operacao.mensagens_cliente || {};

      this.checkoutForm.cash = normalizeBoolean(formasPagamento.dinheiro);
      this.checkoutForm.card = normalizeBoolean(formasPagamento.cartao);
      this.checkoutForm.message = mensagens.checkout ?? "";
      this.persistedCheckout = Object.assign({}, this.checkoutForm);
    },
    applyVisualData(visual, textos) {
      const has = (key) => Object.prototype.hasOwnProperty.call(visual, key);

      if (has("logo_url") && visual.logo_url !== undefined) {
        this.visualForm.logoUrl = visual.logo_url || null;
      }
      if (has("capa_url") && visual.capa_url !== undefined) {
        this.visualForm.capaUrl = visual.capa_url || null;
      }
      this.visualForm.logoFile = null;
      this.visualForm.capaFile = null;
      this.visualForm.removeLogo = false;
      this.visualForm.removeCapa = false;
      this.clearVisualPreview("logo");
      this.clearVisualPreview("capa");
      this.visualForm.primaryColor = visual.primary_color ?? "#EE7361";
      this.visualForm.accentColor = visual.accent_color ?? "#FFD993";
      this.visualForm.backgroundColor = visual.background_color ?? "#FFF8F6";
      this.visualForm.cardStyle = visual.card_style ?? "soft";
      this.visualForm.buttonStyle = visual.button_style ?? "rounded";

      const emptyText = (textos || []).find(
        (texto) => texto.chave === "sem_produtos"
      );
      const errorText = (textos || []).find(
        (texto) => texto.chave === "erro_carregamento"
      );
      const trackingText = (textos || []).find(
        (texto) => texto.chave === "acompanhamento"
      );

      this.visualForm.emptyText = emptyText?.conteudo ?? "";
      this.visualForm.errorText = errorText?.conteudo ?? "";
      this.visualForm.trackingText = trackingText?.conteudo ?? "";
    },
    normalizeProduct(product) {
      const name = product.nome || product.descricao || "Produto";
      const cardapioProductId =
        product.cardapio_produto_id ??
        (normalizeBoolean(product.publicado) ? product.id : null);
      const configured = Boolean(cardapioProductId);
      const published =
        product.publicado !== undefined
          ? normalizeBoolean(product.publicado)
          : configured;
      const sourceProductId = product.produto_id ?? product.id;
      const publicationActive = normalizeBoolean(product.ativo, true);
      const directlyAvailable = normalizeBoolean(product.disponivel, true);
      const priceCents = Number(
        product.preco_centavos ??
          Math.round(Number(product.preco || product.valor || 0) * 100)
      );

      return {
        id: configured ? cardapioProductId || product.id : null,
        cardapioProductId: configured ? cardapioProductId || product.id : null,
        sourceId: sourceProductId,
        productId: sourceProductId,
        code: product.codigo,
        type: product.tipo || "produto",
        slug: product.slug || this.slugify(name),
        name,
        description: product.descricao || "Sem descricao cadastrada.",
        categoryId: product.categoria_id,
        categoryName: product.categoria_nome,
        priceCents: Number.isFinite(priceCents) ? priceCents : 0,
        initials: this.initials(name),
        published,
        configured,
        highlighted: normalizeBoolean(product.destaque),
        available:
          product.disponivel_efetivo !== undefined
            ? normalizeBoolean(product.disponivel_efetivo)
            : publicationActive && directlyAvailable,
        rawAvailable: publicationActive && directlyAvailable,
        pausedUntil: product.pausado_ate || null,
        imageUrl: product.imagem_url,
        noPreparation: normalizeBoolean(
          product.sem_preparo ?? product.restaurante_sem_preparo
        ),
        destinations: Array.isArray(product.destinos)
          ? product.destinos
          : Array.isArray(product.restaurante_destinos)
          ? product.restaurante_destinos
          : [],
        channels: Array.isArray(product.canais) ? product.canais : [],
        hasPizza: normalizeBoolean(product.tem_pizza) || Boolean(product.pizza),
        hasOptions:
          normalizeBoolean(product.possui_opcoes) ||
          Boolean(
            Array.isArray(product.grupos_opcoes) && product.grupos_opcoes.length
          ),
        requiresConfiguration:
          normalizeBoolean(product.requer_configuracao) ||
          Boolean(product.pizza) ||
          Boolean(
            Array.isArray(product.grupos_opcoes) && product.grupos_opcoes.length
          ),
        unavailabilityReasons: Array.isArray(product.motivos_indisponibilidade)
          ? product.motivos_indisponibilidade.filter(Boolean)
          : [],
      };
    },
    normalizePizza(pizza) {
      return Object.assign({}, pizza, {
        tamanhos: (pizza.tamanhos || []).map((size) =>
          Object.assign({}, size, {
            precoText: this.moneyTextFromCents(size.preco_base_centavos || 0),
            ativo: normalizeBoolean(size.ativo, true),
            localKey: size.id || `${Date.now()}-${Math.random()}`,
          })
        ),
        sabores: (pizza.sabores || []).map((flavor) =>
          Object.assign({}, flavor, {
            precoText: this.moneyTextFromCents(flavor.preco_centavos || 0),
            tamanhos: flavor.tamanhos || [],
            ativo: normalizeBoolean(flavor.ativo, true),
            disponivel: normalizeBoolean(flavor.disponivel, true),
            localKey: flavor.id || `${Date.now()}-${Math.random()}`,
          })
        ),
        bordas: (pizza.bordas || []).map((border) =>
          Object.assign({}, border, {
            precoText: this.moneyTextFromCents(
              border.preco_centavos || border.preco_adicional_centavos || 0
            ),
            tamanhos: border.tamanhos || [],
            ativo: normalizeBoolean(border.ativo, true),
            disponivel: normalizeBoolean(border.disponivel, true),
            localKey: border.id || `${Date.now()}-${Math.random()}`,
          })
        ),
        ativo: normalizeBoolean(pizza.ativo, true),
      });
    },
    normalizeOptionGroup(group) {
      const regras = group.regras || {};
      const categoriaIds = Array.isArray(regras.categorias)
        ? regras.categorias
        : [];

      return Object.assign({}, group, {
        regras,
        categoria_ids: categoriaIds,
        ativo: normalizeBoolean(group.ativo, true),
        opcoes: (group.opcoes || []).map((option) =>
          Object.assign({}, option, {
            precoText: this.moneyTextFromCents(
              option.preco_adicional_centavos || 0
            ),
            ativo: normalizeBoolean(option.ativo, true),
            disponivel: normalizeBoolean(option.disponivel, true),
            available:
              option.disponivel_efetivo !== undefined
                ? normalizeBoolean(option.disponivel_efetivo)
                : normalizeBoolean(option.disponivel, true),
            pausedUntil: option.pausado_ate || null,
            localKey: option.id || `${Date.now()}-${Math.random()}`,
          })
        ),
      });
    },
    syncFormsFromConfig() {
      this.publishingForm = {
        name: this.config.name,
        slug: this.config.slug,
        description: this.config.description,
        closedMessage: this.config.closedMessage,
        weeklyHours: this.clonePlain(
          this.config.weeklyHours.length
            ? this.config.weeklyHours
            : this.defaultWeeklyHours()
        ),
      };
      this.persistedPublishing = this.serializePublishingDraft(
        this.publishingForm
      );
      this.setSlugAvailability(
        this.config.slug ? "available" : "idle",
        this.config.slug,
        ""
      );

      this.deliveryForm.feeText = this.moneyTextFromCents(
        this.config.deliveryFeeCents
      );
      this.deliveryForm.minimumText = this.moneyTextFromCents(
        this.config.minimumOrderCents
      );
      this.deliveryForm.minMinutes = this.config.minMinutes;
      this.deliveryForm.maxMinutes = this.config.maxMinutes;
      this.deliveryForm.deliveryEnabled = this.config.deliveryEnabled;
      this.deliveryForm.baseAddress = this.config.baseAddress;
      this.deliveryForm.pickupEnabled = this.config.pickupEnabled;
      this.deliveryForm.pickupMinutes = this.config.pickupMinutes;
      this.deliveryForm.pickupMessage = this.config.pickupMessage;
      this.persistedDeliveryForm = this.clonePlain(this.deliveryForm);
    },
    async confirmDiscardChanges() {
      if (!this.hasUnsavedChanges) return true;
      return this.$bvModal.msgBoxConfirm(
        "Existem alterações não salvas nesta configuração. Deseja descartá-las?",
        {
          title: "Descartar alterações?",
          okTitle: "Descartar",
          cancelTitle: "Continuar editando",
          okVariant: "danger",
          centered: true,
        }
      );
    },
    discardAllDrafts() {
      this.resetChannelDraft();
      this.cancelPublishing(false);
      this.cancelVisual(false);
      this.resetDeliveryDraft();
      this.resetCheckoutDraft();
      this.entityDraftBaselines = {};
    },
    async selectWorkspace(workspaceId) {
      const workspace = this.sections.find((item) => item.id === workspaceId);
      if (!workspace || workspace.id === this.activeWorkspace) return;
      if (!(await this.confirmDiscardChanges())) return;
      this.discardAllDrafts();
      this.activeSection = workspace.defaultPanel;
      this.syncNavigationQuery();
    },
    async selectPanel(panelId) {
      if (panelId === this.activeSection) return;
      if (!(await this.confirmDiscardChanges())) return;
      this.discardAllDrafts();
      this.activeSection = panelId;
      this.syncNavigationQuery();
    },
    selectSection(sectionId) {
      this.selectPanel(sectionId);
    },
    categoryName(categoryId) {
      const category = this.categories.find((item) =>
        sameIdentifier(item.id, categoryId)
      );
      return category ? category.name : "Sem categoria";
    },
    productName(productId) {
      const product = this.products.find((item) =>
        sameIdentifier(item.id, productId)
      );
      return product ? product.name : "Grupo global";
    },
    productPreparationLabel(product) {
      if (product?.noPreparation) return "Sem preparo";

      const destinations = (product?.destinations || []).map((item) =>
        String(item?.tipo || item?.codigo || item || "").toLowerCase()
      );
      const labels = [];
      if (destinations.some((item) => item.includes("cozinha"))) {
        labels.push("Cozinha");
      }
      if (destinations.some((item) => item.includes("bar"))) labels.push("Bar");

      return labels.length ? labels.join(" + ") : "Definir preparo";
    },
    isTemporarilyPaused(product) {
      if (!product?.pausedUntil || product.available) return false;
      const pausedUntil = new Date(product.pausedUntil);
      return !Number.isNaN(pausedUntil.getTime()) && pausedUntil > new Date();
    },
    optionGroupScopeLabel(group) {
      if (group.cardapio_produto_id) {
        return `Produto: ${this.productName(group.cardapio_produto_id)}`;
      }

      const categorias = group.regras?.categorias || group.categoria_ids || [];

      if (!categorias.length) {
        return "Todo o cardápio";
      }

      return `Categorias: ${categorias
        .map((id) => this.categoryName(id))
        .join(", ")}`;
    },
    productOptionGroups(product) {
      if (!product?.id) {
        return [];
      }

      return this.optionGroups.filter((group) =>
        sameIdentifier(group.cardapio_produto_id, product.id)
      );
    },
    productPizzaTemplate(product) {
      if (!product?.id) {
        return null;
      }

      return (
        this.pizzas.find((pizza) =>
          sameIdentifier(pizza.cardapio_produto_id, product.id)
        ) || null
      );
    },
    optionTypeLabel(type) {
      const item = this.optionGroupTypes.find(
        (option) => option.value === type
      );
      return item ? item.label : "Opção";
    },
    formatMoney(cents) {
      return (Number(cents || 0) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    moneyTextFromCents(cents) {
      return (Number(cents || 0) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    parseMoneyToCents(value) {
      return parseBRLMoneyToCents(value);
    },
    goToNewProduct() {
      this.$router.push({ name: "produtosCreate" });
    },
    goToProducts() {
      this.$router.push({ name: "produtosLista" });
    },
    resetProductPublisher() {
      this.stockRequestSequence += 1;
      this.publicationForm = null;
      this.stockError = "";
      this.stockLoading = false;
      this.categoryCreatedForPublisher = false;
      this.categoryCreatedForProduct = false;
      this.closeEntityDraft("publisher");
    },
    normalizeStockProduct(product) {
      const name = product.nome || product.descricao || "Produto";
      const rawDestinations = Array.isArray(product.destinos)
        ? product.destinos
        : Array.isArray(product.restaurante_destinos)
        ? product.restaurante_destinos
        : [];
      const destinations = rawDestinations
        .map((item) =>
          String(item?.tipo || item?.codigo || item || "").toLowerCase()
        )
        .filter((item) => item === "bar" || item === "cozinha");

      return {
        productId: Number(product.produto_id || product.id || 0),
        name,
        code: product.codigo || product.codigo_barras || "",
        barcode: product.codigo_barras || "",
        priceCents: Number(
          product.preco_centavos !== undefined
            ? product.preco_centavos
            : Math.round(Number(product.preco || 0) * 100)
        ),
        imageUrl: product.imagem_url || product.imagem || null,
        initials: this.initials(name),
        active:
          product.ativo === undefined || product.ativo === null
            ? true
            : normalizeBoolean(product.ativo),
        restaurante: normalizeBoolean(product.restaurante),
        noPreparation: normalizeBoolean(
          product.restaurante_sem_preparo || product.sem_preparo
        ),
        destinations: [...new Set(destinations)],
        groupId: product.grupo_id || product.id_grupo || null,
        groupName: product.grupo_nome || product.categoria_nome || "",
        categoryId: product.categoria_id || null,
        channels: Array.isArray(product.canais) ? product.canais : [],
        quantity: product.quantidade,
      };
    },
    async openProductPublisher() {
      this.resetProductPublisher();
      this.$bvModal.show("modalCardapioProductPublisher");
      await this.loadStockProducts(1);
    },
    async loadStockProducts(page = 1) {
      const requestSequence = ++this.stockRequestSequence;
      this.stockLoading = true;
      this.stockError = "";
      try {
        const { data } = await this.$http.get(
          "/restaurante/cardapio-online/produtos/candidatos",
          {
            params: {
              q: this.stockSearch.q || undefined,
              grupo_id: this.stockSearch.grupo_id || undefined,
              restaurante:
                this.stockSearch.restaurante === ""
                  ? undefined
                  : this.stockSearch.restaurante,
              ativo:
                this.stockSearch.ativo === ""
                  ? undefined
                  : this.stockSearch.ativo,
              page,
            },
          }
        );
        if (requestSequence !== this.stockRequestSequence) return;
        const response = this.unwrapResponse(data) || {};
        const paginator = response.produtos || response;
        const pagination = response.pagination || paginator;
        const items = Array.isArray(paginator)
          ? paginator
          : paginator.data ||
            response.items ||
            response.produtos_candidatos ||
            [];
        this.stockProducts = items.map(this.normalizeStockProduct);
        this.stockMeta = {
          page: Number(
            pagination.current_page || response.current_page || page
          ),
          lastPage: Number(
            pagination.last_page ||
              response.last_page ||
              response.total_paginas ||
              1
          ),
          total: Number(pagination.total || response.total || items.length),
        };
      } catch (error) {
        if (requestSequence !== this.stockRequestSequence) return;
        this.stockProducts = [];
        this.stockMeta = {
          page: 1,
          lastPage: 1,
          total: 0,
        };
        this.stockError =
          error.response?.data?.message ||
          "Não foi possível consultar o estoque. Tente novamente.";
        this.showApiError(error, this.stockError);
      } finally {
        if (requestSequence === this.stockRequestSequence) {
          this.stockLoading = false;
        }
      }
    },
    selectStockProduct(product) {
      const category = this.categories.find((item) =>
        sameIdentifier(item.grupoId, product.groupId)
      );
      const defaultChannels = [
        "cardapio_digital",
        "self_pedido",
        "salao_garcom",
      ];
      this.publicationForm = Object.assign({}, product, {
        destinations: product.noPreparation
          ? []
          : product.destinations.length
          ? [...product.destinations]
          : ["cozinha"],
        categoryId: product.categoryId || category?.id || null,
        channels: product.channels.length
          ? [...product.channels]
          : defaultChannels,
        published: normalizeBoolean(product.active),
      });
      this.captureEntityDraft("publisher");
    },
    onPublicationPreparationChange() {
      if (!this.publicationForm) return;
      if (this.publicationForm.noPreparation) {
        this.publicationForm.destinations = [];
      } else if (!this.publicationForm.destinations.length) {
        this.publicationForm.destinations = ["cozinha"];
      }
    },
    async publishStockProduct() {
      if (!this.publicationForm) return;
      if (!this.publicationForm.categoryId) {
        this.$toast.warning("Escolha ou crie uma categoria.");
        return;
      }
      if (
        !this.publicationForm.noPreparation &&
        !this.publicationForm.destinations.length
      ) {
        this.$toast.warning("Escolha Bar, Cozinha ou ambos.");
        return;
      }
      if (!this.publicationForm.channels.length) {
        this.$toast.warning("Escolha pelo menos um canal.");
        return;
      }

      this.clearFieldErrors();
      this.setSaving("product-publication", true);
      try {
        await this.$http.post(
          "/restaurante/cardapio-online/produtos/publicar",
          {
            produto_id: this.publicationForm.productId,
            habilitar_restaurante: true,
            restaurante_sem_preparo: Boolean(
              this.publicationForm.noPreparation
            ),
            destinos: this.publicationForm.noPreparation
              ? []
              : this.publicationForm.destinations,
            categoria_id: this.publicationForm.categoryId,
            canais: this.publicationForm.channels,
            publicado: Boolean(
              this.publicationForm.active && this.publicationForm.published
            ),
          }
        );
        if (!(await this.loadManagement(true))) return;
        this.$bvModal.hide("modalCardapioProductPublisher");
        this.$toast.success(
          "Produto configurado no restaurante e no cardápio."
        );
      } catch (error) {
        this.showApiError(error, "Não foi possível publicar o produto.");
      } finally {
        this.setSaving("product-publication", false);
      }
    },
    openCategoryModal(
      category = null,
      createdForPublisher = false,
      createdForProduct = false
    ) {
      this.categoryCreatedForPublisher = Boolean(createdForPublisher);
      this.categoryCreatedForProduct = Boolean(createdForProduct);
      this.categoryForm = {
        id: category?.id || null,
        grupoId: category?.grupoId || null,
        originalName: category?.name || "",
        name: category?.name || "",
        description: category?.description || "",
        order: Number(category?.order || 0),
        visible: category ? category.visible !== false : true,
        channels: Array.isArray(category?.channels)
          ? [...category.channels]
          : this.publicationChannelOptions.map((channel) => channel.value),
      };
      this.captureEntityDraft("category");
      this.$bvModal.show("modalCardapioCategory");
    },
    async saveCategory() {
      const categoryName = String(this.categoryForm?.name || "").trim();
      if (!categoryName) {
        this.$toast.warning("Informe o nome da categoria.");
        return;
      }
      if (Number(this.categoryForm.order) < 0) {
        this.$toast.warning("A ordem da categoria não pode ser negativa.");
        return;
      }
      this.categoryForm.name = categoryName;
      if (
        this.categoryForm.id &&
        this.categoryForm.originalName !== this.categoryForm.name
      ) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          "O novo nome também será aplicado ao grupo usado no cadastro, salão e garçom.",
          {
            title: "Confirmar alteração compartilhada",
            okTitle: "Renomear grupo e categoria",
            cancelTitle: "Cancelar",
            okVariant: "primary",
            centered: true,
          }
        );
        if (!confirmed) return;
      }

      this.clearFieldErrors();
      this.setSaving("category", true);
      try {
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/categorias",
          {
            id: this.categoryForm.id,
            grupo_id: this.categoryForm.grupoId,
            nome: this.categoryForm.name,
            descricao: this.categoryForm.description || null,
            ordem: Number(this.categoryForm.order || 0),
            ativo: true,
            visivel: Boolean(this.categoryForm.visible),
            canais: this.categoryForm.channels,
            confirmar_impacto_cadastro: true,
          }
        );
        const savedCategory = this.unwrapResponse(data) || {};
        const createdForPublisher = this.categoryCreatedForPublisher;
        const createdForProduct = this.categoryCreatedForProduct;
        if (!(await this.loadManagement(true))) return;
        if (createdForPublisher && this.publicationForm) {
          const created = this.categories.find((item) =>
            savedCategory.id
              ? sameIdentifier(item.id, savedCategory.id)
              : item.name.toLocaleLowerCase("pt-BR") ===
                categoryName.toLocaleLowerCase("pt-BR")
          );
          if (created) this.publicationForm.categoryId = created.id;
        }
        if (createdForProduct && this.productForm) {
          const created = this.categories.find((item) =>
            savedCategory.id
              ? sameIdentifier(item.id, savedCategory.id)
              : item.name.toLocaleLowerCase("pt-BR") ===
                categoryName.toLocaleLowerCase("pt-BR")
          );
          if (created) this.productForm.categoryId = created.id;
        }
        this.$bvModal.hide("modalCardapioCategory");
        this.$toast.success("Categoria e grupo salvos.");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a categoria.");
      } finally {
        this.setSaving("category", false);
      }
    },
    openVariantCreator() {
      this.$bvModal.show("modalCardapioVariantCreator");
    },
    selectVariantType(type) {
      this.$bvModal.hide("modalCardapioVariantCreator");
      if (type === "pizza") {
        this.openPizzaModal();
        return;
      }
      this.openOptionGroupModal();
      if (this.optionGroupForm) {
        this.optionGroupForm.papel = "variante";
        this.optionGroupForm.tipo = "escolha_unica";
        this.optionGroupForm.minimo = 1;
        this.optionGroupForm.maximo = 1;
        this.optionGroupForm.obrigatorio = true;
        this.captureEntityDraft("optionGroup");
      }
    },
    openFirstCandidate() {
      const candidate = this.candidateProducts[0];
      if (!candidate) {
        this.$toast.info("Não há produtos elegíveis aguardando publicação.");
        return;
      }
      this.openCatalogProduct(candidate);
    },
    openCatalogProduct(product) {
      if (product?.configured && product?.published) {
        this.openProductModal(product);
        return;
      }

      // Candidatos sempre passam pelo fluxo operacional, que configura
      // restaurante, setores, categoria e canais de forma atômica.
      this.stockSearch.q = product?.code || product?.name || "";
      this.openProductPublisher();
    },
    async copyPublicUrl() {
      if (!this.config.publicUrl) return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.config.publicUrl);
        } else {
          const input = document.createElement("textarea");
          input.value = this.config.publicUrl;
          input.setAttribute("readonly", "readonly");
          input.style.position = "fixed";
          input.style.opacity = "0";
          document.body.appendChild(input);
          input.select();
          const copied = document.execCommand("copy");
          document.body.removeChild(input);
          if (!copied) throw new Error("copy-not-supported");
        }
        this.$toast.success("Link do cardápio copiado.");
      } catch (error) {
        this.$toast.warning("Não foi possível copiar o link automaticamente.");
      }
    },
    goToOperation(target) {
      const routes = {
        orders: "restaurantePedidosOnline",
        tables: "restauranteAmbientes",
      };
      if (target === "deliveries") {
        this.$router.push({
          name: "restaurantePedidosOnline",
          query: { view: "operation", lane: "entrega" },
        });
        return;
      }
      if (routes[target]) {
        this.$router.push({ name: routes[target] });
      }
    },
    openPublicPreview() {
      if (this.config.publicUrl) {
        window.open(this.config.publicUrl, "_blank", "noopener,noreferrer");
      }
    },
    openCurrentOfferModal() {
      const actions = {
        promotions: () => this.openPromotionModal(),
        coupons: () => this.openCouponModal(),
        combos: () => this.openComboModal(),
      };
      (actions[this.activeSection] || actions.promotions)();
    },
    openServiceEditor(section) {
      this.clearFieldErrors();
      const actions = {
        delivery: () => {
          this.resetDeliveryDraft();
          this.$bvModal.show("modalCardapioDelivery");
        },
        pickup: () => {
          this.resetDeliveryDraft();
          this.$bvModal.show("modalCardapioPickup");
        },
        checkout: () => {
          this.resetCheckoutDraft();
          this.$bvModal.show("modalCardapioCheckout");
        },
      };
      if (actions[section]) actions[section]();
    },
    resetCourierForm() {
      this.courierForm = {
        usuarioId: "",
        displayName: "",
        phone: "",
      };
      this.clearFieldErrors();
    },
    courierDisplayName(courier) {
      return (
        courier.nome ||
        courier.nome_exibicao ||
        courier.usuario?.nome ||
        courier.usuario?.name ||
        courier.usuario?.usuario ||
        "Entregador"
      );
    },
    courierSecondaryLabel(courier) {
      const username =
        courier.usuario?.usuario ||
        courier.usuario?.email ||
        courier.usuario?.nome ||
        "Usuário vinculado";
      const phone = courier.telefone || courier.usuario?.telefone;
      return phone ? `${username} · ${phone}` : username;
    },
    async loadDeliveryCouriers(silent = false) {
      if (!silent) this.couriersLoading = true;
      try {
        const { data } = await this.$http.get(
          "/restaurante/cardapio-online/entregadores"
        );
        const response = this.unwrapResponse(data);
        this.deliveryCouriers = Array.isArray(response)
          ? response
          : response?.entregadores || [];
        return true;
      } catch (error) {
        if (!silent) {
          this.showApiError(
            error,
            "Não foi possível carregar a equipe de entrega."
          );
        }
        return false;
      } finally {
        this.couriersLoading = false;
      }
    },
    async loadCourierUsers() {
      this.courierUsersLoading = true;
      try {
        const { data } = await this.$http.get(
          "/restaurante/cardapio-online/entregadores/usuarios-candidatos"
        );
        const response = this.unwrapResponse(data);
        this.courierUsers = Array.isArray(response)
          ? response
          : response?.usuarios || [];
        return true;
      } catch (error) {
        this.showApiError(error, "Não foi possível carregar os usuários.");
        return false;
      } finally {
        this.courierUsersLoading = false;
      }
    },
    async openCourierModal() {
      this.clearFieldErrors();
      this.couriersLoading = true;
      const [couriersLoaded, usersLoaded] = await Promise.all([
        this.loadDeliveryCouriers(true),
        this.loadCourierUsers(),
      ]);
      this.couriersLoading = false;
      if (couriersLoaded && usersLoaded) {
        this.resetCourierForm();
        this.$bvModal.show("modalCardapioCourier");
      }
    },
    openCourierUsers() {
      this.$bvModal.hide("modalCardapioCourier");
      this.$router.push({ name: "usuariosLista" });
    },
    async saveCourier() {
      if (!this.courierForm.usuarioId) {
        this.$toast.warning("Selecione o usuário que será entregador.");
        return;
      }

      this.clearFieldErrors();
      this.setSaving("courier", true);
      try {
        await this.$http.post("/restaurante/cardapio-online/entregadores", {
          usuario_id: this.courierForm.usuarioId,
          nome_exibicao: String(this.courierForm.displayName || "").trim(),
          telefone: String(this.courierForm.phone || "").trim(),
          ativo: true,
        });
        await this.loadDeliveryCouriers(true);
        this.$toast.success("Usuário definido como entregador.");
        this.$bvModal.hide("modalCardapioCourier");
      } catch (error) {
        this.showApiError(error, "Não foi possível vincular o entregador.");
      } finally {
        this.setSaving("courier", false);
      }
    },
    async toggleCourier(courier) {
      const savingKey = `courier-${courier.id}`;
      this.setSaving(savingKey, true);
      try {
        await this.$http.post(
          `/restaurante/cardapio-online/entregadores/${courier.id}/status`,
          { ativo: !courier.ativo }
        );
        await this.loadDeliveryCouriers(true);
        this.$toast.success(
          courier.ativo ? "Entregador pausado." : "Entregador ativado."
        );
      } catch (error) {
        this.showApiError(
          error,
          "Não foi possível alterar o status do entregador."
        );
      } finally {
        this.setSaving(savingKey, false);
      }
    },
    resetDeliveryDraft() {
      if (this.persistedDeliveryForm) {
        this.deliveryForm = this.clonePlain(this.persistedDeliveryForm);
      }
    },
    cancelDelivery() {
      this.resetDeliveryDraft();
      this.$bvModal.hide("modalCardapioDelivery");
    },
    cancelPickup() {
      this.resetDeliveryDraft();
      this.$bvModal.hide("modalCardapioPickup");
    },
    buildVisualPreviewUrl() {
      const base = this.config.publicUrl || "";

      if (!base) {
        return "";
      }
      try {
        const url = new URL(base, window.location.origin);
        url.searchParams.set("preview", "1");
        url.searchParams.set("scenario", this.previewScenario);
        url.searchParams.set("revision", String(this.visualRevision));
        return url.toString();
      } catch (error) {
        return "";
      }
    },
    initVisualPreview() {
      this.visualPreviewSrc = this.buildVisualPreviewUrl();
    },
    pushVisualPreviewTheme() {
      const frame = this.$refs.visualPreviewFrame;

      if (!frame || !frame.contentWindow) {
        return;
      }

      let targetOrigin = "";
      try {
        targetOrigin = new URL(frame.src, window.location.origin).origin;
      } catch (error) {
        return;
      }

      if (!targetOrigin || targetOrigin === "null") return;

      frame.contentWindow.postMessage(
        {
          type: "cardapio-preview-draft",
          scenario: this.previewScenario,
          visual: {
            primary_color: this.normalizedHexColor(
              this.visualForm.primaryColor,
              "#EE7361"
            ),
            accent_color: this.normalizedHexColor(
              this.visualForm.accentColor,
              "#FFD993"
            ),
            background_color: this.normalizedHexColor(
              this.visualForm.backgroundColor,
              "#FFF8F6"
            ),
            card_style: this.visualForm.cardStyle || "soft",
            button_style: this.visualForm.buttonStyle || "rounded",
            logo_url: this.visualLogoPreview || null,
            capa_url: this.visualCoverPreview || null,
          },
          textos: {
            sem_produtos: this.visualForm.emptyText || "",
            erro_carregamento: this.visualForm.errorText || "",
            loja_fechada: this.config.closedMessage || "",
            checkout: this.checkoutForm.message || "",
            acompanhamento: this.visualForm.trackingText || "",
          },
          marca: {
            nome: this.config.name || "",
            descricao: this.config.description || "",
            logo_url: this.visualLogoPreview || null,
            capa_url: this.visualCoverPreview || null,
          },
        },
        targetOrigin
      );
    },
    scheduleVisualPreviewPush() {
      if (this.visualPreviewTimer) {
        clearTimeout(this.visualPreviewTimer);
      }

      this.visualPreviewTimer = setTimeout(() => {
        this.pushVisualPreviewTheme();
        this.visualPreviewTimer = null;
      }, 250);
    },
    openVisualModal() {
      this.clearFieldErrors();
      this.cancelVisual(false);
      this.advancedTheme = this.selectedTheme === "custom";
      this.$bvModal.show("modalCardapioVisual");
    },
    cancelVisual(hide = true) {
      if (this.persistedVisual) {
        this.clearVisualPreview("logo");
        this.clearVisualPreview("capa");
        const draft = this.clonePlain(this.persistedVisual);
        Object.assign(this.visualForm, {
          logoUrl: draft.logoUrl,
          capaUrl: draft.capaUrl,
          logoFile: null,
          capaFile: null,
          logoPreviewUrl: null,
          capaPreviewUrl: null,
          removeLogo: false,
          removeCapa: false,
          primaryColor: draft.primaryColor,
          accentColor: draft.accentColor,
          backgroundColor: draft.backgroundColor,
          cardStyle: draft.cardStyle,
          buttonStyle: draft.buttonStyle,
          emptyText: draft.emptyText,
          errorText: draft.errorText,
          trackingText: draft.trackingText,
        });
      }
      if (hide) this.$bvModal.hide("modalCardapioVisual");
    },
    applyThemePreset(preset) {
      this.visualForm.primaryColor = preset.primary;
      this.visualForm.accentColor = preset.accent;
      this.visualForm.backgroundColor = preset.background;
      this.advancedTheme = false;
    },
    openPublishingModal() {
      this.clearFieldErrors();
      this.syncFormsFromConfig();
      this.$bvModal.show("modalCardapioPublishing");
    },
    openCardapioPauseModal() {
      this.pauseForm = {
        type: "cardapio",
        target: null,
        typeLabel: "Cardápio inteiro",
        name: this.config.name,
        mode: "manual",
        customUntil: "",
        reason: "",
        message:
          this.config.closedMessage ||
          "Estamos fechados agora. Consulte o próximo horário.",
        savingKey: "config",
      };
      this.$bvModal.show("modalCardapioPause");
    },
    openProductModal(product) {
      if (!product) {
        this.$toast.warning(
          "Cadastre ou marque um produto para restaurante primeiro."
        );
        return;
      }

      this.clearFieldErrors();
      this.productForm = Object.assign({}, product, {
        activeTab: "data",
        // O switch edita apenas a disponibilidade persistida da publicação.
        // A disponibilidade efetiva também pode ser reduzida por categoria,
        // cadastro operacional ou pausa temporária e não deve ser gravada nela.
        available: product.rawAvailable,
        effectiveAvailable: product.available,
        originalName: product.name,
        originalCategoryId: product.categoryId,
        priceText: this.moneyTextFromCents(product.priceCents),
        imageFile: null,
        imagePreviewUrl: null,
      });
      this.captureEntityDraft("product");
      this.$bvModal.show("modalCardapioProduct");
    },
    findOptionGroupPreset(key) {
      return this.optionGroupPresets.find((preset) => preset.key === key);
    },
    optionGroupPresetMatches(preset) {
      return (
        this.optionGroupForm &&
        this.optionGroupForm.nome === preset.name &&
        this.optionGroupForm.tipo === preset.type
      );
    },
    applyOptionGroupPreset(key) {
      if (!this.optionGroupForm) {
        return;
      }

      const preset = this.findOptionGroupPreset(key);

      if (!preset) {
        return;
      }

      this.optionGroupForm.nome = preset.name;
      this.optionGroupForm.tipo = preset.type;
      this.optionGroupForm.minimo = preset.min;
      this.optionGroupForm.maximo = preset.max;
      this.optionGroupForm.obrigatorio = preset.required;
      this.optionGroupForm.permite_quantidade = preset.quantity;

      if (
        !this.optionGroupForm.opcoes.length ||
        this.optionGroupForm.id === null
      ) {
        this.optionGroupForm.opcoes = preset.items.map((item, index) => ({
          id: null,
          localKey: `${Date.now()}-${Math.random()}`,
          produto_id: null,
          efeito_operacional:
            preset.type === "observacao_textual" ? "observacao" : "modificador",
          nome: item.name,
          descricao: item.description,
          precoText: this.moneyTextFromCents(item.price),
          ativo: true,
          disponivel: true,
          ordem: index,
        }));
      }
    },
    openOptionGroupModal(group = null, targetProduct = null, presetKey = null) {
      const mode =
        targetProduct || group?.cardapio_produto_id ? "produto" : "global";

      if (!group && mode === "produto" && !targetProduct?.id) {
        this.$toast.warning(
          "Publique um produto no site antes de criar opções."
        );
        return;
      }

      const regras = group?.regras || {};
      const categoriaIds = Array.isArray(regras.categorias)
        ? regras.categorias
        : [];
      const produtoIds = Array.isArray(regras.produtos) ? regras.produtos : [];

      this.optionGroupForm = group
        ? {
            id: group.id,
            cardapio_produto_id: group.cardapio_produto_id,
            mode,
            globalScope:
              regras.escopo === "produtos" && produtoIds.length
                ? "produtos"
                : categoriaIds.length
                ? "categorias"
                : "todos",
            categoria_ids: categoriaIds,
            produto_ids: produtoIds,
            papel: group.papel || "complemento",
            canais: Array.isArray(group.canais)
              ? group.canais
              : ["cardapio_digital", "self_pedido", "salao_garcom"],
            nome: group.nome,
            tipo: group.tipo,
            minimo: group.minimo,
            maximo: group.maximo,
            obrigatorio: normalizeBoolean(group.obrigatorio),
            permite_quantidade: normalizeBoolean(group.permite_quantidade),
            ativo: normalizeBoolean(group.ativo, true),
            ordem: group.ordem || 0,
            opcoes: (group.opcoes || []).map((option) => ({
              id: option.id,
              localKey: option.id || `${Date.now()}-${Math.random()}`,
              produto_id: option.produto_id || null,
              efeito_operacional:
                option.efeito_operacional ||
                (option.produto_id ? "produto_adicional" : "modificador"),
              nome: option.nome,
              descricao: option.descricao,
              precoText: this.moneyTextFromCents(
                option.preco_adicional_centavos || 0
              ),
              ativo: normalizeBoolean(option.ativo, true),
              disponivel: normalizeBoolean(option.disponivel, true),
              ordem: option.ordem || 0,
            })),
          }
        : {
            id: null,
            cardapio_produto_id: mode === "produto" ? targetProduct.id : null,
            mode,
            globalScope: "todos",
            categoria_ids: [],
            produto_ids: [],
            papel: "complemento",
            canais: ["cardapio_digital", "self_pedido", "salao_garcom"],
            nome: "",
            tipo: "multipla_escolha",
            minimo: 0,
            maximo: 1,
            obrigatorio: false,
            permite_quantidade: false,
            ativo: true,
            ordem: this.optionGroups.length,
            opcoes: [],
          };

      if (!group && presetKey) {
        this.applyOptionGroupPreset(presetKey);
      }

      this.captureEntityDraft("optionGroup");
      this.operationalProductSelection = null;
      this.operationalProductSearch = "";
      this.loadOperationalProducts();
      this.$bvModal.show("modalCardapioOptionGroup");
    },
    openProductOptionGroupModal(product, presetKey = null) {
      if (!product?.id) {
        this.$toast.warning("Salve o produto no site antes de criar opções.");
        return;
      }

      this.openOptionGroupModal(null, product, presetKey);
    },
    openOptionProductPicker() {
      if (!this.configuredProducts.length) {
        this.$toast.warning(
          "Configure um produto antes de criar opções específicas."
        );
        return;
      }

      this.$bvModal.show("modalCardapioOptionProductPicker");
    },
    selectProductForOptionGroup(product) {
      this.$bvModal.hide("modalCardapioOptionProductPicker");
      this.openProductOptionGroupModal(product);
    },
    operationalDestinationLabel(product) {
      const destinations = product?.destinations || [];
      const labels = [];
      if (destinations.includes("cozinha")) labels.push("Cozinha");
      if (destinations.includes("bar")) labels.push("Bar");
      return labels.join(" e ") || "Sem setor";
    },
    operationalProductDescription(product) {
      const destination = this.operationalDestinationLabel(product);
      return destination === "Sem setor"
        ? "Produto operacional vinculado"
        : `Produto preparado por: ${destination}`;
    },
    async loadOperationalProducts() {
      const requestSequence = ++this.operationalProductsRequestSequence;
      this.operationalProductsLoading = true;
      this.operationalProductsError = "";
      try {
        const { data } = await this.$http.get(
          "/restaurante/cardapio-online/produtos/candidatos",
          {
            params: {
              q: this.operationalProductSearch || undefined,
              restaurante: 1,
              ativo: 1,
              destino: this.operationalProductDestination || undefined,
              per_page: 50,
            },
          }
        );
        if (requestSequence !== this.operationalProductsRequestSequence) return;
        const response = this.unwrapResponse(data) || {};
        const paginator = response.produtos || response;
        const items = Array.isArray(paginator)
          ? paginator
          : paginator.data || response.items || [];
        this.operationalProducts = items
          .map(this.normalizeStockProduct)
          .filter(
            (product) =>
              product.active &&
              product.destinations.some((destination) =>
                ["bar", "cozinha"].includes(destination)
              ) &&
              (!this.operationalProductDestination ||
                product.destinations.includes(
                  this.operationalProductDestination
                ))
          );
      } catch (error) {
        if (requestSequence !== this.operationalProductsRequestSequence) return;
        this.operationalProducts = [];
        this.operationalProductsError =
          error.response?.data?.message ||
          "Não foi possível consultar os produtos ativos do Bar e da Cozinha.";
      } finally {
        if (requestSequence === this.operationalProductsRequestSequence) {
          this.operationalProductsLoading = false;
        }
      }
    },
    addOperationalProductOption() {
      if (!this.optionGroupForm || !this.operationalProductSelection) return;
      const product = this.operationalProducts.find(
        (item) =>
          Number(item.productId) === Number(this.operationalProductSelection)
      );
      if (!product) return;
      if (
        this.optionGroupForm.opcoes.some((option) =>
          sameIdentifier(option.produto_id, product.productId)
        )
      ) {
        this.$toast.info("Este produto já está vinculado ao grupo.");
        return;
      }

      this.optionGroupForm.opcoes.push({
        id: null,
        localKey: `${Date.now()}-${Math.random()}`,
        produto_id: product.productId,
        efeito_operacional: "produto_adicional",
        nome: product.name,
        descricao: this.operationalProductDescription(product),
        precoText: this.moneyTextFromCents(product.priceCents),
        ativo: true,
        disponivel: true,
        ordem: this.optionGroupForm.opcoes.length,
      });
      this.operationalProductSelection = null;
    },
    openProductPizzaModal(product) {
      if (!product?.id) {
        this.$toast.warning(
          "Salve o produto no site antes de ativar variante."
        );
        return;
      }

      this.openPizzaModal(this.productPizzaTemplate(product), product);
    },
    openProductPromotionModal(product) {
      if (product?.id) {
        const promotion = this.promocoes.find((item) =>
          sameIdentifier(item.cardapio_produto_id, product.id)
        );
        this.openPromotionModal(promotion);
        this.promotionForm.productId = product.id;
        if (!promotion) {
          this.syncPromotionPreviousPriceFromProduct();
        }
        return;
      }

      this.openPromotionModal();
    },
    openPizzaModal(pizza = null, targetProduct = null) {
      const firstProduct = targetProduct || this.configuredProducts[0] || null;

      if (!pizza && !firstProduct) {
        this.$toast.warning(
          "Cadastre ou publique um produto antes da variante."
        );
        return;
      }

      this.pizzaForm = pizza
        ? {
            id: pizza.id,
            cardapio_produto_id: pizza.cardapio_produto_id,
            nome_template: pizza.nome_template,
            regra_preco: "soma_sabores",
            max_sabores_padrao: pizza.max_sabores_padrao || 2,
            ativo: normalizeBoolean(pizza.ativo, true),
            tamanhos: (pizza.tamanhos || []).map((size) => ({
              id: size.id,
              localKey: size.id || `${Date.now()}-${Math.random()}`,
              nome: size.nome,
              slug: size.slug,
              precoText: this.moneyTextFromCents(size.preco_base_centavos || 0),
              max_sabores: size.max_sabores || 2,
              fatias: size.fatias || null,
              ativo: normalizeBoolean(size.ativo, true),
              ordem: size.ordem || 0,
            })),
            sabores: (pizza.sabores || []).map((flavor) => ({
              id: flavor.id,
              localKey: flavor.id || `${Date.now()}-${Math.random()}`,
              produto_id: flavor.produto_id || null,
              nome: flavor.nome,
              slug: flavor.slug,
              descricao: flavor.descricao,
              precoText: this.moneyTextFromCents(flavor.preco_centavos || 0),
              ativo: normalizeBoolean(flavor.ativo, true),
              disponivel: normalizeBoolean(flavor.disponivel, true),
              ordem: flavor.ordem || 0,
              tamanhos: flavor.tamanhos || [],
            })),
            bordas: (pizza.bordas || []).map((border) => ({
              id: border.id,
              localKey: border.id || `${Date.now()}-${Math.random()}`,
              nome: border.nome,
              slug: border.slug,
              descricao: border.descricao || "",
              precoText: this.moneyTextFromCents(
                border.preco_centavos || border.preco_adicional_centavos || 0
              ),
              ativo: normalizeBoolean(border.ativo, true),
              disponivel: normalizeBoolean(border.disponivel, true),
              ordem: border.ordem || 0,
              tamanhos: border.tamanhos || [],
            })),
          }
        : {
            id: null,
            cardapio_produto_id: firstProduct.id,
            nome_template: firstProduct.name,
            regra_preco: "soma_sabores",
            max_sabores_padrao: 2,
            ativo: true,
            tamanhos: [],
            sabores: [],
            bordas: [],
          };

      if (!this.pizzaForm.tamanhos.length) {
        this.addPizzaSize();
      }

      if (!this.pizzaForm.sabores.length) {
        this.addPizzaFlavor();
      }

      this.captureEntityDraft("pizza");
      this.$bvModal.show("modalCardapioPizza");
    },
    duplicatePizza(pizza) {
      const usedProductIds = new Set(
        this.pizzas
          .filter((item) => item.id !== pizza.id)
          .map((item) => item.cardapio_produto_id)
      );
      const targetProduct =
        this.configuredProducts.find(
          (product) =>
            product.id !== pizza.cardapio_produto_id &&
            !usedProductIds.has(product.id)
        ) || null;

      if (!targetProduct) {
        this.$toast.warning(
          "Publique outro produto no catálogo antes de duplicar a variante."
        );
        return;
      }

      this.pizzaForm = {
        id: null,
        cardapio_produto_id: targetProduct.id,
        nome_template: `Cópia de ${pizza.nome_template}`,
        regra_preco: "soma_sabores",
        max_sabores_padrao: pizza.max_sabores_padrao || 2,
        ativo: true,
        tamanhos: (pizza.tamanhos || []).map((size, index) => ({
          id: null,
          localKey: `${Date.now()}-${Math.random()}`,
          nome: size.nome,
          slug: size.slug || this.slugify(size.nome),
          precoText: this.moneyTextFromCents(size.preco_base_centavos || 0),
          max_sabores: size.max_sabores || 2,
          fatias: size.fatias || null,
          ativo: normalizeBoolean(size.ativo, true),
          ordem: index,
        })),
        sabores: (pizza.sabores || []).map((flavor, index) => ({
          id: null,
          localKey: `${Date.now()}-${Math.random()}`,
          produto_id: null,
          nome: flavor.nome,
          slug: flavor.slug || this.slugify(flavor.nome),
          descricao: flavor.descricao,
          precoText: this.moneyTextFromCents(flavor.preco_centavos || 0),
          ativo: normalizeBoolean(flavor.ativo, true),
          disponivel: normalizeBoolean(flavor.disponivel, true),
          ordem: index,
          tamanhos: flavor.tamanhos || [],
        })),
        bordas: (pizza.bordas || []).map((border, index) => ({
          id: null,
          localKey: `${Date.now()}-${Math.random()}`,
          nome: border.nome,
          slug: border.slug || this.slugify(border.nome),
          descricao: border.descricao || "",
          precoText: this.moneyTextFromCents(
            border.preco_centavos || border.preco_adicional_centavos || 0
          ),
          ativo: normalizeBoolean(border.ativo, true),
          disponivel: normalizeBoolean(border.disponivel, true),
          ordem: index,
          tamanhos: border.tamanhos || [],
        })),
      };

      this.captureEntityDraft("pizza");
      this.$bvModal.show("modalCardapioPizza");
    },
    validarImagemSelecionada(file) {
      if (!file) {
        return false;
      }

      const mimePermitidos = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      if (!mimePermitidos.includes(file.type)) {
        this.$toast.error("Formato não suportado. Use JPG, PNG ou WebP.");
        return false;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error("Imagem muito grande. O limite é 5MB.");
        return false;
      }

      return true;
    },
    onProductImageSelected(event) {
      const [file] = event.target.files || [];

      if (!this.productForm || !file) {
        return;
      }

      if (!this.validarImagemSelecionada(file)) {
        event.target.value = "";
        return;
      }

      if (this.productForm.imagePreviewUrl) {
        URL.revokeObjectURL(this.productForm.imagePreviewUrl);
      }

      this.productForm.imageFile = file;
      this.productForm.imagePreviewUrl = URL.createObjectURL(file);
    },
    clearVisualPreview(type) {
      const key = type === "capa" ? "capaPreviewUrl" : "logoPreviewUrl";

      if (this.visualForm[key] && this.visualForm[key].startsWith("blob:")) {
        URL.revokeObjectURL(this.visualForm[key]);
      }
      this.visualForm[key] = null;
    },
    onVisualImageSelected(event, type) {
      const [file] = event.target.files || [];
      const fileKey = type === "capa" ? "capaFile" : "logoFile";
      const previewKey = type === "capa" ? "capaPreviewUrl" : "logoPreviewUrl";

      if (!file) {
        return;
      }

      if (!this.validarImagemSelecionada(file)) {
        event.target.value = "";
        return;
      }

      this.clearVisualPreview(type);
      this.visualForm[fileKey] = file;
      this.visualForm[type === "capa" ? "removeCapa" : "removeLogo"] = false;
      const reader = new FileReader();
      reader.onload = () => {
        this.visualForm[previewKey] = reader.result;
      };
      reader.readAsDataURL(file);
    },
    removeVisualImage(type) {
      const isCover = type === "capa";
      this.clearVisualPreview(type);
      this.visualForm[isCover ? "capaFile" : "logoFile"] = null;
      this.visualForm[isCover ? "capaUrl" : "logoUrl"] = null;
      this.visualForm[isCover ? "removeCapa" : "removeLogo"] = true;
    },
    isValidHexColor(value) {
      return validateHexColor(value);
    },
    normalizedHexColor(value, fallback) {
      return normalizeHexColor(value, fallback);
    },
    colorContrast(colorA, colorB) {
      return calculateColorContrast(colorA, colorB);
    },
    addPizzaSize() {
      if (!this.pizzaForm) return;

      const index = this.pizzaForm.tamanhos.length;
      const nome = index === 0 ? "Grande" : `Tamanho ${index + 1}`;

      this.pizzaForm.tamanhos.push({
        id: null,
        localKey: `${Date.now()}-${Math.random()}`,
        nome,
        slug: this.slugify(nome),
        precoText: "R$ 0,00",
        max_sabores: this.pizzaForm.max_sabores_padrao || 2,
        fatias: null,
        ativo: true,
        ordem: index,
      });
    },
    removePizzaSize(index) {
      if (!this.pizzaForm) return;
      const removed = this.pizzaForm.tamanhos[index];
      this.pizzaForm.tamanhos.splice(index, 1);

      if (removed?.slug) {
        this.pizzaForm.sabores = this.pizzaForm.sabores.map((flavor) =>
          Object.assign({}, flavor, {
            tamanhos: (flavor.tamanhos || []).filter(
              (slug) => slug !== removed.slug
            ),
          })
        );
      }
    },
    addPizzaFlavor() {
      if (!this.pizzaForm) return;

      this.pizzaForm.sabores.push({
        id: null,
        localKey: `${Date.now()}-${Math.random()}`,
        produto_id: null,
        nome: "",
        slug: "",
        descricao: "",
        precoText: "R$ 0,00",
        ativo: true,
        disponivel: true,
        ordem: this.pizzaForm.sabores.length,
        tamanhos: [],
      });
    },
    removePizzaFlavor(index) {
      if (!this.pizzaForm) return;
      this.pizzaForm.sabores.splice(index, 1);
    },
    addPizzaBorder() {
      if (!this.pizzaForm) return;
      this.pizzaForm.bordas.push({
        id: null,
        localKey: `${Date.now()}-${Math.random()}`,
        nome: "",
        slug: "",
        descricao: "",
        precoText: "R$ 0,00",
        ativo: true,
        disponivel: true,
        ordem: this.pizzaForm.bordas.length,
        tamanhos: [],
      });
    },
    removePizzaBorder(index) {
      if (!this.pizzaForm) return;
      this.pizzaForm.bordas.splice(index, 1);
    },
    validatePizzaDraft() {
      if (!this.pizzaForm?.cardapio_produto_id) {
        return "Escolha o produto que receberá a variante.";
      }
      if (!String(this.pizzaForm.nome_template || "").trim()) {
        return "Informe o nome da variante.";
      }
      if (!this.pizzaForm.tamanhos.length) {
        return "Adicione ao menos um tamanho.";
      }
      if (!this.pizzaForm.sabores.length) {
        return "Adicione ao menos um sabor.";
      }

      const sizeSlugs = this.pizzaForm.tamanhos.map((size) =>
        this.slugify(size.slug || size.nome)
      );
      if (
        this.pizzaForm.tamanhos.some(
          (size) =>
            !String(size.nome || "").trim() ||
            !Number.isInteger(Number(size.max_sabores)) ||
            Number(size.max_sabores) < 1 ||
            (size.fatias !== null &&
              size.fatias !== "" &&
              (!Number.isInteger(Number(size.fatias)) ||
                Number(size.fatias) < 1))
        )
      ) {
        return "Preencha nome e limite de sabores de todos os tamanhos.";
      }
      if (new Set(sizeSlugs).size !== sizeSlugs.length) {
        return "Os tamanhos precisam ter nomes diferentes.";
      }
      if (
        this.pizzaForm.tamanhos.some(
          (size) => this.parseMoneyToCents(size.precoText) < 0
        )
      ) {
        return "O preço base dos tamanhos não pode ser negativo.";
      }

      const flavorNames = this.pizzaForm.sabores.map((flavor) =>
        String(flavor.nome || "")
          .trim()
          .toLocaleLowerCase("pt-BR")
      );
      if (flavorNames.some((name) => !name)) {
        return "Preencha o nome de todos os sabores.";
      }
      if (new Set(flavorNames).size !== flavorNames.length) {
        return "Não cadastre sabores duplicados.";
      }
      if (
        this.pizzaForm.sabores.some(
          (flavor) => this.parseMoneyToCents(flavor.precoText) < 0
        )
      ) {
        return "O acréscimo dos sabores não pode ser negativo.";
      }
      if (
        this.pizzaForm.bordas.some(
          (border) => !String(border.nome || "").trim()
        )
      ) {
        return "Preencha o nome de todas as bordas.";
      }
      const borderNames = this.pizzaForm.bordas.map((border) =>
        String(border.nome || "")
          .trim()
          .toLocaleLowerCase("pt-BR")
      );
      if (new Set(borderNames).size !== borderNames.length) {
        return "Não cadastre bordas duplicadas.";
      }
      if (
        this.pizzaForm.bordas.some(
          (border) => this.parseMoneyToCents(border.precoText) < 0
        )
      ) {
        return "O acréscimo das bordas não pode ser negativo.";
      }

      const allowedSizeSlugs = new Set(sizeSlugs);
      const hasUnknownSize = [
        ...this.pizzaForm.sabores,
        ...this.pizzaForm.bordas,
      ].some((item) =>
        (item.tamanhos || []).some(
          (sizeSlug) => !allowedSizeSlugs.has(this.slugify(sizeSlug))
        )
      );
      if (hasUnknownSize) {
        return "Revise os tamanhos permitidos de sabores e bordas.";
      }

      return "";
    },
    async savePizza() {
      if (!this.pizzaForm) return;

      const validationMessage = this.validatePizzaDraft();
      if (validationMessage) {
        this.$toast.warning(validationMessage);
        return;
      }

      this.clearFieldErrors();
      this.setSaving("pizza", true);
      try {
        const payload = Object.assign({}, this.pizzaForm, {
          nome_template: String(this.pizzaForm.nome_template).trim(),
          regra_preco: "soma_sabores",
          tamanhos: this.pizzaForm.tamanhos.map((size, index) => ({
            id: size.id,
            nome: size.nome,
            slug: size.slug || this.slugify(size.nome),
            preco_base_centavos: this.parseMoneyToCents(size.precoText),
            max_sabores: size.max_sabores || 2,
            fatias: size.fatias || null,
            ativo: normalizeBoolean(size.ativo, true),
            ordem: index,
          })),
          sabores: this.pizzaForm.sabores.map((flavor, index) => ({
            id: flavor.id,
            produto_id: flavor.produto_id || null,
            nome: flavor.nome,
            slug: flavor.slug || this.slugify(flavor.nome),
            descricao: flavor.descricao,
            preco_centavos: this.parseMoneyToCents(flavor.precoText),
            ativo: normalizeBoolean(flavor.ativo, true),
            disponivel: normalizeBoolean(flavor.disponivel, true),
            ordem: index,
            tamanhos: flavor.tamanhos || [],
          })),
          bordas: this.pizzaForm.bordas.map((border, index) => ({
            id: border.id,
            nome: border.nome,
            slug: border.slug || this.slugify(border.nome),
            descricao: border.descricao,
            preco_centavos: this.parseMoneyToCents(border.precoText),
            ativo: normalizeBoolean(border.ativo, true),
            disponivel: normalizeBoolean(border.disponivel, true),
            ordem: index,
            tamanhos: border.tamanhos || [],
          })),
        });
        await this.$http.post("/restaurante/cardapio-online/pizzas", payload);
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Variante salva.");
        this.$bvModal.hide("modalCardapioPizza");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a variante.");
      } finally {
        this.setSaving("pizza", false);
      }
    },
    async togglePizza(pizza) {
      this.clearFieldErrors();
      this.setSaving("pizza-status", true);
      try {
        const activating = !pizza.ativo;
        await this.$http.post(
          `/restaurante/cardapio-online/pizzas/${pizza.id}/status`,
          { ativo: activating }
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success(
          activating ? "Variante ativada." : "Variante pausada."
        );
      } catch (error) {
        this.showApiError(error, "Não foi possível alterar a variante.");
      } finally {
        this.setSaving("pizza-status", false);
      }
    },
    addOptionItem() {
      if (!this.optionGroupForm) return;

      this.optionGroupForm.opcoes.push({
        id: null,
        localKey: `${Date.now()}-${Math.random()}`,
        produto_id: null,
        efeito_operacional:
          this.optionGroupForm.tipo === "observacao_textual"
            ? "observacao"
            : "modificador",
        nome: "",
        descricao: "",
        precoText: "R$ 0,00",
        ativo: true,
        disponivel: true,
        ordem: this.optionGroupForm.opcoes.length,
      });
    },
    onOptionEffectChange(option) {
      if (!option) return;
      if (option.efeito_operacional !== "produto_adicional") {
        option.produto_id = null;
      }
    },
    onOptionLinkedProductChange(option) {
      if (!option || !option.produto_id) return;
      option.efeito_operacional = "produto_adicional";
      const linkedProduct = this.linkableProducts.find((product) =>
        sameIdentifier(product.productId, option.produto_id)
      );
      if (linkedProduct && !String(option.nome || "").trim()) {
        option.nome = linkedProduct.name;
      }
      if (linkedProduct) {
        option.precoText = this.moneyTextFromCents(linkedProduct.priceCents);
      }
    },
    removeOptionItem(index) {
      if (!this.optionGroupForm) return;
      this.optionGroupForm.opcoes.splice(index, 1);
    },
    async saveOptionGroup() {
      if (!this.optionGroupForm) return;
      const groupName = String(this.optionGroupForm.nome || "").trim();
      const options = this.optionGroupForm.opcoes || [];
      const minimum = Math.max(0, Number(this.optionGroupForm.minimo || 0));
      const maximum = this.optionGroupForm.maximo
        ? Number(this.optionGroupForm.maximo)
        : null;

      if (!groupName) {
        this.$toast.warning("Informe o nome do grupo.");
        return;
      }
      if (!(this.optionGroupForm.canais || []).length) {
        this.$toast.warning("Escolha pelo menos um canal para o grupo.");
        return;
      }
      if (
        this.optionGroupForm.tipo !== "observacao_textual" &&
        !options.length
      ) {
        this.$toast.warning("Adicione ao menos uma opção ao grupo.");
        return;
      }
      if (maximum !== null && (maximum < 1 || minimum > maximum)) {
        this.$toast.warning("O máximo deve ser maior ou igual ao mínimo.");
        return;
      }
      if (options.some((option) => !String(option.nome || "").trim())) {
        this.$toast.warning("Preencha o nome de todas as opções.");
        return;
      }
      const optionNames = options.map((option) =>
        String(option.nome || "")
          .trim()
          .toLocaleLowerCase("pt-BR")
      );
      if (new Set(optionNames).size !== optionNames.length) {
        this.$toast.warning("Não cadastre opções duplicadas no mesmo grupo.");
        return;
      }
      if (
        options.some((option) => this.parseMoneyToCents(option.precoText) < 0)
      ) {
        this.$toast.warning("O preço adicional não pode ser negativo.");
        return;
      }
      if (
        options.some(
          (option) =>
            option.efeito_operacional === "produto_adicional" &&
            !option.produto_id
        )
      ) {
        this.$toast.warning(
          "Escolha o produto vinculado em cada produto adicional."
        );
        return;
      }
      if (
        this.optionGroupForm.mode === "global" &&
        this.optionGroupForm.globalScope === "categorias" &&
        !(this.optionGroupForm.categoria_ids || []).length
      ) {
        this.$toast.warning("Escolha ao menos uma categoria.");
        return;
      }
      if (
        this.optionGroupForm.mode === "global" &&
        this.optionGroupForm.globalScope === "produtos" &&
        !(this.optionGroupForm.produto_ids || []).length
      ) {
        this.$toast.warning("Escolha ao menos um produto.");
        return;
      }

      this.clearFieldErrors();
      this.setSaving("option-group", true);
      try {
        const isGlobal = this.optionGroupForm.mode !== "produto";
        let regras = null;
        if (isGlobal && this.optionGroupForm.globalScope === "categorias") {
          regras = {
            escopo: "categorias",
            categorias: (this.optionGroupForm.categoria_ids || [])
              .map((categoryId) => Number(categoryId))
              .filter((categoryId) => categoryId > 0),
          };
        } else if (
          isGlobal &&
          this.optionGroupForm.globalScope === "produtos"
        ) {
          regras = {
            escopo: "produtos",
            produtos: (this.optionGroupForm.produto_ids || [])
              .map((productId) => Number(productId))
              .filter((productId) => productId > 0),
          };
        }
        const payload = Object.assign({}, this.optionGroupForm, {
          mode: undefined,
          globalScope: undefined,
          categoria_ids: undefined,
          produto_ids: undefined,
          cardapio_produto_id: isGlobal
            ? null
            : this.optionGroupForm.cardapio_produto_id,
          papel: this.optionGroupForm.papel || "complemento",
          canais: this.optionGroupForm.canais || [],
          regras,
          nome: groupName,
          minimo: this.optionGroupForm.obrigatorio
            ? Math.max(1, minimum)
            : minimum,
          maximo: maximum,
          opcoes: this.optionGroupForm.opcoes.map((option, index) => ({
            id: option.id,
            produto_id: option.produto_id || null,
            efeito_operacional:
              option.efeito_operacional ||
              (option.produto_id ? "produto_adicional" : "modificador"),
            nome: option.nome,
            descricao: option.descricao,
            preco_adicional_centavos:
              option.efeito_operacional === "observacao" ||
              this.optionGroupForm.tipo === "remocao_sem_preco"
                ? 0
                : this.parseMoneyToCents(option.precoText),
            ativo: normalizeBoolean(option.ativo, true),
            disponivel: normalizeBoolean(option.disponivel, true),
            ordem: index,
          })),
        });
        await this.$http.post("/restaurante/cardapio-online/opcoes", payload);
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Grupo de opções salvo.");
        this.$bvModal.hide("modalCardapioOptionGroup");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o grupo.");
      } finally {
        this.setSaving("option-group", false);
      }
    },
    async toggleOptionGroup(group) {
      this.clearFieldErrors();
      this.setSaving("option-status", true);
      try {
        const activating = !group.ativo;
        await this.$http.post(
          `/restaurante/cardapio-online/opcoes/${group.id}/status`,
          { ativo: activating }
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success(activating ? "Grupo ativado." : "Grupo pausado.");
      } catch (error) {
        this.showApiError(error, "Não foi possível alterar o status do grupo.");
      } finally {
        this.setSaving("option-status", false);
      }
    },
    async duplicateOptionGroup(group) {
      this.clearFieldErrors();
      this.setSaving("option-duplicate", true);
      try {
        await this.$http.post(
          `/restaurante/cardapio-online/opcoes/${group.id}/duplicar`
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Grupo duplicado.");
      } catch (error) {
        this.showApiError(error, "Não foi possível duplicar o grupo.");
      } finally {
        this.setSaving("option-duplicate", false);
      }
    },
    pauseTargetConfig(type, target) {
      const configs = {
        category: {
          label: "Categoria",
          name: target?.name || "Categoria",
          savingKey: "category-status",
          url: `/restaurante/cardapio-online/categorias/${target?.id}/status`,
          payloadKey: "ativo",
          success: "Categoria pausada",
        },
        product: {
          label: "Produto",
          name: target?.name || "Produto",
          savingKey: "product-status",
          url: `/restaurante/cardapio-online/produtos/${target?.id}/status`,
          payloadKey: "disponivel",
          success: "Produto pausado",
        },
        option: {
          label: "Opção",
          name: target?.nome || "Opção",
          savingKey: "option-item-status",
          url: `/restaurante/cardapio-online/opcoes/itens/${target?.id}/status`,
          payloadKey: "disponivel",
          success: "Opção pausada",
        },
        cardapio: {
          label: "Cardápio inteiro",
          name: this.config.name || "Cardápio",
          savingKey: "config",
          success: "Cardápio pausado",
        },
      };

      return configs[type] || null;
    },
    openPauseModal(type, target) {
      const config = this.pauseTargetConfig(type, target);

      if (!config || !target?.id) {
        this.$toast.warning("Salve o item antes de pausar a venda.");
        return;
      }

      this.pauseForm = {
        type,
        target,
        typeLabel: config.label,
        name: config.name,
        mode: "manual",
        customUntil: "",
        savingKey: config.savingKey,
      };
      this.$bvModal.show("modalCardapioPause");
    },
    async confirmPauseTarget() {
      const config = this.pauseTargetConfig(
        this.pauseForm.type,
        this.pauseForm.target
      );

      if (!config) {
        return;
      }

      if (this.pauseForm.type === "cardapio") {
        const saved = await this.saveConfiguration(
          `${config.success} ${this.pauseLabel(this.pauseForm.mode)}.`,
          {
            habilitado: false,
            mensagem_fechado:
              this.pauseForm.message || this.config.closedMessage,
          }
        );

        if (saved) {
          this.$bvModal.hide("modalCardapioPause");
          return;
        }

        return;
      }

      const pausadoAte = this.pausePayload(
        this.pauseForm.mode,
        this.pauseForm.customUntil
      );

      if (pausadoAte === false) {
        return;
      }

      this.clearFieldErrors();
      this.setSaving(config.savingKey, true);
      try {
        const payload = {
          [config.payloadKey]: false,
          pausado_ate: pausadoAte,
        };
        await this.$http.post(config.url, payload);
        if (!(await this.loadManagement(true))) return;
        this.$toast.success(
          `${config.success} ${this.pauseLabel(this.pauseForm.mode)}.`
        );
        this.$bvModal.hide("modalCardapioPause");
      } catch (error) {
        this.showApiError(error, "Não foi possível pausar a venda.");
      } finally {
        this.setSaving(config.savingKey, false);
      }
    },
    pausePayload(mode, customValue = null) {
      if (mode === "manual") {
        return null;
      }

      const date = new Date();

      if (mode === "hour") {
        date.setHours(date.getHours() + 1);
        return date.toISOString();
      }

      if (mode === "today") {
        date.setHours(23, 59, 59, 999);
        return date.toISOString();
      }

      if (mode === "custom") {
        const value = customValue;

        if (!value) {
          this.$toast.warning("Informe quando a venda deve voltar.");
          return false;
        }

        const parsed = new Date(value.replace(" ", "T"));

        if (Number.isNaN(parsed.getTime())) {
          this.$toast.warning("Data de pausa inválida.");
          return false;
        }

        return parsed.toISOString();
      }

      return null;
    },
    pauseLabel(mode) {
      if (mode === "hour") {
        return "por 1 hora";
      }

      if (mode === "today") {
        return "até o fim do dia";
      }

      if (mode === "custom") {
        return "até o horário escolhido";
      }

      return "até ativar novamente";
    },
    async confirmCategoryImpact(category, activate) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `${activate ? "Ativar" : "Pausar"} a categoria "${
          category.name
        }" nos canais do cardápio? O grupo e o uso no salão não serão alterados.`,
        {
          title: "Confirmar visibilidade da categoria",
          okTitle: activate ? "Ativar categoria" : "Pausar categoria",
          cancelTitle: "Cancelar",
          okVariant: activate ? "success" : "danger",
          centered: true,
        }
      );
      if (!confirmed) return;
      if (activate) {
        await this.activateCategory(category);
      } else {
        await this.pauseCategory(category);
      }
    },
    async pauseCategory(category, mode = "manual") {
      if (!category.id) {
        this.$toast.warning("Salve a categoria antes de pausar.");
        return;
      }

      const pausadoAte = this.pausePayload(mode);

      if (pausadoAte === false) {
        return;
      }

      this.clearFieldErrors();
      this.setSaving("category-status", true);
      try {
        await this.$http.post(
          `/restaurante/cardapio-online/categorias/${category.id}/status`,
          { ativo: false, pausado_ate: pausadoAte }
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success(`Categoria pausada ${this.pauseLabel(mode)}.`);
      } catch (error) {
        this.showApiError(error, "Não foi possível pausar a categoria.");
      } finally {
        this.setSaving("category-status", false);
      }
    },
    async activateCategory(category) {
      this.clearFieldErrors();
      this.setSaving("category-status", true);
      try {
        await this.$http.post(
          `/restaurante/cardapio-online/categorias/${category.id}/status`,
          { ativo: true }
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Categoria ativada.");
      } catch (error) {
        this.showApiError(error, "Não foi possível ativar a categoria.");
      } finally {
        this.setSaving("category-status", false);
      }
    },
    async pauseOptionItem(option, mode = "manual") {
      const pausadoAte = this.pausePayload(mode);

      if (pausadoAte === false) {
        return;
      }

      this.clearFieldErrors();
      this.setSaving("option-item-status", true);
      try {
        await this.$http.post(
          `/restaurante/cardapio-online/opcoes/itens/${option.id}/status`,
          { disponivel: false, pausado_ate: pausadoAte }
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success(`Opção pausada ${this.pauseLabel(mode)}.`);
      } catch (error) {
        this.showApiError(error, "Não foi possível pausar a opção.");
      } finally {
        this.setSaving("option-item-status", false);
      }
    },
    async activateOptionItem(option) {
      this.clearFieldErrors();
      this.setSaving("option-item-status", true);
      try {
        await this.$http.post(
          `/restaurante/cardapio-online/opcoes/itens/${option.id}/status`,
          { disponivel: true }
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Opção ativada.");
      } catch (error) {
        this.showApiError(error, "Não foi possível ativar a opção.");
      } finally {
        this.setSaving("option-item-status", false);
      }
    },
    async pauseProduct(product, mode = "manual") {
      if (!product.id) {
        this.$toast.warning("Publique o produto no site antes de pausar.");
        return;
      }

      const pausadoAte = this.pausePayload(mode);

      if (pausadoAte === false) {
        return;
      }

      this.clearFieldErrors();
      this.setSaving("product-status", true);
      try {
        await this.$http.post(
          `/restaurante/cardapio-online/produtos/${product.id}/status`,
          { disponivel: false, pausado_ate: pausadoAte }
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success(`Produto pausado ${this.pauseLabel(mode)}.`);
      } catch (error) {
        this.showApiError(error, "Não foi possível pausar o produto.");
      } finally {
        this.setSaving("product-status", false);
      }
    },
    async activateProduct(product) {
      if (!product.id) {
        this.$toast.warning("Publique o produto no site antes de ativar.");
        return;
      }

      this.clearFieldErrors();
      this.setSaving("product-status", true);
      try {
        await this.$http.post(
          `/restaurante/cardapio-online/produtos/${product.id}/status`,
          { disponivel: true }
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Produto ativado.");
      } catch (error) {
        this.showApiError(error, "Não foi possível ativar o produto.");
      } finally {
        this.setSaving("product-status", false);
      }
    },
    async savePublishing() {
      const name = String(this.publishingForm.name || "").trim();
      const slug = this.slugify(this.publishingForm.slug);
      if (!name) {
        this.$toast.warning("Informe o nome exibido no site.");
        return;
      }
      if (!slug) {
        this.$toast.warning("Informe um endereço válido para o link público.");
        return;
      }
      if (
        this.slugAvailability.checkedSlug !== slug ||
        this.slugAvailability.status !== "available"
      ) {
        const available = await this.checkSlugAvailability(slug);
        if (!available) return;
      }
      if (!this.validateWeeklyHours(this.publishingForm.weeklyHours)) {
        return;
      }

      const saved = await this.saveConfiguration("Publicação salva.", {
        slug,
        nome_publico: name,
        descricao: String(this.publishingForm.description || "").trim(),
        horarios_semanais: this.normalizeWeeklyHours(
          this.publishingForm.weeklyHours
        ),
        mensagem_fechado: String(
          this.publishingForm.closedMessage || ""
        ).trim(),
      });

      if (saved) {
        this.$bvModal.hide("modalCardapioPublishing");
      } else if (this.fieldError("slug")) {
        this.setSlugAvailability("unavailable", slug, this.fieldError("slug"));
      }
    },
    cancelPublishing(hide = true) {
      if (Object.keys(this.persistedPublishing).length) {
        this.publishingForm = this.clonePlain(this.persistedPublishing);
      }
      if (hide) this.$bvModal.hide("modalCardapioPublishing");
    },
    setSlugAvailability(status, checkedSlug = "", message = "") {
      this.slugAvailability = { status, checkedSlug, message };
    },
    scheduleSlugAvailability(value) {
      if (this.slugAvailabilityTimer) {
        clearTimeout(this.slugAvailabilityTimer);
        this.slugAvailabilityTimer = null;
      }

      const slug = this.slugify(value);
      this.$delete(this.fieldErrors, "slug");

      if (
        this.slugAvailability.checkedSlug === slug &&
        ["available", "unavailable"].includes(this.slugAvailability.status)
      ) {
        return;
      }

      if (!slug) {
        this.setSlugAvailability("idle", "", "");
        return;
      }

      if (slug === this.config.slug) {
        this.setSlugAvailability("available", slug, "");
        return;
      }

      this.setSlugAvailability("checking", slug, "");
      this.slugAvailabilityTimer = setTimeout(() => {
        this.slugAvailabilityTimer = null;
        this.checkSlugAvailability(slug);
      }, 450);
    },
    async checkSlugAvailability(slugValue) {
      const slug = this.slugify(slugValue);
      if (!slug) {
        this.setSlugAvailability("idle", "", "");
        return false;
      }

      if (this.slugAvailabilityTimer) {
        clearTimeout(this.slugAvailabilityTimer);
        this.slugAvailabilityTimer = null;
      }

      const requestId = ++this.slugAvailabilityRequest;
      this.setSlugAvailability("checking", slug, "");

      try {
        const response = await this.$http.get(
          "/restaurante/cardapio-online/slug-disponibilidade",
          { params: { slug } }
        );
        if (requestId !== this.slugAvailabilityRequest) return false;

        const result = this.unwrapResponse(response.data) || {};
        const normalized = this.slugify(result.slug || slug);
        const available = result.disponivel === true;
        const message =
          result.motivo === "reservado_sistema"
            ? "Este endereço é reservado pelo sistema. Escolha outro."
            : available
            ? ""
            : "Este endereço já foi utilizado. Escolha outro.";

        this.publishingForm.slug = normalized;
        this.setSlugAvailability(
          available ? "available" : "unavailable",
          normalized,
          message
        );
        return available;
      } catch (error) {
        if (requestId !== this.slugAvailabilityRequest) return false;
        const message =
          error.response?.data?.message ||
          "Não foi possível verificar agora. Tente novamente.";
        this.setSlugAvailability("error", slug, message);
        return false;
      }
    },
    validateWeeklyHours(hours) {
      const validation = validateWeeklyHoursValue(hours);
      if (!validation.valid) {
        this.$set(
          this.fieldErrors,
          "horarios_semanais",
          `${validation.message} Verifique ${this.weekdayLongLabel(
            validation.day
          )}.`
        );
        return false;
      }
      this.$delete(this.fieldErrors, "horarios_semanais");
      return true;
    },
    async saveDelivery() {
      const deliveryFeeCents = this.parseMoneyToCents(
        this.deliveryForm.feeText
      );
      const minimumOrderCents = this.parseMoneyToCents(
        this.deliveryForm.minimumText
      );
      const minMinutes = Number(this.deliveryForm.minMinutes);
      const maxMinutes = Number(this.deliveryForm.maxMinutes);

      if (deliveryFeeCents < 0 || minimumOrderCents < 0) {
        this.$toast.warning("Taxa e pedido mínimo não podem ser negativos.");
        return;
      }
      if (
        !Number.isInteger(minMinutes) ||
        !Number.isInteger(maxMinutes) ||
        minMinutes < 0 ||
        maxMinutes < minMinutes
      ) {
        this.$toast.warning(
          "Informe um prazo válido; o tempo máximo deve ser maior ou igual ao mínimo."
        );
        return;
      }

      this.clearFieldErrors();
      this.setSaving("delivery", true);
      try {
        const deliveryPayload = {
          aceita_entrega: Boolean(this.deliveryForm.deliveryEnabled),
          taxa_entrega_padrao_centavos: deliveryFeeCents,
          pedido_minimo_centavos: minimumOrderCents,
          endereco_base: String(this.deliveryForm.baseAddress || "").trim(),
          tempo_entrega_minutos_min: minMinutes,
          tempo_entrega_minutos_max: maxMinutes,
        };

        await this.$http.post(
          "/restaurante/cardapio-online/entrega",
          deliveryPayload
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Entrega salva.");
        this.$bvModal.hide("modalCardapioDelivery");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar entrega.");
      } finally {
        this.setSaving("delivery", false);
      }
    },
    async savePickup() {
      const pickupMinutes = Number(this.deliveryForm.pickupMinutes);
      if (
        !Number.isInteger(pickupMinutes) ||
        pickupMinutes < 0 ||
        pickupMinutes > 1440
      ) {
        this.$toast.warning(
          "Informe um tempo de retirada entre 0 e 1440 minutos."
        );
        return;
      }
      this.clearFieldErrors();
      this.setSaving("pickup", true);
      try {
        await this.$http.post("/restaurante/cardapio-online/retirada", {
          aceita_retirada: Boolean(this.deliveryForm.pickupEnabled),
          tempo_preparo_minutos: pickupMinutes,
          mensagem: String(this.deliveryForm.pickupMessage || "").trim(),
        });
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Retirada salva.");
        this.$bvModal.hide("modalCardapioPickup");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar retirada.");
      } finally {
        this.setSaving("pickup", false);
      }
    },
    zoneScopeLabel(zone) {
      if (zone.bairro) {
        return `Bairro: ${zone.bairro}`;
      }

      if (zone.cep_inicio && zone.cep_fim) {
        return `CEP ${zone.cep_inicio} a ${zone.cep_fim}`;
      }

      if (zone.cep_inicio) {
        return `CEP ${zone.cep_inicio}`;
      }

      return "Sem bairro/CEP definido";
    },
    newZone() {
      this.zoneForm = {
        open: true,
        id: null,
        nome: "Zona de entrega",
        bairro: "",
        cepInicio: "",
        cepFim: "",
        feeText: this.deliveryForm.feeText || "R$ 0,00",
        minimumText: "R$ 0,00",
        freeAboveText: "R$ 0,00",
        minMinutes: this.deliveryForm.minMinutes,
        maxMinutes: this.deliveryForm.maxMinutes,
        bloqueada: false,
      };
      this.captureEntityDraft("zone");
    },
    editZone(zone) {
      this.zoneForm = {
        open: true,
        id: zone.id,
        nome: zone.nome || "Zona de entrega",
        bairro: zone.bairro || "",
        cepInicio: zone.cep_inicio || "",
        cepFim: zone.cep_fim || "",
        feeText: this.moneyTextFromCents(zone.taxa_entrega_centavos || 0),
        minimumText: this.moneyTextFromCents(zone.pedido_minimo_centavos || 0),
        freeAboveText: this.moneyTextFromCents(
          zone.frete_gratis_acima_centavos || 0
        ),
        minMinutes: zone.tempo_entrega_minutos_min,
        maxMinutes: zone.tempo_entrega_minutos_max,
        bloqueada: Boolean(zone.bloqueada),
      };
      this.captureEntityDraft("zone");
    },
    cancelZone() {
      this.zoneForm.open = false;
      this.closeEntityDraft("zone");
    },
    async saveZone() {
      const freeAboveCents = this.parseMoneyToCents(
        this.zoneForm.freeAboveText
      );
      const bairro = String(this.zoneForm.bairro || "").trim();
      const cepInicio = String(this.zoneForm.cepInicio || "").replace(
        /\D/g,
        ""
      );
      const cepFim = String(this.zoneForm.cepFim || "").replace(/\D/g, "");
      const minMinutes = Number(this.zoneForm.minMinutes);
      const maxMinutes = Number(this.zoneForm.maxMinutes);

      if (!bairro && !cepInicio && !cepFim) {
        this.$toast.warning("Informe um bairro ou uma faixa de CEP.");
        return;
      }
      if (
        (cepInicio && cepInicio.length !== 8) ||
        (cepFim && cepFim.length !== 8)
      ) {
        this.$toast.warning("Informe o CEP com 8 dígitos.");
        return;
      }
      if (cepInicio && cepFim && Number(cepFim) < Number(cepInicio)) {
        this.$toast.warning(
          "O CEP final deve ser maior ou igual ao CEP inicial."
        );
        return;
      }
      if (
        !Number.isInteger(minMinutes) ||
        !Number.isInteger(maxMinutes) ||
        minMinutes < 0 ||
        maxMinutes < minMinutes
      ) {
        this.$toast.warning("Confira o prazo mínimo e máximo desta zona.");
        return;
      }

      const payload = {
        id: this.zoneForm.id || undefined,
        nome: String(this.zoneForm.nome || "Zona de entrega").trim(),
        bairro: bairro || null,
        cep_inicio: cepInicio || null,
        cep_fim: cepFim || null,
        taxa_entrega_centavos: this.parseMoneyToCents(this.zoneForm.feeText),
        pedido_minimo_centavos: this.parseMoneyToCents(
          this.zoneForm.minimumText
        ),
        frete_gratis_acima_centavos: freeAboveCents > 0 ? freeAboveCents : null,
        tempo_entrega_minutos_min: minMinutes,
        tempo_entrega_minutos_max: maxMinutes,
        bloqueada: Boolean(this.zoneForm.bloqueada),
        ativo: true,
      };

      this.clearFieldErrors();
      this.setSaving("delivery-zone", true);
      try {
        await this.$http.post(
          "/restaurante/cardapio-online/entrega/zonas",
          payload
        );
        if (!(await this.loadManagement(true))) return;
        this.zoneForm.open = false;
        this.closeEntityDraft("zone");
        this.$toast.success("Zona de entrega salva.");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a zona.");
      } finally {
        this.setSaving("delivery-zone", false);
      }
    },
    async toggleZone(zone) {
      try {
        await this.$http.post(
          `/restaurante/cardapio-online/entrega/zonas/${zone.id}/status`,
          { ativo: !zone.ativo }
        );
        if (!(await this.loadManagement(true))) return;
      } catch (error) {
        this.showApiError(error, "Não foi possível atualizar a zona.");
      }
    },
    async deleteZone(zone) {
      const confirmacao = await this.$bvModal.msgBoxConfirm(
        `Remover a zona "${zone.nome}"?`,
        {
          okTitle: "Remover",
          cancelTitle: "Cancelar",
          okVariant: "danger",
          centered: true,
        }
      );

      if (!confirmacao) {
        return;
      }

      // Guarda contra duplo-clique durante o DELETE (antes nao havia loading).
      if (this.isSaving("zone-delete")) {
        return;
      }

      try {
        this.setSaving("zone-delete", true);
        await this.$http.delete(
          `/restaurante/cardapio-online/entrega/zonas/${zone.id}`
        );
        if (!(await this.loadManagement(true))) return;

        if (this.zoneForm.id === zone.id) {
          this.zoneForm.open = false;
        }

        this.$toast.success("Zona de entrega removida.");
      } catch (error) {
        this.showApiError(error, "Não foi possível remover a zona.");
      } finally {
        this.setSaving("zone-delete", false);
      }
    },
    buildProductPayload() {
      const payload = {
        id: this.productForm.id,
        produto_id: this.productForm.productId,
        categoria_id: this.productForm.categoryId,
        tipo: this.productForm.type || "produto",
        nome_publico: this.productForm.name,
        slug: this.productForm.slug,
        descricao_publica: this.productForm.description,
        preco_centavos: this.parseMoneyToCents(this.productForm.priceText),
        imagem_principal_url: this.productForm.imageUrl,
        destaque: Boolean(this.productForm.highlighted),
        disponivel: Boolean(this.productForm.available),
      };

      // Estes flags pertencem ao registro publicado. Em uma edição, omiti-los
      // faz o backend preservar o estado atual; ao publicar um candidato, os
      // dois são ativados explicitamente.
      if (!this.productForm.id) {
        payload.ativo = true;
        payload.publicado = true;
      }

      return payload;
    },
    productPayloadForRequest() {
      const payload = this.buildProductPayload();

      if (!this.productForm.imageFile) {
        return payload;
      }

      const formData = new FormData();

      Object.keys(payload).forEach((key) => {
        const value = payload[key];

        if (value === null || value === undefined) {
          return;
        }

        if (typeof value === "boolean") {
          formData.append(key, value ? "1" : "0");
          return;
        }

        formData.append(key, value);
      });

      formData.append("imagem", this.productForm.imageFile);

      return formData;
    },
    async saveProduct() {
      if (!this.productForm) return;

      const publicName = String(this.productForm.name || "").trim();
      const priceCents = this.parseMoneyToCents(this.productForm.priceText);
      if (!publicName) {
        this.$toast.warning("Informe o nome do produto no site.");
        return;
      }
      if (priceCents < 0) {
        this.$toast.warning("O preço do produto não pode ser negativo.");
        return;
      }
      this.productForm.name = publicName;

      const hasCurrentCategory =
        this.productForm.categoryId !== null &&
        this.productForm.categoryId !== undefined &&
        this.productForm.categoryId !== "";
      const hasOriginalCategory =
        this.productForm.originalCategoryId !== null &&
        this.productForm.originalCategoryId !== undefined &&
        this.productForm.originalCategoryId !== "";
      const categoryChanged =
        hasCurrentCategory !== hasOriginalCategory ||
        (hasCurrentCategory &&
          !sameIdentifier(
            this.productForm.categoryId,
            this.productForm.originalCategoryId
          ));
      const sharedCatalogChange =
        categoryChanged ||
        (this.productForm.published &&
          this.productForm.name !== this.productForm.originalName);

      if (sharedCatalogChange) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          categoryChanged
            ? "Mover este produto também altera sua categoria no catálogo usado pelo salão e pelo garçom. Deseja aplicar esta alteração compartilhada?"
            : "Esta renomeação afeta a organização compartilhada do produto. Deseja aplicar a alteração?",
          {
            title: "Confirmar alteração compartilhada",
            okTitle: "Aplicar alteração",
            cancelTitle: "Revisar",
            okVariant: "primary",
            centered: true,
          }
        );

        if (!confirmed) return;
      }

      this.clearFieldErrors();
      this.setSaving("product", true);
      try {
        await this.$http.post(
          "/restaurante/cardapio-online/produtos",
          this.productPayloadForRequest()
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Produto salvo no site.");
        this.$bvModal.hide("modalCardapioProduct");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o produto.");
      } finally {
        this.setSaving("product", false);
      }
    },
    async saveVisual() {
      this.clearFieldErrors();
      if (!this.visualColorsValid) {
        this.$toast.warning("Informe as três cores no formato #RRGGBB.");
        return;
      }
      this.setSaving("visual", true);
      try {
        const formData = new FormData();

        formData.append("logo_url", this.visualForm.logoUrl || "");
        formData.append("capa_url", this.visualForm.capaUrl || "");
        formData.append("primary_color", this.visualForm.primaryColor || "");
        formData.append("accent_color", this.visualForm.accentColor || "");
        formData.append(
          "background_color",
          this.visualForm.backgroundColor || ""
        );
        formData.append("card_style", this.visualForm.cardStyle || "soft");
        formData.append("theme_preset", this.selectedTheme);
        formData.append(
          "button_style",
          this.visualForm.buttonStyle || "rounded"
        );
        formData.append("empty_text", this.visualForm.emptyText || "");
        formData.append("error_text", this.visualForm.errorText || "");
        formData.append("tracking_text", this.visualForm.trackingText || "");
        formData.append("remove_logo", this.visualForm.removeLogo ? "1" : "0");
        formData.append("remove_capa", this.visualForm.removeCapa ? "1" : "0");

        if (this.visualForm.logoFile) {
          formData.append("logo", this.visualForm.logoFile);
        }

        if (this.visualForm.capaFile) {
          formData.append("capa", this.visualForm.capaFile);
        }

        await this.$http.post(
          "/restaurante/cardapio-online/visual-textos",
          formData
        );
        if (!(await this.loadManagement(true))) return;
        this.persistedVisual = this.serializeVisualDraft(this.visualForm);
        this.visualRevision = Date.now();
        this.visualPreviewSrc = this.buildVisualPreviewUrl();
        this.$toast.success("Aparência e mensagens salvas.");
        this.$bvModal.hide("modalCardapioVisual");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar visual e textos.");
      } finally {
        this.setSaving("visual", false);
      }
    },
    async publishCardapio() {
      if (!this.canPublishCardapio) {
        this.$toast.warning(this.publishBlockingIssues[0]);
        return;
      }

      await this.saveConfiguration("Cardápio publicado.", {
        habilitado: true,
      });
    },
    promotionTargetLabel(promotion) {
      if (promotion.cardapio_produto_id) {
        const variantName = promotion.regras?.variante_nome;
        return [
          this.productName(promotion.cardapio_produto_id),
          variantName || null,
        ]
          .filter(Boolean)
          .join(" · ");
      }

      if (promotion.categoria_id) {
        return this.categoryName(promotion.categoria_id);
      }

      return "Todo o cardápio";
    },
    promotionPeriodLabel(promotion) {
      if (promotion.inicio_at && promotion.fim_at) {
        return `${promotion.inicio_at} até ${promotion.fim_at}`;
      }

      if (promotion.fim_at) {
        return `Até ${promotion.fim_at}`;
      }

      return "Sem data final";
    },
    openPromotionModal(promotion = null) {
      const regras = promotion?.regras || {};
      const targetType = promotion?.categoria_id
        ? "categoria"
        : promotion?.cardapio_produto_id
        ? "produto"
        : "cardapio";
      const productId =
        promotion?.cardapio_produto_id ||
        this.publishedProducts[0]?.id ||
        null;
      const product = this.findPromotionProduct(productId);
      const variantKey = this.promotionVariantKeyFromRules(
        productId,
        regras
      );
      const variant = this.promotionVariantOptionsFor(productId).find(
        (item) => item.key === variantKey
      );
      const previousPriceCents = promotion
        ? Number(regras.preco_de_centavos || 0)
        : targetType === "produto"
        ? Number(variant?.priceCents || product?.priceCents || 0)
        : 0;

      this.promotionForm = {
        id: promotion?.id || null,
        preset: regras.preset || "banner",
        name: promotion?.nome || "Promoção do cardápio",
        subtitle: regras.subtitulo || "",
        description: promotion?.descricao || "",
        targetType,
        productId,
        variantKey,
        categoryId: promotion?.categoria_id || this.categories[0]?.id || null,
        discountType:
          promotion?.tipo === "percentual" || promotion?.percentual_desconto > 0
            ? "percentual"
            : "valor",
        discountText: this.moneyTextFromCents(
          promotion?.valor_desconto_centavos || 0
        ),
        discountPercent: Number(promotion?.percentual_desconto || 0),
        previousPriceText: this.moneyTextFromCents(previousPriceCents),
        promotionalPriceText: this.moneyTextFromCents(
          regras.preco_por_centavos || 0
        ),
        bannerText: regras.banner_text || promotion?.descricao || "",
        giftText: regras.brinde || "",
        imageUrl: promotion?.imagem_url || regras.imagem_url || "",
        imageFile: null,
        imagePreviewUrl: null,
        start: promotion?.inicio_at || "",
        end: promotion?.fim_at || "",
        weekdays: Array.isArray(regras.dias_semana) ? regras.dias_semana : [],
        enabled: promotion ? normalizeBoolean(promotion.ativo) : true,
      };
      this.captureEntityDraft("promotion");
      this.$bvModal.show("modalCardapioPromotions");
    },
    findPromotionProduct(productId = this.promotionForm.productId) {
      return this.products.find((product) =>
        sameIdentifier(product.id, productId)
      );
    },
    promotionOptionGroupAppliesToProduct(group, product) {
      if (!group || !product) return false;

      if (group.cardapio_produto_id) {
        return sameIdentifier(group.cardapio_produto_id, product.id);
      }

      const regras = group.regras || {};
      const produtos = Array.isArray(regras.produtos) ? regras.produtos : [];
      const categorias = Array.isArray(regras.categorias)
        ? regras.categorias
        : [];

      if (regras.escopo === "produtos") {
        return produtos.some((id) => sameIdentifier(id, product.id));
      }

      return (
        !categorias.length ||
        categorias.some((id) => sameIdentifier(id, product.categoryId))
      );
    },
    promotionVariantOptionsFor(productId) {
      const product = this.findPromotionProduct(productId);
      if (!product) return [];

      const options = [];
      const pizza = this.productPizzaTemplate(product);

      (pizza?.tamanhos || [])
        .filter((size) => normalizeBoolean(size.ativo, true))
        .forEach((size) => {
          const code = size.slug || this.slugify(size.nome);
          options.push({
            key: `pizza_tamanho:${size.id || code}`,
            type: "pizza_tamanho",
            id: size.id || null,
            code,
            groupId: pizza.id || null,
            groupCode: null,
            name: size.nome,
            label: `Tamanho: ${size.nome}`,
            priceCents: Number(size.preco_base_centavos || 0),
          });
        });

      this.optionGroups
        .filter(
          (group) =>
            group.papel === "variante" &&
            normalizeBoolean(group.ativo, true) &&
            this.promotionOptionGroupAppliesToProduct(group, product)
        )
        .forEach((group) => {
          (group.opcoes || [])
            .filter(
              (option) =>
                normalizeBoolean(option.ativo, true) &&
                normalizeBoolean(option.disponivel, true)
            )
            .forEach((option) => {
              const code = option.codigo || String(option.id || "");
              options.push({
                key: `opcao_variante:${option.id || code}`,
                type: "opcao_variante",
                id: option.id || null,
                code,
                groupId: group.id || null,
                groupCode: group.codigo || null,
                name: option.nome,
                label: `${group.nome}: ${option.nome}`,
                priceCents:
                  Number(product.priceCents || 0) +
                  Number(option.preco_adicional_centavos || 0),
              });
            });
        });

      return options;
    },
    promotionVariantKeyFromRules(productId, regras = {}) {
      const type = regras.variante_tipo;
      if (!type) return "";

      const variants = this.promotionVariantOptionsFor(productId);
      const variant = variants.find(
        (item) =>
          item.type === type &&
          ((regras.variante_id && sameIdentifier(item.id, regras.variante_id)) ||
            (regras.variante_codigo &&
              item.code === regras.variante_codigo))
      );

      return variant?.key || "";
    },
    syncPromotionPreviousPriceFromProduct() {
      if (this.promotionForm.targetType !== "produto") {
        return;
      }

      const product = this.findPromotionProduct();
      const variants = this.promotionVariantOptionsFor(product?.id);
      if (
        this.promotionForm.variantKey &&
        !variants.some(
          (variant) => variant.key === this.promotionForm.variantKey
        )
      ) {
        this.promotionForm.variantKey = "";
      }
      const variant = variants.find(
        (item) => item.key === this.promotionForm.variantKey
      );
      this.promotionForm.previousPriceText = this.moneyTextFromCents(
        variant?.priceCents || product?.priceCents || 0
      );
    },
    onPromotionTargetTypeChange() {
      if (
        this.promotionForm.targetType === "produto" &&
        !this.promotionForm.productId
      ) {
        this.promotionForm.productId = this.publishedProducts[0]?.id || null;
      }

      if (this.promotionForm.targetType !== "produto") {
        this.promotionForm.variantKey = "";
      }

      this.syncPromotionPreviousPriceFromProduct();
    },
    applyPromotionPreset(preset) {
      this.promotionForm.preset = preset;

      const presetData = {
        banner: {
          name: "Banner promocional",
          subtitle: "Oferta por tempo limitado",
          discountType: "valor",
        },
        preco_especial: {
          name: "Preço especial",
          subtitle: "Preço promocional no cardápio",
          discountType: "valor",
        },
        compre_ganhe: {
          name: "Compre e ganhe",
          subtitle: "Benefício na compra deste item",
          discountType: "valor",
        },
        dia_semana: {
          name: "Promoção da semana",
          subtitle: "Válida nos dias selecionados",
          discountType: "valor",
        },
      }[preset];

      if (!presetData) {
        return;
      }

      this.promotionForm.name = presetData.name;
      this.promotionForm.subtitle = presetData.subtitle;
      this.promotionForm.discountType = presetData.discountType;

      if (preset === "dia_semana" && !this.promotionForm.weekdays.length) {
        this.promotionForm.weekdays = [2];
      }
    },
    onPromotionImageSelected(event) {
      const file = event.target.files && event.target.files[0];

      if (!file) {
        return;
      }

      if (!this.validarImagemSelecionada(file)) {
        event.target.value = "";
        return;
      }

      if (this.promotionForm.imagePreviewUrl) {
        URL.revokeObjectURL(this.promotionForm.imagePreviewUrl);
      }

      this.promotionForm.imageFile = file;
      this.promotionForm.imagePreviewUrl = URL.createObjectURL(file);
    },
    openCouponModal(coupon = null) {
      this.couponForm = {
        id: coupon?.id || null,
        code: coupon?.codigo || "",
        name: coupon?.nome || "",
        discountType:
          coupon?.tipo_desconto === "percentual" ||
          coupon?.percentual_desconto > 0
            ? "percentual"
            : "valor",
        discountText: this.moneyTextFromCents(
          coupon?.valor_desconto_centavos || 0
        ),
        discountPercent: Number(coupon?.percentual_desconto || 0),
        minimumText: this.moneyTextFromCents(
          coupon?.pedido_minimo_centavos || 0
        ),
        totalLimit: coupon?.limite_uso_total || null,
        start: coupon?.inicio_at || "",
        end: coupon?.fim_at || "",
        enabled: coupon ? normalizeBoolean(coupon.ativo) : true,
      };
      this.captureEntityDraft("coupon");
      this.$bvModal.show("modalCardapioCoupon");
    },
    openComboModal(combo = null) {
      const itens = Array.isArray(combo?.itens) ? combo.itens : [];

      this.comboForm = {
        id: combo?.id || null,
        productId:
          combo?.cardapio_produto_id || this.publishedProducts[0]?.id || null,
        name: combo?.nome || "Combo",
        description: combo?.descricao || "",
        priceText: this.moneyTextFromCents(combo?.preco_combo_centavos || 0),
        itemsText: itens
          .map((item) => (typeof item === "string" ? item : item.nome || ""))
          .filter(Boolean)
          .join("\n"),
        enabled: combo ? normalizeBoolean(combo.ativo) : true,
      };
      this.captureEntityDraft("combo");
      this.$bvModal.show("modalCardapioCombo");
    },
    dateRangeError(start, end) {
      if (!start || !end) return "";
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (
        Number.isNaN(startDate.getTime()) ||
        Number.isNaN(endDate.getTime())
      ) {
        return "Informe datas válidas.";
      }
      return endDate < startDate
        ? "A data final deve ser posterior à data inicial."
        : "";
    },
    async savePromotion() {
      const name = String(this.promotionForm.name || "").trim();
      const isPercentual = this.promotionForm.discountType === "percentual";
      const discountPercent = Number(this.promotionForm.discountPercent || 0);
      const discountCents = this.parseMoneyToCents(
        this.promotionForm.discountText
      );
      const dateError = this.dateRangeError(
        this.promotionForm.start,
        this.promotionForm.end
      );
      if (!name) {
        this.$toast.warning("Informe o nome da campanha.");
        return;
      }
      if (
        (this.promotionForm.targetType === "produto" &&
          !this.promotionForm.productId) ||
        (this.promotionForm.targetType === "categoria" &&
          !this.promotionForm.categoryId)
      ) {
        this.$toast.warning("Escolha onde a campanha será aplicada.");
        return;
      }
      if (
        this.promotionForm.targetType === "produto" &&
        this.promotionVariantOptions.length &&
        !this.selectedPromotionVariant
      ) {
        this.$toast.warning("Escolha o tamanho ou variante da promoção.");
        return;
      }
      if (
        (isPercentual &&
          (!Number.isFinite(discountPercent) ||
            discountPercent < 0 ||
            discountPercent > 100)) ||
        (!isPercentual && discountCents < 0)
      ) {
        this.$toast.warning("Informe um desconto válido.");
        return;
      }
      if (
        this.promotionForm.preset === "dia_semana" &&
        !(this.promotionForm.weekdays || []).length
      ) {
        this.$toast.warning("Escolha ao menos um dia da semana.");
        return;
      }
      if (dateError) {
        this.$toast.warning(dateError);
        return;
      }
      this.promotionForm.name = name;
      this.clearFieldErrors();
      this.setSaving("promotion", true);
      try {
        const product = this.products.find((item) =>
          sameIdentifier(item.id, this.promotionForm.productId)
        );
        const promotionPriceCents =
          this.selectedPromotionVariant?.priceCents || product?.priceCents || 0;
        const promotionalPrice = this.parseMoneyToCents(
          this.promotionForm.promotionalPriceText
        );
        const calculatedDiscount =
          !isPercentual && promotionalPrice > 0 && product
            ? Math.max(0, promotionPriceCents - promotionalPrice)
            : this.parseMoneyToCents(this.promotionForm.discountText);
        const formData = new FormData();

        if (this.promotionForm.id) {
          formData.append("id", this.promotionForm.id);
        }

        formData.append("nome", this.promotionForm.name);
        formData.append("tipo", isPercentual ? "percentual" : "valor");
        formData.append(
          "descricao",
          this.promotionForm.description ||
            this.promotionForm.bannerText ||
            this.promotionForm.name
        );
        formData.append(
          "cardapio_produto_id",
          this.promotionForm.targetType === "produto"
            ? this.promotionForm.productId || ""
            : ""
        );
        formData.append(
          "categoria_id",
          this.promotionForm.targetType === "categoria"
            ? this.promotionForm.categoryId || ""
            : ""
        );
        formData.append(
          "valor_desconto_centavos",
          isPercentual ? 0 : calculatedDiscount
        );
        formData.append(
          "percentual_desconto",
          isPercentual
            ? Math.min(
                100,
                Math.max(0, Number(this.promotionForm.discountPercent || 0))
              )
            : 0
        );
        formData.append("inicio_at", this.promotionForm.start || "");
        formData.append("fim_at", this.promotionForm.end || "");
        formData.append("ativo", this.promotionForm.enabled ? "1" : "0");
        formData.append("destaque", "1");
        formData.append("regras[preset]", this.promotionForm.preset);
        formData.append("regras[subtitulo]", this.promotionForm.subtitle || "");
        formData.append(
          "regras[banner_text]",
          this.promotionForm.bannerText || ""
        );
        formData.append("regras[brinde]", this.promotionForm.giftText || "");
        formData.append(
          "regras[preco_de_centavos]",
          this.parseMoneyToCents(this.promotionForm.previousPriceText)
        );
        formData.append("regras[preco_por_centavos]", promotionalPrice);
        if (this.selectedPromotionVariant) {
          formData.append(
            "regras[variante_tipo]",
            this.selectedPromotionVariant.type
          );
          formData.append(
            "regras[variante_id]",
            this.selectedPromotionVariant.id || ""
          );
          formData.append(
            "regras[variante_codigo]",
            this.selectedPromotionVariant.code || ""
          );
          formData.append(
            "regras[variante_grupo_id]",
            this.selectedPromotionVariant.groupId || ""
          );
          formData.append(
            "regras[variante_grupo_codigo]",
            this.selectedPromotionVariant.groupCode || ""
          );
          formData.append(
            "regras[variante_nome]",
            this.selectedPromotionVariant.name || ""
          );
        }
        (this.promotionForm.weekdays || []).forEach((day, index) => {
          formData.append(`regras[dias_semana][${index}]`, day);
        });

        if (this.promotionForm.imageUrl) {
          formData.append("imagem_url", this.promotionForm.imageUrl);
        }

        if (this.promotionForm.imageFile) {
          formData.append("imagem", this.promotionForm.imageFile);
        }

        await this.$http.post(
          "/restaurante/cardapio-online/promocoes",
          formData
        );
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Promoção salva.");
        this.$bvModal.hide("modalCardapioPromotions");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a promoção.");
      } finally {
        this.setSaving("promotion", false);
      }
    },
    async saveCoupon() {
      const code = String(this.couponForm.code || "")
        .trim()
        .toLocaleUpperCase("pt-BR");
      const isPercentual = this.couponForm.discountType === "percentual";
      const percentage = Number(this.couponForm.discountPercent || 0);
      const valueCents = this.parseMoneyToCents(this.couponForm.discountText);
      const minimumCents = this.parseMoneyToCents(this.couponForm.minimumText);
      const limit = this.couponForm.totalLimit;
      const dateError = this.dateRangeError(
        this.couponForm.start,
        this.couponForm.end
      );
      if (!code || !/^[A-Z0-9_-]+$/.test(code)) {
        this.$toast.warning(
          "Use letras, números, hífen ou sublinhado no código do cupom."
        );
        return;
      }
      if (
        (isPercentual &&
          (!Number.isFinite(percentage) ||
            percentage <= 0 ||
            percentage > 100)) ||
        (!isPercentual && valueCents <= 0)
      ) {
        this.$toast.warning("O desconto do cupom deve ser maior que zero.");
        return;
      }
      if (minimumCents < 0) {
        this.$toast.warning("O pedido mínimo não pode ser negativo.");
        return;
      }
      if (
        limit !== null &&
        limit !== "" &&
        (!Number.isInteger(Number(limit)) || Number(limit) < 1)
      ) {
        this.$toast.warning("O limite de usos deve ser um número positivo.");
        return;
      }
      if (dateError) {
        this.$toast.warning(dateError);
        return;
      }
      this.couponForm.code = code;
      this.clearFieldErrors();
      this.setSaving("coupon", true);
      try {
        await this.$http.post("/restaurante/cardapio-online/cupons", {
          id: this.couponForm.id || null,
          codigo: code,
          nome: this.couponForm.name,
          tipo_desconto: isPercentual ? "percentual" : "valor",
          valor_desconto_centavos: isPercentual ? 0 : valueCents,
          percentual_desconto: isPercentual
            ? Math.min(
                100,
                Math.max(0, Number(this.couponForm.discountPercent || 0))
              )
            : 0,
          pedido_minimo_centavos: minimumCents,
          limite_uso_total: this.couponForm.totalLimit || null,
          inicio_at: this.couponForm.start || null,
          fim_at: this.couponForm.end || null,
          ativo: Boolean(this.couponForm.enabled),
        });
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Cupom salvo.");
        this.$bvModal.hide("modalCardapioCoupon");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o cupom.");
      } finally {
        this.setSaving("coupon", false);
      }
    },
    async saveCombo() {
      const name = String(this.comboForm.name || "").trim();
      const priceCents = this.parseMoneyToCents(this.comboForm.priceText);
      const itemNames = String(this.comboForm.itemsText || "")
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);
      if (!this.comboForm.productId) {
        this.$toast.warning("Escolha o produto publicado do combo.");
        return;
      }
      if (!name) {
        this.$toast.warning("Informe o nome do combo.");
        return;
      }
      if (priceCents < 0) {
        this.$toast.warning("O preço do combo não pode ser negativo.");
        return;
      }
      if (!itemNames.length) {
        this.$toast.warning("Descreva ao menos um item do combo.");
        return;
      }
      this.comboForm.name = name;
      this.clearFieldErrors();
      this.setSaving("combo", true);
      try {
        const itens = itemNames.map((nome, index) => ({ nome, ordem: index }));
        await this.$http.post("/restaurante/cardapio-online/combos", {
          id: this.comboForm.id || null,
          cardapio_produto_id: this.comboForm.productId,
          nome: name,
          descricao: this.comboForm.description,
          preco_combo_centavos: priceCents,
          ativo: Boolean(this.comboForm.enabled),
          itens,
        });
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Combo salvo.");
        this.$bvModal.hide("modalCardapioCombo");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o combo.");
      } finally {
        this.setSaving("combo", false);
      }
    },
    resetChannelDraft() {
      this.channelForm = Object.assign({}, this.persistedChannels);
    },
    async saveChannels() {
      this.clearFieldErrors();
      this.setSaving("channels", true);
      try {
        await this.$http.patch("/restaurante/cardapio-online/canais", {
          cardapio_digital_ativo: Boolean(this.channelForm.cardapioDigital),
          self_pedido_ativo: Boolean(this.channelForm.selfPedido),
        });
        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Canais atualizados.");
      } catch (error) {
        this.showApiError(error, "Não foi possível atualizar os canais.");
      } finally {
        this.setSaving("channels", false);
      }
    },
    resetCheckoutDraft() {
      this.checkoutForm = Object.assign({}, this.persistedCheckout);
    },
    cancelCheckout() {
      this.resetCheckoutDraft();
      this.$bvModal.hide("modalCardapioCheckout");
    },
    async saveCheckout() {
      this.clearFieldErrors();
      this.setSaving("checkout", true);
      try {
        await this.$http.post("/restaurante/cardapio-online/pagamento", {
          pix: false,
          dinheiro: this.checkoutForm.cash,
          cartao: this.checkoutForm.card,
          mensagem: this.checkoutForm.message,
        });

        if (!(await this.loadManagement(true))) return;
        this.$toast.success("Checkout salvo.");
        this.$bvModal.hide("modalCardapioCheckout");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o checkout.");
      } finally {
        this.setSaving("checkout", false);
      }
    },
    async saveConfiguration(successMessage, overrides = {}) {
      this.clearFieldErrors();
      this.setSaving("config", true);
      try {
        const payload = Object.assign(
          {
            slug: this.config.slug,
            nome_publico: this.config.name,
            descricao: this.config.description,
            habilitado: this.config.habilitado,
            aceita_entrega: this.config.deliveryEnabled,
            aceita_retirada: this.config.pickupEnabled,
            pedido_minimo_centavos: this.config.minimumOrderCents,
            taxa_entrega_padrao_centavos: this.config.deliveryFeeCents,
            tempo_entrega_minutos_min: this.config.minMinutes,
            tempo_entrega_minutos_max: this.config.maxMinutes,
            horarios_semanais: this.normalizeWeeklyHours(
              this.config.weeklyHours
            ),
            mensagem_fechado: this.config.closedMessage,
          },
          overrides
        );
        await this.$http.post(
          "/restaurante/cardapio-online/configuracao",
          payload
        );
        if (!(await this.loadManagement(true))) return false;
        this.$toast.success(successMessage);
        return true;
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a configuração.");
        return false;
      } finally {
        this.setSaving("config", false);
      }
    },
    slugify(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },
    initials(value) {
      return String(value || "")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },
  },
};
</script>

<style scoped>
.cardapio-online-admin {
  min-width: 0;
}

.cardapio-online-admin button:not(.close),
.cardapio-online-admin a.btn {
  min-width: 44px;
  min-height: 44px;
}

.modal-footer {
  position: sticky;
  z-index: 4;
  bottom: 0;
  margin-top: 20px;
  padding-top: 14px !important;
  border-top: 1px solid #ebedf3;
  background: #ffffff;
}

.cardapio-online-admin button:focus-visible,
.cardapio-online-admin a:focus-visible,
.cardapio-online-admin input:focus-visible,
.cardapio-online-admin select:focus-visible,
.cardapio-online-admin textarea:focus-visible {
  outline: 3px solid rgba(54, 153, 255, 0.32);
  outline-offset: 2px;
}

.overview-grid,
.content-grid {
  display: grid;
  gap: 16px;
}

.overview-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.presentation-alert {
  display: flex;
  gap: 16px;
  padding: 18px 22px;
  border: 1px solid #d8f3dc;
  border-radius: 6px;
  background: #f0fff4;
  color: #172c30;
}

.presentation-alert-icon {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 6px;
  background: #16a34a;
  color: #ffffff;
}

.presentation-alert strong,
.presentation-alert p {
  margin: 0;
}

.slug-availability,
.slug-public-url {
  display: block;
  margin-top: 5px;
  overflow-wrap: anywhere;
}

.slug-availability--checking {
  color: #3699ff;
}

.slug-availability--available {
  color: #0f8f55;
  font-weight: 700;
}

.slug-availability--unavailable,
.slug-availability--error {
  color: #c53030;
  font-weight: 700;
}

.slug-public-url {
  color: #7e8299;
}

.presentation-alert p {
  color: #5f6678;
}

.presentation-alert--success {
  border-color: #c8eed4;
  background: #f0fff4;
}

.presentation-alert--warning {
  border-color: #f6dca8;
  background: #fff9ec;
}

.presentation-alert--warning .presentation-alert-icon {
  background: #e5a100;
}

.presentation-alert--neutral,
.presentation-alert--loading {
  border-color: #dbe7f7;
  background: #f4f8fd;
}

.presentation-alert--neutral .presentation-alert-icon,
.presentation-alert--loading .presentation-alert-icon {
  background: #3699ff;
}

.presentation-alert--danger {
  border-color: #f6c7c7;
  background: #fff4f4;
}

.presentation-alert--danger .presentation-alert-icon {
  background: #f64e60;
}

.overview-card,
.control-card {
  min-width: 0;
  padding: 18px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.overview-card span,
.overview-card small,
.control-card p,
.control-card li,
.control-card small,
.section-button span,
.section-header p,
.toolbar-line p {
  color: #7e8299;
}

.overview-card strong {
  display: block;
  margin: 8px 0 4px;
  color: #181c32;
  font-size: 24px;
  font-weight: 800;
}

.admin-layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 20px;
}

.admin-sidebar {
  display: grid;
  align-content: start;
  gap: 10px;
}

.section-button {
  min-height: 64px;
  width: 100%;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  text-align: left;
}

.section-button:focus-visible,
.workspace-tab:focus-visible,
.theme-preset:focus-visible,
.visual-choice:focus-visible {
  outline: 3px solid rgba(54, 153, 255, 0.32);
  outline-offset: 2px;
}

.section-button strong,
.section-button span {
  display: block;
}

.section-button-active {
  border-color: #f3695f;
  background: #fff5f3;
}

.section-button-active strong {
  color: #f3695f;
}

.admin-content {
  min-width: 0;
}

.section-header,
.toolbar-line,
.control-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.section-header {
  margin-bottom: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid #ebedf3;
}

.workspace-tabs {
  display: flex;
  overflow-x: auto;
  gap: 8px;
  margin: -6px 0 20px;
  padding: 2px 2px 10px;
}

.workspace-tab {
  flex: 0 0 auto;
  min-height: 44px;
  padding: 9px 14px;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  background: #ffffff;
  color: #5f6678;
  font-weight: 700;
}

.workspace-tab--active {
  border-color: #f3695f;
  background: #fff5f3;
  color: #d94f46;
}

.section-header h4,
.control-card h5,
.toolbar-line h5 {
  margin: 0 0 6px;
  color: #181c32;
  font-weight: 800;
}

.content-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.control-card-wide {
  grid-column: span 2;
}

.brand-preview {
  display: flex;
  align-items: center;
  gap: 18px;
}

.brand-preview img {
  width: 78px;
  height: 78px;
  object-fit: contain;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.brand-preview__fallback {
  display: grid;
  flex: 0 0 78px;
  width: 78px;
  height: 78px;
  place-items: center;
  border-radius: 8px;
  background: #fff2ef;
  color: #d94f46;
  font-size: 24px;
  font-weight: 900;
}

.operation-shortcuts,
.publishing-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.channel-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.channel-card,
.service-summary-card,
.appearance-summary,
.publishing-summary {
  padding: 20px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  background: #ffffff;
}

.channel-card__header,
.service-summary-card,
.appearance-summary,
.section-save-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.channel-card__header span,
.channel-card p,
.channel-card li,
.service-summary-card span,
.service-summary-card p,
.publishing-summary span,
.publishing-summary small {
  color: #7e8299;
}

.channel-card h5,
.service-summary-card h5,
.publishing-summary strong {
  margin: 3px 0 8px;
  color: #181c32;
  font-weight: 800;
}

.section-save-bar {
  align-items: center;
  margin-top: 20px;
  padding: 16px 0 0;
  border-top: 1px solid #ebedf3;
}

.delivery-team-card {
  margin-top: 24px;
  padding: 20px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  background: #ffffff;
}

.delivery-team-card__header,
.delivery-team-row,
.delivery-team-row__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.delivery-team-card__header {
  align-items: flex-start;
}

.delivery-team-card__header h5,
.delivery-team-card__header p {
  margin: 0;
}

.delivery-team-card__header p,
.delivery-team-card__permission-note,
.delivery-team-row small,
.delivery-team-card__empty {
  color: #7e8299;
}

.delivery-team-card__header p {
  max-width: 720px;
  margin-top: 6px;
}

.delivery-team-card__eyebrow {
  color: #7e8299;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.delivery-team-list {
  margin-top: 18px;
  border-top: 1px solid #ebedf3;
}

.delivery-team-row {
  min-height: 62px;
  padding: 10px 0;
  border-bottom: 1px solid #ebedf3;
}

.delivery-team-row strong,
.delivery-team-row small {
  display: block;
}

.delivery-team-card__empty {
  margin-top: 18px;
  padding: 18px;
  border-radius: 8px;
  background: #f6f8fb;
  text-align: center;
}

.delivery-team-card__permission-note {
  display: block;
  margin-top: 14px;
}

.publishing-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.publishing-summary > div,
.publishing-summary span,
.publishing-summary strong,
.publishing-summary small {
  display: block;
  min-width: 0;
}

.publishing-summary small {
  overflow-wrap: anywhere;
}

.shared-impact-note {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 13px 14px;
  border: 1px solid #dbe7f7;
  border-radius: 8px;
  background: #f4f8fd;
  color: #4d5d73;
}

.shared-impact-note i {
  color: #3699ff;
}

.catalog-summary,
.service-detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.catalog-summary span,
.service-detail-grid > div {
  padding: 12px 14px;
  border-radius: 8px;
  background: #f6f8fb;
  color: #5f6678;
}

.catalog-summary strong,
.service-detail-grid span,
.service-detail-grid strong {
  display: block;
}

.service-detail-grid span {
  color: #7e8299;
  font-size: 12px;
}

.service-detail-grid strong {
  margin-top: 4px;
  color: #181c32;
  overflow-wrap: anywhere;
}

.appearance-summary {
  align-items: center;
}

.appearance-summary__brand {
  display: flex;
  align-items: center;
  gap: 14px;
}

.appearance-summary__brand img,
.appearance-summary__brand > span {
  display: grid;
  width: 62px;
  height: 62px;
  place-items: center;
  border-radius: 10px;
  background: #fff2ef;
  object-fit: cover;
  color: #d94f46;
  font-weight: 900;
}

.appearance-summary__brand strong,
.appearance-summary__brand small {
  display: block;
}

.appearance-summary__colors {
  display: flex;
  gap: 8px;
}

.appearance-summary__colors span {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(24, 28, 50, 0.12);
  border-radius: 50%;
}

.theme-preset-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.theme-preset {
  min-height: 132px;
  padding: 14px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  background: #ffffff;
  text-align: left;
}

.theme-preset strong,
.theme-preset small {
  display: block;
}

.theme-preset small {
  margin-top: 5px;
  color: #7e8299;
}

.theme-preset--active {
  border-color: #f3695f;
  box-shadow: 0 0 0 2px rgba(243, 105, 95, 0.12);
}

.theme-preset__colors {
  display: flex;
  margin-bottom: 12px;
}

.theme-preset__colors i {
  width: 34px;
  height: 24px;
  border: 1px solid rgba(24, 28, 50, 0.1);
}

.theme-preset__colors i:first-child {
  border-radius: 6px 0 0 6px;
}

.theme-preset__colors i:last-child {
  border-radius: 0 6px 6px 0;
}

.color-control > div {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 8px;
}

.color-control input[type="color"] {
  width: 48px;
  height: 38px;
  padding: 2px;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background: #ffffff;
}

.weekly-schedule {
  display: grid;
  gap: 10px;
}

.weekly-schedule__head,
.weekly-day,
.weekly-day__identity,
.weekly-interval {
  display: flex;
  align-items: center;
  gap: 12px;
}

.weekly-schedule__head {
  justify-content: space-between;
}

.weekly-schedule__head h5,
.weekly-schedule__head p {
  margin: 0;
}

.weekly-schedule__head p,
.weekly-schedule__head small {
  color: #7e8299;
}

.weekly-schedule__presets,
.weekly-schedule__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  background: #f8f9fb;
}

.weekly-schedule__presets > div:last-child {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.weekly-schedule__presets strong,
.weekly-schedule__presets small,
.weekly-schedule__summary span,
.weekly-schedule__summary strong {
  display: block;
}

.weekly-schedule__presets small,
.weekly-schedule__summary span {
  color: #7e8299;
}

.weekly-schedule__summary {
  align-items: flex-start;
  flex-direction: column;
  background: #eef7ff;
}

.weekly-schedule__summary strong {
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
}

.weekly-schedule__presets .btn,
.weekly-interval .btn,
.weekly-day__intervals > .btn {
  min-height: 44px;
}

.weekly-day {
  align-items: flex-start;
  min-height: 64px;
  padding: 12px;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
}

.weekly-day__identity {
  flex: 0 0 160px;
  min-height: 40px;
}

.weekly-day__intervals {
  display: grid;
  flex: 1;
  gap: 8px;
}

.weekly-interval {
  flex-wrap: wrap;
}

.weekly-interval label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}

.weekly-interval label span {
  color: #7e8299;
  font-size: 12px;
}

.weekly-interval .form-control {
  width: 126px;
}

.weekly-day--closed {
  background: #f8f9fb;
}

.brand-preview strong {
  display: block;
  color: #f3695f;
  word-break: break-word;
}

.visual-editor {
  display: grid;
  grid-template-columns: minmax(560px, 1fr) 414px;
  gap: 28px;
  align-items: stretch;
  min-height: 0;
  flex: 1 1 auto;
}

.visual-editor > div:first-child {
  overflow-y: auto;
  min-width: 0;
  min-height: 0;
  padding-right: 8px;
}

.visual-upload-card {
  display: grid;
  gap: 10px;
}

.visual-upload-card label {
  margin-bottom: 0;
  color: #3f4254;
  font-weight: 700;
}

.visual-upload-card small {
  color: #7e8299;
}

.visual-logo-preview,
.visual-cover-preview {
  display: grid;
  overflow: hidden;
  place-items: center;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.visual-logo-preview {
  width: 132px;
  height: 132px;
}

.visual-logo-preview img,
.visual-cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.visual-logo-preview span {
  color: #f3695f;
  font-size: 36px;
  font-weight: 900;
}

.visual-cover-preview {
  min-height: 164px;
  text-align: center;
  background: linear-gradient(135deg, #172c30, #ee7361);
  color: #ffffff;
}

.visual-cover-preview div {
  display: grid;
  gap: 6px;
  padding: 18px;
}

.visual-cover-preview strong,
.visual-cover-preview span {
  display: block;
}

.visual-cover-preview strong {
  font-size: 20px;
}

.visual-cover-preview span {
  color: rgba(255, 255, 255, 0.78);
}

.visual-choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.visual-choice-label {
  margin-bottom: 8px;
  color: #3f4254;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.visual-choice {
  min-height: 92px;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 8px;
  background: #ffffff;
  color: #3f4254;
  text-align: left;
}

.visual-choice strong,
.visual-choice span {
  display: block;
}

.visual-choice span {
  margin-top: 5px;
  color: #7e8299;
  font-size: 12px;
}

.visual-choice--active {
  border-color: #ee7361;
  background: #fff5f2;
  color: #ee7361;
}

.visual-warning,
.visual-preview-note {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff4de;
  color: #895b00;
  font-weight: 700;
}

.visual-live-preview-panel {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  min-width: 0;
}

.visual-live-preview-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
  padding: 0 4px;
}

.visual-live-preview-panel__header div,
.visual-live-preview-panel__header strong,
.visual-live-preview-panel__header span {
  display: block;
}

.visual-live-preview-panel__header strong {
  color: #181c32;
  font-size: 14px;
}

.visual-live-preview-panel__header div > span {
  margin-top: 2px;
  color: #7e8299;
  font-size: 11px;
}

.visual-live-preview-panel__badge {
  padding: 5px 9px;
  border-radius: 999px;
  background: #e8fff3;
  color: #1bc5bd;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.visual-live-preview {
  align-self: start;
  overflow: hidden;
  width: 414px;
  height: min(868px, calc(100vh - 220px));
  min-height: 620px;
  padding: 12px;
  border: 1px solid #cfd4df;
  border-radius: 34px;
  background: #e7e9ef;
  box-shadow: 0 18px 45px rgba(24, 28, 50, 0.18);
  color: #172c30;
}

.visual-live-preview__frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  border-radius: 23px;
  background: #ffffff;
}

.visual-live-preview__empty {
  display: grid;
  gap: 6px;
  align-content: center;
  justify-items: center;
  height: 100%;
  padding: 20px;
  color: #7e8299;
  text-align: center;
}

.visual-live-preview__empty strong {
  color: #3f4254;
}

.visual-live-preview__cover {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  min-height: 124px;
  padding: 14px;
  border-radius: var(--preview-card-radius);
  background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.92),
      rgba(255, 255, 255, 0.72)
    ),
    linear-gradient(135deg, var(--preview-accent), var(--preview-primary));
  background-position: center;
  background-size: cover;
}

.visual-live-preview__logo {
  display: grid;
  overflow: hidden;
  width: 54px;
  height: 54px;
  place-items: center;
  border: 1px solid rgba(238, 115, 97, 0.22);
  border-radius: 10px;
  background: #ffffff;
  color: var(--preview-primary);
  font-weight: 900;
}

.visual-live-preview__logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.visual-live-preview__cover strong,
.visual-live-preview__cover small {
  display: block;
}

.visual-live-preview__cover strong {
  font-size: 18px;
}

.visual-live-preview__cover small {
  margin-top: 4px;
  color: #7e8299;
}

.visual-live-preview__button,
.visual-live-preview__cart {
  display: grid;
  min-height: 42px;
  place-items: center;
  border: 0;
  border-radius: var(--preview-button-radius);
  background: var(--preview-primary);
  color: #ffffff;
  font-weight: 900;
}

.visual-live-preview__card {
  display: grid;
  gap: 4px;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: var(--preview-card-radius);
  background: #ffffff;
  box-shadow: 0 12px 26px rgba(23, 44, 48, 0.08);
}

.visual-live-preview__card span {
  color: var(--preview-primary);
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.visual-live-preview__card small {
  color: #7e8299;
}

.visual-live-preview__card b {
  margin-top: 6px;
  font-size: 18px;
}

.visual-live-preview__alert {
  padding: 11px 12px;
  border: 1px solid rgba(238, 115, 97, 0.26);
  border-radius: var(--preview-card-radius);
  background: rgba(238, 115, 97, 0.08);
  color: #172c30;
  font-weight: 700;
}

.surface-list,
.category-row,
.mini-product-row,
.publishing-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.publishing-actions {
  margin-top: 14px;
}

.category-row {
  margin: 18px 0;
}

.category-chip,
.mini-product {
  padding: 10px 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
  color: #181c32;
  font-weight: 800;
}

.category-chip span,
.mini-product small {
  display: block;
  color: #7e8299;
  font-size: 12px;
  font-weight: 700;
}

.category-chip--paused {
  border-color: #ffd7dd;
  background: #fff5f6;
}

.catalog-status-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.pause-modal-body {
  display: grid;
  gap: 12px;
}

.pause-target-box {
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.pause-target-box span,
.pause-target-box strong,
.pause-target-box small {
  display: block;
}

.pause-target-box span {
  color: #7e8299;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.pause-target-box strong {
  margin: 4px 0;
  color: #181c32;
}

.pause-target-box small,
.pause-choice small {
  color: #7e8299;
}

.pause-choice {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 0;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
}

.pause-choice input {
  margin-top: 3px;
}

.pause-choice strong,
.pause-choice small {
  display: block;
}

.pause-choice-active {
  border-color: #f3695f;
  background: #fff5f3;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-modal-header {
  display: flex;
  gap: 14px;
  align-items: center;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebedf3;
}

.product-modal-header strong,
.product-modal-header small {
  display: block;
}

.product-modal-header strong {
  color: #181c32;
  font-size: 18px;
}

.product-modal-header small {
  color: #7e8299;
}

.product-modal-header .product-photo-preview {
  grid-row: auto;
  overflow: hidden;
  flex: 0 0 68px;
}

.product-modal-header .product-photo-preview,
.product-modal-header .product-photo-preview img,
.product-modal-header .product-photo-preview span {
  width: 68px;
  height: 68px;
}

.product-modal-header .product-photo-preview img,
.product-modal-header .product-photo-preview span {
  display: grid;
  place-items: center;
}

.product-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.product-tabs button {
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  color: #3f4254;
  font-weight: 800;
}

.product-tabs .product-tab-active {
  border-color: #f3695f;
  background: #fff5f3;
  color: #f3695f;
}

.product-media-panel,
.product-config-panel {
  display: grid;
  gap: 18px;
}

.product-media-panel {
  grid-template-columns: 260px minmax(0, 1fr);
  align-items: start;
}

.guided-option-panel {
  display: grid;
  gap: 14px;
  padding: 16px;
  border: 1px solid #ffe1db;
  border-radius: 6px;
  background: #fff8f6;
}

.guided-option-panel strong,
.guided-option-panel p {
  display: block;
  margin: 0;
}

.guided-option-panel p {
  color: #7e8299;
}

.guided-option-grid,
.option-preset-panel {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.guided-option-grid button,
.option-preset-panel button {
  min-height: 74px;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  text-align: left;
}

.guided-option-grid button:hover,
.option-preset-panel button:hover,
.option-preset-active {
  border-color: #f3695f !important;
  background: #fff5f3 !important;
}

.guided-option-grid strong,
.guided-option-grid span,
.option-preset-panel strong,
.option-preset-panel span {
  display: block;
}

.guided-option-grid strong,
.option-preset-panel strong {
  color: #181c32;
}

.guided-option-grid span,
.option-preset-panel span {
  margin-top: 4px;
  color: #7e8299;
  font-size: 12px;
}

.option-scope-guide {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.option-scope-guide article {
  padding: 16px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.option-scope-guide span,
.option-scope-guide strong,
.option-scope-guide p {
  display: block;
}

.option-scope-guide span {
  color: #f3695f;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.option-scope-guide strong {
  margin-top: 4px;
  color: #181c32;
}

.option-scope-guide p {
  margin: 6px 0 0;
  color: #7e8299;
}

.product-linked-list {
  display: grid;
  gap: 10px;
}

.product-linked-list article {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.product-linked-list strong,
.product-linked-list small {
  display: block;
}

.product-linked-list small {
  color: #7e8299;
}

.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.cardapio-online-admin .compact-toolbar-actions .btn {
  min-height: 36px;
  padding: 7px 12px;
  font-size: 12px;
  line-height: 18px;
}

.promotion-preset-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.promotion-preset-grid button {
  min-height: 86px;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  text-align: left;
}

.promotion-preset-grid button:hover,
.promotion-preset-active {
  border-color: #f3695f !important;
  background: #fff5f3 !important;
}

.promotion-preset-grid strong,
.promotion-preset-grid span {
  display: block;
}

.promotion-preset-grid span {
  margin-top: 5px;
  color: #7e8299;
  font-size: 12px;
}

.promotion-image-row {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.promotion-image-preview {
  display: grid;
  width: 100%;
  min-height: 124px;
  overflow: hidden;
  place-items: center;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: linear-gradient(135deg, #fff5f3, #f8f9fb);
  color: #f3695f;
  font-weight: 900;
  text-align: center;
}

.promotion-image-preview img {
  width: 100%;
  height: 124px;
  object-fit: cover;
}

.commercial-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.commercial-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 132px;
  padding: 18px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.commercial-card-featured {
  border-color: #ffd4cc;
  background: #fff5f3;
}

.commercial-card span,
.commercial-card strong,
.commercial-card small {
  display: block;
}

.commercial-card span {
  color: #7e8299;
  font-weight: 800;
}

.commercial-card strong {
  margin: 6px 0;
  color: #181c32;
  font-size: 28px;
  font-weight: 900;
}

.commercial-card small,
.commercial-row small {
  color: #7e8299;
}

.commercial-list-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.commercial-list-wide {
  grid-column: span 2;
}

.commercial-list {
  display: grid;
  align-content: start;
  gap: 10px;
}

.commercial-list h5 {
  margin: 0;
  color: #181c32;
  font-weight: 900;
}

.commercial-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.commercial-row-with-image {
  grid-template-columns: auto minmax(0, 1fr) auto auto;
}

.commercial-row-image {
  width: 64px;
  height: 44px;
  border-radius: 6px;
  object-fit: cover;
}

.commercial-row strong,
.commercial-row small {
  display: block;
}

.weekday-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.weekday-picker label {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
}

.highlight-toggle-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
}

.highlight-toggle-card input {
  margin-top: 4px;
}

.highlight-toggle-card strong,
.highlight-toggle-card small {
  display: block;
}

.highlight-toggle-card small {
  color: #7e8299;
}

.product-cell > span,
.mini-product span,
.product-photo-preview span {
  display: grid;
  place-items: center;
  border-radius: 6px;
  background: #fff5f3;
  color: #f3695f;
  font-weight: 900;
}

.product-cell > span,
.product-cell img {
  width: 48px;
  height: 48px;
}

.product-photo-preview-lg,
.product-photo-preview-lg img,
.product-photo-preview-lg span {
  width: 240px;
  height: 180px;
}

.product-cell img,
.mini-product img,
.product-photo-preview img {
  border-radius: 6px;
  object-fit: cover;
}

.product-cell img {
  flex: 0 0 48px;
}

.product-cell strong,
.product-cell small {
  display: block;
}

.product-cell small {
  max-width: 360px;
  color: #7e8299;
  white-space: normal;
}

.mini-product {
  display: grid;
  width: 160px;
  min-height: 172px;
  gap: 8px;
  text-align: left;
}

.mini-product img,
.mini-product span {
  width: 100%;
  height: 96px;
}

.option-admin-list {
  display: grid;
  gap: 12px;
}

.option-admin-card {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(280px, 2fr) auto;
  gap: 16px;
  align-items: start;
  padding: 16px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.option-admin-card strong,
.option-admin-card span,
.option-admin-card small {
  display: block;
  min-width: 0;
  overflow-wrap: anywhere;
}

.option-admin-card small {
  color: #7e8299;
}

.option-admin-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
  min-width: 0;
}

.option-admin-item {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  min-height: 44px;
  padding: 3px 5px 3px 10px;
  border: 1px solid #cfe2ff;
  border-radius: 8px;
  background: #eef6ff;
  color: #3f4254;
}

.option-admin-item--paused {
  border-color: #ffd2d8;
  background: #fff1f3;
}

.option-admin-item > span {
  display: inline;
  min-width: 0;
  overflow-wrap: anywhere;
}

.option-admin-item .btn {
  flex: 0 0 auto;
  min-height: 44px;
  min-width: 44px;
}

.option-admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.option-admin-actions .btn {
  min-height: 44px;
}

.option-editor {
  display: grid;
  gap: 12px;
}

.option-editor-row {
  display: grid;
  grid-template-columns:
    minmax(130px, 1fr) minmax(160px, 1.2fr) 110px minmax(150px, 1fr) auto
    auto;
  gap: 10px;
  align-items: center;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.option-editor-row-wide {
  grid-template-columns:
    minmax(120px, 1fr) minmax(120px, 1fr) minmax(160px, 1.2fr) 100px
    minmax(150px, 1fr) minmax(140px, 1fr) auto auto;
}

.advanced-section-note {
  display: grid;
  gap: 4px;
  margin-bottom: 18px;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.advanced-section-note strong,
.advanced-section-note span {
  display: block;
}

.advanced-section-note span {
  color: #7e8299;
}

.delivery-zones {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #ebedf3;
}

.delivery-zones__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.delivery-zones__head strong {
  display: block;
}

.delivery-zones__head small {
  display: block;
  color: #7e8299;
}

.delivery-zones__empty {
  margin: 0 0 12px;
  color: #7e8299;
  font-size: 0.9rem;
}

.delivery-zones__list {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.delivery-zones__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #ebedf3;
  border-radius: 8px;
}

.delivery-zones__info strong {
  display: block;
}

.delivery-zones__info small {
  display: block;
  color: #7e8299;
}

.delivery-zones__actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.delivery-zones__form {
  margin-top: 4px;
  padding: 14px;
  border: 1px dashed #c9cdd6;
  border-radius: 10px;
}

.delivery-zones__form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.option-preset-panel {
  margin-bottom: 18px;
}

.option-category-picker {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.option-product-picker {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.option-product-picker button {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 10px;
  align-items: center;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  text-align: left;
}

.option-product-picker button:hover {
  border-color: #f3695f;
  background: #fff5f3;
}

.option-product-picker img,
.option-product-picker span {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
}

.option-product-picker span {
  display: grid;
  place-items: center;
  background: #fff1ef;
  color: #f3695f;
  font-weight: 800;
}

.option-product-picker strong,
.option-product-picker small {
  display: block;
}

.option-product-picker small {
  color: #7e8299;
}

.option-advanced-link {
  color: #7e8299;
  font-weight: 700;
}

.option-advanced-link summary {
  cursor: pointer;
}

.option-advanced-link select {
  margin-top: 8px;
}

.checkbox-line {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0;
  color: #3f4254;
  font-weight: 600;
}

.control-card ul {
  margin: 14px 0;
  padding-left: 18px;
}

.modal-control-grid,
.product-modal-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.product-modal-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.modal-control-field,
.modal-control-field label,
.modal-control-field small {
  display: block;
}

.modal-control-field label {
  color: #181c32;
  font-weight: 800;
}

.modal-control-field small {
  margin-top: 6px;
  color: #7e8299;
}

.modal-control-field-wide {
  grid-column: span 2;
}

.product-photo-preview {
  grid-row: span 3;
}

.product-photo-preview img,
.product-photo-preview span {
  width: 100%;
  height: 220px;
}

.stock-search-toolbar {
  display: grid;
  grid-template-columns: minmax(260px, 2fr) repeat(3, minmax(150px, 1fr)) auto;
  gap: 12px;
  align-items: end;
}

.stock-search-toolbar__button {
  min-height: 38px;
}

.stock-product-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.stock-product-card {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  min-height: 78px;
  padding: 12px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  background: #fff;
  color: #181c32;
  text-align: left;
}

.stock-product-card:hover,
.stock-product-card:focus-visible {
  border-color: #3699ff;
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.12);
  outline: none;
}

.stock-product-card img,
.stock-product-card__initials,
.publisher-product-summary > span {
  display: grid;
  width: 52px;
  height: 52px;
  place-items: center;
  border-radius: 8px;
  background: #f3f6f9;
  object-fit: cover;
  color: #3699ff;
  font-weight: 800;
}

.stock-product-card__body,
.stock-product-card__body small,
.publisher-product-summary small,
.bar-product-picker small {
  display: block;
}

.stock-product-card__body small,
.publisher-product-summary small,
.bar-product-picker small {
  margin-top: 4px;
  color: #7e8299;
}

.stock-pagination,
.publisher-product-summary,
.destination-picker,
.bar-product-picker {
  display: flex;
  gap: 12px;
  align-items: center;
}

.stock-pagination {
  justify-content: center;
}

.publisher-product-summary {
  padding: 14px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
}

.destination-picker {
  flex-wrap: wrap;
  min-height: 44px;
}

.destination-picker.disabled {
  opacity: 0.55;
}

.bar-product-picker {
  display: grid;
  grid-template-columns:
    minmax(220px, 1fr) minmax(150px, 0.65fr) minmax(220px, 1fr)
    minmax(240px, 1fr)
    auto;
  padding: 14px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  background: #f8f9fb;
}

.variant-type-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.variant-type-grid button {
  min-height: 132px;
  padding: 20px;
  border: 1px solid #e4e6ef;
  border-radius: 12px;
  background: #fff;
  color: #181c32;
  text-align: left;
}

.variant-type-grid button:hover,
.variant-type-grid button:focus-visible {
  border-color: #3699ff;
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.12);
  outline: none;
}

.variant-type-grid strong,
.variant-type-grid span {
  display: block;
}

.variant-type-grid span {
  margin-top: 8px;
  color: #7e8299;
}

.loading-box {
  padding: 32px;
  border: 1px dashed #d7dae7;
  border-radius: 6px;
  color: #7e8299;
  text-align: center;
}

@media (max-width: 1199px) {
  .overview-grid,
  .content-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .admin-layout {
    grid-template-columns: 1fr;
  }

  .admin-sidebar {
    grid-auto-flow: column;
    grid-auto-columns: minmax(190px, 1fr);
    overflow-x: auto;
    padding-bottom: 8px;
  }

  .option-admin-card {
    grid-template-columns: 1fr;
  }

  .option-admin-actions {
    justify-content: flex-start;
  }

  .stock-search-toolbar,
  .bar-product-picker,
  .option-editor-row,
  .option-editor-row-wide {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .guided-option-grid,
  .option-preset-panel {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .visual-editor {
    grid-template-columns: minmax(0, 1fr) 414px;
    gap: 20px;
  }

  .visual-live-preview {
    width: 414px;
    min-height: 600px;
  }
}

@media (max-width: 1099px) {
  .visual-editor {
    display: flex;
    flex-direction: column;
    overflow: visible;
  }

  .visual-editor > div:first-child {
    overflow: visible;
    padding-right: 0;
  }

  .visual-live-preview-panel {
    position: static;
    width: 100%;
  }

  .visual-live-preview-panel__header {
    max-width: 414px;
  }

  .visual-live-preview {
    align-self: center;
    width: min(414px, 100%);
    height: min(868px, calc(100vh - 140px));
  }
}

@media (max-width: 767px) {
  .overview-grid,
  .content-grid,
  .modal-control-grid,
  .product-modal-grid,
  .visual-editor,
  .visual-choice-grid,
  .channel-grid,
  .publishing-summary,
  .catalog-summary,
  .service-detail-grid,
  .theme-preset-grid {
    grid-template-columns: 1fr;
  }

  .stock-search-toolbar,
  .stock-product-list,
  .bar-product-picker,
  .variant-type-grid,
  .product-media-panel,
  .promotion-image-row,
  .commercial-grid,
  .option-product-picker,
  .guided-option-grid,
  .option-preset-panel,
  .option-category-picker,
  .option-editor-row,
  .option-editor-row-wide {
    grid-template-columns: 1fr;
  }

  .control-card-wide,
  .modal-control-field-wide {
    grid-column: span 1;
  }

  .section-header,
  .toolbar-line,
  .brand-preview,
  .service-summary-card,
  .appearance-summary,
  .section-save-bar,
  .weekly-schedule__head,
  .weekly-day,
  .delivery-zones__item {
    display: block;
  }

  .delivery-team-card__header,
  .delivery-team-row {
    align-items: stretch;
    flex-direction: column;
  }

  .delivery-team-card__header .btn {
    min-height: 44px;
    margin-top: 14px;
  }

  .delivery-team-row__actions {
    justify-content: flex-start;
  }

  .delivery-team-row__actions .btn {
    min-height: 44px;
  }

  .card-toolbar,
  .toolbar-actions,
  .operation-shortcuts,
  .section-save-bar > div {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .card-toolbar .btn,
  .toolbar-line .btn,
  .section-save-bar .btn {
    min-height: 44px;
  }

  .weekly-day__identity {
    margin-bottom: 10px;
  }

  .weekly-schedule__presets,
  .weekly-schedule__summary {
    align-items: stretch;
    flex-direction: column;
  }

  .weekly-schedule__presets > div:last-child {
    justify-content: flex-start;
  }

  .weekly-interval {
    align-items: flex-end;
  }

  .weekly-interval label,
  .weekly-interval .form-control {
    width: 100%;
  }

  .weekly-interval label {
    display: block;
  }

  .delivery-zones__actions,
  .stock-pagination {
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px;
  }

  .product-photo-preview-lg,
  .product-photo-preview-lg img,
  .product-photo-preview-lg span {
    width: 100%;
    max-width: 100%;
  }

  .modal-footer {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .modal-footer .btn {
    flex: 1 1 160px;
    margin: 0 !important;
  }

  .commercial-row,
  .commercial-row-with-image {
    grid-template-columns: 1fr;
  }

  .visual-live-preview {
    position: static;
    width: 100%;
    height: min(780px, calc(100vh - 100px));
    min-height: 560px;
    border-radius: 24px;
  }

  .visual-live-preview__frame {
    border-radius: 15px;
  }

  .product-photo-preview {
    grid-row: span 1;
  }
}
</style>

<style>
.cardapio-visual-dialog {
  width: calc(100vw - 32px);
  max-width: 1720px !important;
  margin: 16px auto !important;
}

.cardapio-visual-content {
  height: calc(100vh - 32px);
  max-height: 1040px;
  overflow: hidden;
}

.cardapio-visual-body {
  display: flex;
  overflow: hidden;
  flex-direction: column;
  min-height: 0;
  padding: 20px 24px !important;
}

@media (max-width: 1099px) {
  .cardapio-visual-dialog {
    width: calc(100vw - 20px);
    margin: 10px auto !important;
  }

  .cardapio-visual-content {
    height: calc(100vh - 20px);
  }

  .cardapio-visual-body {
    overflow-y: auto;
  }
}

@media (max-width: 575px) {
  .cardapio-visual-dialog {
    width: 100%;
    min-height: 100vh;
    margin: 0 !important;
  }

  .cardapio-visual-content {
    min-height: 100vh;
    border: 0;
    border-radius: 0;
  }

  .cardapio-visual-body {
    padding: 16px !important;
  }
}
</style>
